import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, Suspense, lazy } from "react";
import ReactDOM from "react-dom";
import TopicCategoryPicker from "./TopicCategoryPicker";
import { patchScenarioTurn, synthesizeSpeech, isAudioPlaybackAllowed, patchTopicWord, setWordRoot, deleteScenarioTurn, fetchGlobalPronounAnnotations, setGlobalPronounAnnotation, deleteGlobalPronounAnnotation, uploadTopicPDF, deleteTopicPDF, createScenarioTurn, reorderScenarioTurns, deleteTopic, refineVideoTranscript, markVocabularyComplete, markReadingComplete, incrementListeningPlay, fetchTopicWordReviewItems, fetchVerbFormQuestions, fetchLemmaCandidates, linkWordLemma, fetchTopicDetail, generateTurnMorphology, addWordBankEntry, analyzeWordBankWord, fetchAiQuizConfig, regenerateAiQuiz } from "../api";
import { useTTS } from "../hooks/useTTS";
import GuidedTopicJourney from "./GuidedTopicJourney";
import ShareTopicButton from "./ShareTopicButton";
import VocabularyStep from "./VocabularyStep";
import ListeningPanel from "./ListeningPanel";
import YouTubeSyncPlayer from "./YouTubeSyncPlayer";
import WordMorphology from "./WordMorphology";
import QuizBankManager from "./quiz/admin/QuizBankManager";
import DiamondDivider from "../theme/DiamondDivider";
import {
  morphIsVerb,
  morphIsPronounWord,
  morphIsPreposition,
  morphIsNoun,
  morphPronounSuffixSplit,
} from "../utils/arabicMorphology";

const TopicPdfViewer = lazy(() => import("./reading/TopicPdfViewer"));

const ARABIC_DIACRITICS_REGEX =
  /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/g;
const PUNCTUATION_REGEX = /[.,!?؟،؛:"«»()[\]]/g;

function normalizeArabic(str) {
  if (!str) return "";
  return str
    .replace(ARABIC_DIACRITICS_REGEX, "")
    .replace(PUNCTUATION_REGEX, "")
    .trim();
}

// Remove punctuation but KEEP harakat — the surface form of a token as
// written in the sentence (mirror of backend strip_punctuation_only).
function stripPunctuationOnly(str) {
  if (!str) return "";
  return str.replace(PUNCTUATION_REGEX, "").trim();
}

// Single-char diacritic test (non-global mirror of ARABIC_DIACRITICS_REGEX).
const ARABIC_DIACRITIC_CHAR = /[ؐ-ًؚ-ٰٟۖ-ۭ]/;
const _WAW = "و";                        // و
const _LAM = "ل";                        // ل
const _ALEF_VARIANTS = "اأإآٱ"; // ا أ إ آ ٱ

// Split a leading و and/or ال (with their harakat) off a diacritized token,
// returning { prefix, rest }. prefix is "" when no such clitic is present or
// when peeling it would leave fewer than 2 base letters. Used to tint the
// clitic prefix of a word that's been linked to its base form's definition.
function splitLinkedPrefix(tok) {
  if (!tok) return { prefix: "", rest: tok };
  let i = 0;
  const n = tok.length;
  const skipDiacritics = () => { while (i < n && ARABIC_DIACRITIC_CHAR.test(tok[i])) i++; };
  let matched = false;

  if (i < n && tok[i] === _WAW) {
    i += 1;
    skipDiacritics();
    matched = true;
  }
  if (i < n && _ALEF_VARIANTS.includes(tok[i])) {
    let j = i + 1;
    while (j < n && ARABIC_DIACRITIC_CHAR.test(tok[j])) j += 1;
    if (j < n && tok[j] === _LAM) {
      i = j + 1;
      skipDiacritics();
      matched = true;
    }
  }
  if (!matched) return { prefix: "", rest: tok };
  const rest = tok.slice(i);
  if (normalizeArabic(rest).length < 2) return { prefix: "", rest: tok };
  return { prefix: tok.slice(0, i), rest };
}

// ── Preposition grammar helpers ───────────────────────────────────────────────

// Core A1 Arabic prepositions (normalized, without diacritics).
// Used as a fallback when the token has no diacritics.
const PREPOSITIONS_SET = new Set([
  "في",          // in / at
  "على",         // on
  "من",          // from
  "إلى", "الى", // to (hamza variants)
  "مع",          // with
  "عن",          // about
  "ل",           // for / to (bare lam, tatweel stripped by normalizeArabic)
]);

// Diacritized forms of each preposition for precise matching.
// This prevents false positives like مَنْ (who — interrogative pronoun)
// being mistaken for مِنْ (from — preposition) after diacritics are stripped.
const PREPOSITIONS_DIACRITIZED = new Set([
  "فِي", "فِيهِ", "فِيهَا", "فِيكَ", "فِيكِ",
  "عَلَى", "عَلَيْهِ", "عَلَيْهَا", "عَلَيْكَ",
  "مِنْ", "مِنَ",                         // "from" — mīm + kasra; مِنَ before ال
  "إِلَى", "إِلَيْهِ", "إِلَيْهَا", "إِلَيْكَ",
  "مَعَ", "مَعَهُ", "مَعَهَا", "مَعَكَ",
  "عَنْ", "عَنْهُ", "عَنْهَا", "عَنْكَ",
  "لِ", "لَهُ", "لَهَا", "لَكَ", "لَكِ",
]);

/**
 * Returns true if tok is a حرف جر (preposition).
 * Checks the diacritized form first, falling back to the normalised set
 * only when the token carries no diacritics at all.
 */
function isPrepositionToken(tok) {
  if (!tok) return false;
  const bare = tok.trim();
  if (PREPOSITIONS_DIACRITIZED.has(bare)) return true;
  const hasDiacritics = /[\u064B-\u065F\u0670]/.test(bare);
  if (!hasDiacritics) return PREPOSITIONS_SET.has(normalizeArabic(bare));
  return false;
}

// Display forms (with diacritics) and English meanings shown in the grammar panel.
const PREPOSITIONS_DISPLAY = [
  { ar: "فِي",  en: "in / at" },
  { ar: "عَلَى", en: "on" },
  { ar: "مِنْ", en: "from" },
  { ar: "إِلَى", en: "to" },
  { ar: "مَعَ", en: "with" },
  { ar: "عَنْ", en: "about" },
  { ar: "لِـ",  en: "for / to" },
];

// ── Definite article helper ───────────────────────────────────────────────────

/**
 * Returns true if tok starts with the Arabic definite article (ال).
 * Checks the normalized form (without diacritics).
 * Requires at least 3 characters (ال + at least one letter).
 */
function isDefiniteToken(tok) {
  if (!tok) return false;
  const normalized = normalizeArabic(tok);
  return normalized.length >= 3 && normalized.startsWith("ال");
}

// Like isDefiniteToken but also treats nouns with attached pronoun suffixes as
// definite — needed for Idafa detection.  e.g. صَدِيقِكَ is definite via كَ even
// though it doesn't carry ال.  Uses the same exclusion list as detectAttachedPronoun
// so demonstratives / particles are never mis-classified.
function isDefiniteForIdafa(tok) {
  if (!tok) return false;
  if (isDefiniteToken(tok)) return true;
  const norm = normalizeArabic(tok);
  return detectAttachedPronoun(norm) !== null;
}

// ── Verb detection helper ─────────────────────────────────────────────────────

// All conjugated forms of كَانَ (normalized, no diacritics). Its stem alternates:
// كَانَ / كَانَتْ / كَانُوا keep the long ā, while كُنْتُ / كُنَّا / كُنْتُمْ shrink
// to كُنْـ — and shrinking drops the shrunk forms below the length thresholds the
// generic past-tense patterns rely on (كُنْتُ normalises to just 3 characters), so
// they are listed explicitly rather than left to the patterns.
const KANA_FORMS = new Set([
  "كان",    // كَانَ     — 3rd m sg (he was)
  "كانت",   // كَانَتْ   — 3rd f sg (she was)
  "كانوا",  // كَانُوا   — 3rd m pl (they were)
  "كانا",   // كَانَا    — dual (they two were)
  "كنت",    // كُنْتُ/كُنْتَ/كُنْتِ — 1st/2nd sg (I/you was/were)
  "كنا",    // كُنَّا     — 1st pl (we were)
  "كنتم",   // كُنْتُمْ   — 2nd m pl (you all were)
  "كنتما",  // كُنْتُمَا  — dual (you two were)
  "كنتن",   // كُنْتُنَّ  — 2nd f pl (you all f were)
]);

/**
 * Returns true if tok appears to be an Arabic verb (فعل).
 * A1-level heuristic — covers the most common patterns only:
 *   • Past tense (فعل ماضٍ): token ends with fatha (ـَ U+064E) and
 *     normalised form has ≥ 3 characters. Covers ذَهَبَ, جَلَسَ, كَتَبَ, …
 *     Short prepositions (مَعَ = 2 chars normalised) are excluded by the
 *     length check.
 *   • Present tense 3rd-person masculine (فعل مضارع): token starts with يَـ.
 * Note: this heuristic is intentionally simple for A1-level sentence
 * classification. It does not cover all Arabic verb forms.
 */
function isVerbToken(tok) {
  if (!tok) return false;
  const bare = tok.trim();
  const norm = normalizeArabic(bare);
  // Independent pronouns are never verbs (أَنْتَ ends in fatha but is a pronoun)
  if (INDEPENDENT_PRONOUNS_SET.has(norm)) return false;
  // Particles, comparatives, and other closed-class words are never verbs.
  if (GENDER_SKIP_PARTICLES.has(norm)) return false;
  // Words starting with the definite article ال are always nouns/adjectives, never verbs.
  if (norm.startsWith("ال")) return false;
  // Words ending with ة (taa marbouta) are feminine nouns/masdars, never verbs.
  if (norm.endsWith("ة")) return false;
  // Tanwin (ً ٌ ٍ) is exclusive to nouns/adjectives — verbs never carry nunation.
  // This excludes adverbs like أَيْضًا, أَحْيَانًا, أَخِيرًا that start with verb-prefix letters.
  if (/[\u064B\u064C\u064D]/.test(bare)) return false;
  // كَانَ and its conjugations — the shrunk كُنْـ forms are too short for the
  // generic past-tense patterns below, so they are matched by name.
  if (KANA_FORMS.has(norm)) return true;
  // Past tense: ends with fatha (U+064E), but NOT if last consonant is ك (possessive كَ suffix)
  if (/\u064E$/.test(bare) && !/\u0643\u064E$/.test(bare) && norm.length >= 3) return true;
  // Past tense with تْ suffix (3rd fem sg, e.g. ذَهَبَتْ) — ends in تْ
  if (/\u062A\u0652$/.test(bare) && norm.length >= 4) return true;
  // Past tense with ـْتُ/ـْتَ/ـْتِ suffix (1st/2nd sg, e.g. ذَهَبْتُ) — sukun + تُ/تَ/تِ
  if (/\u0652\u062A[\u064E\u064F\u0650]$/.test(bare) && norm.length >= 4) return true;
  // Past tense with نَا suffix (1st pl, e.g. ذَهَبْنَا)
  if (/\u0646\u064E\u0627$/.test(bare) && norm.length >= 5) return true;
  // Past tense with وا suffix (3rd pl masc, e.g. ذَهَبُوا)
  if (/\u0648\u0627$/.test(bare) && norm.length >= 5) return true;
  // Past tense dual: 3rd fem dual تَا (e.g. ذَهَبَتَا)
  if (/\u062A\u064E\u0627$/.test(bare) && norm.length >= 5) return true;
  // Past tense dual: 2nd dual ـْتُمَا (e.g. ذَهَبْتُمَا)
  if (/\u0652\u062A\u064F\u0645\u064E\u0627$/.test(bare) && norm.length >= 6) return true;
  // Past tense with ـْتُمْ suffix (2nd pl masc, e.g. ذَهَبْتُمْ) — sukun + تُ + مْ
  if (/\u0652\u062A\u064F\u0645\u0652$/.test(bare) && norm.length >= 5) return true;
  // Past tense dual: 3rd masc dual ـَا (fatha + alef, e.g. ذَهَبَا)
  // Exclude ـهَا (possessive pronoun suffix on nouns, e.g. مُشَاهَدَتَهَا, كِتَابَهَا).
  if (/\u064E\u0627$/.test(bare) && !/\u0647\u064E\u0627$/.test(bare) && norm.length >= 4) return true;
  // Past tense: 2nd/3rd fem pl ـْتُنَّ (e.g. ذَهَبْتُنَّ — أَنْتُنَّ) — sukun+تُ+نّ (with optional final fatha)
  if (/\u0652\u062A\u064F\u0646\u0651[\u064E]?$/.test(bare) && norm.length >= 5) return true;
  // Present tense: starts with ي/ت/ن/أ + short vowel (fatha/damma/kasra).
  // Threshold ≥ 4 stripped chars excludes 3-char nouns like نَوْعُ, يَوْمُ, أَمْرُ.
  // Exception: geminate (doubled-root) verbs like تُحِبُّ have 3 stripped chars but carry
  // a shadda (U+0651), allowing the threshold to drop to 3 in that case.
  if (/^[يتنأ][\u064E\u064F\u0650]/.test(bare)) {
    if (norm.length >= 4) return true;
    if (norm.length >= 3 && bare.includes("\u0651")) return true; // e.g. تُحِبُّ, يُحِبُّ
  }
  return false;
}

/**
 * Returns true if tok is a past-tense verb in any conjugation.
 * Used to distinguish SVO verbal sentences (subject + past verb)
 * from nominal sentences (subject + noun/adjective predicate).
 * Includes liaison-kasra check (فتحة + تِ ending) for 3rd f sg verbs
 * like خَبَزَتِ الْكَعْكَ.
 */
function isPastVerbToken(tok) {
  if (!tok) return false;
  const bare = tok.trim();
  if (INDEPENDENT_PRONOUNS_SET.has(normalizeArabic(bare))) return false;
  // Words starting with ال are nouns/adjectives, never verbs.
  if (normalizeArabic(bare).startsWith("ال")) return false;
  // Words ending with ة (taa marbouta) are nouns/adjectives, never verbs.
  if (normalizeArabic(bare).endsWith("ة")) return false;
  // Tanwin (ً ٌ ٍ) appears only on nouns/adjectives, never on verbs.
  if (/[\u064B\u064C\u064D]/.test(bare)) return false;
  // كَانَ and its conjugations (see KANA_FORMS) — matched by name because the
  // shrunk كُنْـ forms fall below the length thresholds used below.
  if (KANA_FORMS.has(normalizeArabic(bare))) return true;
  if (/\u064E$/.test(bare) && !/\u0643\u064E$/.test(bare) && normalizeArabic(bare).length >= 3) return true;
  if (/\u062A\u0652$/.test(bare) && normalizeArabic(bare).length >= 4) return true;
  if (/\u064E\u062A\u0650$/.test(bare) && normalizeArabic(bare).length >= 4) return true; // liaison kasra: فَعَلَتِ
  if (/\u0652\u062A[\u064E\u064F\u0650]$/.test(bare) && normalizeArabic(bare).length >= 4) return true;
  if (/\u0646\u064E\u0627$/.test(bare) && normalizeArabic(bare).length >= 5) return true;
  if (/\u0648\u0627$/.test(bare) && normalizeArabic(bare).length >= 5) return true;
  if (/\u062A\u064E\u0627$/.test(bare) && normalizeArabic(bare).length >= 5) return true;
  if (/\u0652\u062A\u064F\u0645\u064E\u0627$/.test(bare) && normalizeArabic(bare).length >= 6) return true;
  if (/\u064E\u0627$/.test(bare) && !/\u0647\u064E\u0627$/.test(bare) && normalizeArabic(bare).length >= 4) return true;
  if (/\u0652\u062A\u064F\u0646\u0651[\u064E]?$/.test(bare) && normalizeArabic(bare).length >= 5) return true;
  return false;
}

/**
 * Returns true if tok is a present tense verb in the intro scope:
 *   أَ prefix → first person singular (أَنَا)
 *   تَ prefix → second person (أَنْتَ / أَنْتِ / أَنْتُمَا / أَنْتُمْ) and third fem sg (هِيَ)
 *   يَ prefix → third person masc (هُوَ / هُمَا / هُمْ)
 *   نَ prefix → first person plural (نَحْنُ)
 * Requires ≥ 4 stripped characters to avoid short particles.
 * Independent pronouns are excluded.
 */
function isPresentTenseIntroToken(tok) {
  if (!tok) return false;
  const bare = tok.trim();
  if (INDEPENDENT_PRONOUNS_SET.has(normalizeArabic(bare))) return false;
  // أَ / تَ / يَ / نَ + rest of present tense verb (fatha on prefix letter)
  if (/^[أتين]\u064E/.test(bare) && normalizeArabic(bare).length >= 4) return true;
  return false;
}

/**
 * Returns true if tok is an elative/comparative adjective (اسم التفضيل).
 * Detection heuristic: the normalized form starts with أ (U+0623, alef with
 * hamza above) and has exactly 4 characters — matching the أفعل pattern.
 * Common comparatives: أَكْبَر، أَفْضَل، أَسْهَل، أَجْمَل، أَصْغَر، أَقْرَب …
 * Present-tense verbs with أ prefix (e.g. أَكْتُبُ) strip to 5+ characters,
 * so the length=4 guard keeps false positives low.
 * Independent pronouns are excluded.
 */
function isComparativeToken(tok) {
  if (!tok) return false;
  const bare = tok.trim();
  const norm = normalizeArabic(bare);
  if (INDEPENDENT_PRONOUNS_SET.has(norm)) return false;
  // 3-char elative exceptions that don't fit the 4-letter أفعل pattern
  if (norm === "\u0623\u0642\u0644" /* أقل, less */ || norm === "\u0623\u0647\u0645" /* أهم, more important */) return true;
  // Must start with أ (U+0623 – alef with hamza above, after diacritic stripping)
  if (!norm.startsWith("\u0623")) return false;
  // Exactly 4 consonantal letters (the أفعل pattern):
  // أَفْضَل، أَكْبَر، أَصْغَر، أَسْهَل، أَصْعَب، أَجْمَل، أَكْثَر، أَسْرَع، أَبْطَأ،
  // أَطْوَل، أَقْصَر، أَقْرَب، أَبْعَد، أَرْخَص، أَغْلَى، أَقْوَى، أَحْلَى، أَطْيَب،
  // أَشْهَر، أَحْسَن …
  if (norm.length !== 4) return false;
  return true;
}

// Dual suffix normalized forms (stripped of diacritics):
// ـانِ → "ان" (nominative dual), ـيْنِ → "ين" (accusative/genitive dual).
const DUAL_SUFFIX_AN = "\u0627\u0646"; // ان
const DUAL_SUFFIX_YN = "\u064A\u0646"; // ين

/**
 * Returns true if tok appears to be an Arabic imperative verb (فعل الأمر).
 * Heuristic: the token ends with sukun (ْ U+0652), the hallmark of imperative
 * forms across all verb forms:
 *   Form I:   اِذْهَبْ, اِقْرَأْ, اُكْتُبْ, اُجْلُسْ, اِفْتَحْ, اِسْمَعْ
 *   Form II:  دَرِّسْ
 *   Form V:   تَذَكَّرْ
 * Prepositions (مِنْ, عَنْ) end with sukun but are excluded via isPrepositionToken().
 * Common particles, pronouns, definite nouns, and nouns with tanwin are excluded.
 */
// Ready-made imperative chunks taught by lesson 23 that the sukūn heuristic
// cannot see: تَعَالَ ends in a fatha, أَعْطِنِي in its نِي tail, and the
// two-letter خُذْ falls under the length guard. Normalized (harakat-stripped)
// forms. كُلْ (eat!) is deliberately absent — normalized it collides with
// كُلّ (every/all); the admin override can mark it per-token instead.
const KNOWN_IMPERATIVE_CHUNKS = new Set(["تعال", "خذ", "اعطني"]);

function isImperativeToken(tok) {
  if (!tok) return false;
  // Strip trailing punctuation before checking for sukun — scenario tokens
  // often carry a period or Arabic comma (،) after the final sukun.
  const bare = tok.trim().replace(PUNCTUATION_REGEX, "");
  // Must end with sukun (ْ U+0652) — the hallmark of imperative forms
  if (!bare.endsWith("\u0652")) return false;
  const norm = normalizeArabic(bare);
  if (norm.length < 3) return false;
  if (INDEPENDENT_PRONOUNS_SET.has(norm)) return false;
  if (GENDER_SKIP_PARTICLES.has(norm)) return false;
  if (norm.startsWith("\u0627\u0644")) return false; // ال definite article
  if (norm.endsWith("\u0629")) return false; // ة taa marbouta = noun
  // Tanwin is exclusive to nouns/adjectives — imperatives never carry it
  if (/[\u064B\u064C\u064D]/.test(bare)) return false;
  // Prepositions like مِنْ and عَنْ end with sukun but are not imperatives
  if (isPrepositionToken(tok)) return false;
  return true;
}

/**
 * Returns true if tok appears to be a dual noun (مثنى) ending in ـانِ or ـيْنِ.
 * Detection heuristic:
 *   • Normalize (strip diacritics + punctuation).
 *   • Require ≥ 4 characters (stem ≥ 2 + suffix 2) so short words are excluded.
 *   • Must end with "ان" (ـانِ) or "ين" (ـيْنِ).
 *   • Exclude independent pronouns and gender-skip particles.
 *   • Definite dual forms (الكِتَابَانِ) pass through — the ال prefix does not
 *     affect the suffix, so no special exclusion is needed for them.
 *   • Exclude verbs: words detected by isVerbToken() are skipped.
 *   • Exclude common non-dual words that coincidentally end in "ان" or "ين":
 *     كان، يكون، مكان، زمان، أحيان، إنسان، ميزان، حنان، رمضان، بيان،
 *     حين، دين، يقين، أمين، لين، زين, etc.
 */
const DUAL_FALSE_POSITIVES = new Set([
  // Common non-dual words ending in ان
  "كان", "يكون", "مكان", "زمان", "أحيان", "احيان", "إنسان", "انسان",
  "ميزان", "حنان", "رمضان", "بيان", "برهان", "عنوان", "ديوان",
  "جنان", "ربان", "سلطان", "عثمان", "ريان", "لسان", "لبنان",
  "إيمان", "ايمان", "رضوان", "عدنان", "حسان", "سفيان", "شعبان",
  "عمران", "كيان", "حيوان", "جريان", "فيضان", "غليان", "خان", "شان",
  // Common non-dual words ending in ين
  "دين", "يقين", "أمين", "امين", "حين", "لين", "زين", "سين",
  "يمين", "صابرين", "مؤمنين", "مسلمين", "كافرين",
  "ياسين", "حسين", "جنين", "طين",
]);

function isDualFormToken(tok) {
  if (!tok) return false;
  const bare = tok.trim();
  const norm = normalizeArabic(bare);
  if (INDEPENDENT_PRONOUNS_SET.has(norm)) return false;
  if (GENDER_SKIP_PARTICLES.has(norm)) return false;
  if (norm.length < 4) return false;
  const endAN = norm.endsWith(DUAL_SUFFIX_AN);
  const endYN = norm.endsWith(DUAL_SUFFIX_YN);
  if (!endAN && !endYN) return false;
  if (DUAL_FALSE_POSITIVES.has(norm)) return false;
  // Exclude verb tokens (past tense verbs ending in ـَانِ pattern like يَفْعَلَانِ
  // would pass — but the isVerbToken guard catches most common verb forms).
  if (isVerbToken(tok)) return false;
  return true;
}

/**
 * Returns { normStemLength } for a token that ends with the dual suffix ـانِ or ـيْنِ,
 * where normStemLength = norm.length - 2 (the last two consonants are the suffix).
 * Used with splitTokenAtNormPos() to split the stem from the suffix for split rendering.
 * Returns null if no dual suffix is detected.
 */
function detectDualFormSuffix(tok) {
  if (!tok) return null;
  const norm = normalizeArabic(tok.trim());
  if (norm.endsWith(DUAL_SUFFIX_AN) || norm.endsWith(DUAL_SUFFIX_YN)) {
    return { normStemLength: norm.length - 2 };
  }
  return null;
}

/**
 * Detect the present tense prefix on a verb token.
 * Present tense verbs always carry a single-letter prefix (أَ / تَ / يَ / نَ).
 * Returns { normPrefixLength: 1 } so the caller can use splitTokenAtNormPos
 * to split prefix from stem.
 */
function detectPresentTensePrefix(tok) {
  if (!tok) return null;
  if (!isPresentTenseIntroToken(tok.trim())) return null;
  return { normPrefixLength: 1 };
}

/**
 * Detect a detachable past tense suffix on a verb token.
 * Returns { normStemLength } indicating where to split the normalized string,
 * or null if the verb has no detachable suffix (e.g. 3rd masc sg like ذَهَبَ).
 *
 * Suffixes detected (checked longest-first to avoid partial matches):
 *   - وا      (3rd pl. masc, e.g. ذَهَبُوا)
 *   - ـْتُنَّ  (2nd fem pl, e.g. ذَهَبْتُنَّ — أَنْتُنَّ)
 *   - ـْتُمَا  (2nd dual, e.g. ذَهَبْتُمَا)
 *   - ـْتُمْ   (2nd pl masc, e.g. ذَهَبْتُمْ)
 *   - نَا      (1st pl, e.g. ذَهَبْنَا)
 *   - ـْنَ     (3rd fem pl, e.g. ذَهَبْنَ — هُنَّ)
 *   - تْ       (3rd fem sg, e.g. ذَهَبَتْ) — taa + sukun at end
 *   - ـْتُ/ـْتَ/ـْتِ (1st/2nd sg, e.g. ذَهَبْتُ)
 */
function detectPastTenseSuffix(tok) {
  if (!tok) return null;
  const bare = tok.trim();
  const norm = normalizeArabic(bare);
  // كُنْتُ / كُنْتَ / كُنْتِ / كُنْتُمْ — the shrunk forms of كَانَ. Their stem
  // is only كُنْ, which puts them under the length thresholds the generic
  // patterns below use, so they are split by name (to the same offsets those
  // patterns would give). كُنَّا is deliberately absent: its doubled ن belongs
  // to both the stem and the نَا ending, so there is no honest place to cut it.
  if (norm === "كنت") return { normStemLength: 2 };   // كن | ت
  if (norm === "كنتم") return { normStemLength: 2 };  // كن | تم
  // وا — 3rd pl. masc (normalized: وا)
  if (norm.length > 3 && norm.endsWith("وا")) {
    return { normStemLength: norm.length - 2 };
  }
  // ـْتُنَّ — 2nd fem pl (e.g. ذَهَبْتُنَّ) — check before ـْتُ to avoid partial match
  if (/\u0652\u062A\u064F\u0646\u0651[\u064E]?$/.test(bare) && norm.length > 4) {
    return { normStemLength: norm.length - 2 }; // تن = 2 normalized chars
  }
  // ـْتُمَا — 2nd dual (e.g. ذَهَبْتُمَا) — check before ـْتُ to avoid partial match
  if (/\u0652\u062A\u064F\u0645\u064E\u0627$/.test(bare) && norm.length > 4) {
    return { normStemLength: norm.length - 3 }; // تما = 3 normalized chars
  }
  // ـْتُمْ — 2nd pl masc (e.g. ذَهَبْتُمْ) — check before ـْتُ to avoid partial match
  if (/\u0652\u062A\u064F\u0645\u0652$/.test(bare) && norm.length > 4) {
    return { normStemLength: norm.length - 2 }; // تم = 2 normalized chars
  }
  // نَا — 1st pl. (match diacritized to avoid false positives on هنا etc.)
  if (/\u0646\u064E\u0627$/.test(bare) && norm.length > 3) {
    return { normStemLength: norm.length - 2 };
  }
  // ـْنَ — 3rd fem pl (e.g. ذَهَبْنَ — هُنَّ) — sukun + nun + fatha
  if (/\u0652\u0646\u064E$/.test(bare) && norm.length > 3) {
    return { normStemLength: norm.length - 1 }; // ن = 1 normalized char
  }
  // تْ — 3rd fem sg (taa + sukun at end)
  if (/\u062A\u0652$/.test(bare) && norm.length > 3) {
    return { normStemLength: norm.length - 1 };
  }
  // ـْتُ/ـْتَ/ـْتِ — 1st/2nd sg (sukun before taa + short vowel)
  if (/\u0652\u062A[\u064E\u064F\u0650]$/.test(bare) && norm.length > 3) {
    return { normStemLength: norm.length - 1 };
  }
  // تَا — 3rd fem dual (e.g. ذَهَبَتَا)
  if (/\u062A\u064E\u0627$/.test(bare) && norm.length > 3) {
    return { normStemLength: norm.length - 2 }; // تا = 2 normalized chars
  }
  // ـَا — 3rd masc dual (fatha + alef, e.g. ذَهَبَا)
  if (/\u064E\u0627$/.test(bare) && norm.length > 3) {
    return { normStemLength: norm.length - 1 }; // ا = 1 normalized char
  }
  return null;
}



// Normalized forms of Arabic negation particles.
const NEGATION_LA = "لا";   // لَا — present tense negation (لَا أَذْهَبُ)
const NEGATION_MA = "ما";   // مَا — past tense negation, classical (مَا ذَهَبَ)
const NEGATION_LAM = "لم";  // لَمْ — past negation via jussive present verb (لَمْ يَذْهَبْ)

// Normalized forms of the six core A1 Arabic question words (أَدَوَاتُ الِاسْتِفْهَام).
const QUESTION_WORDS_SET = new Set([
  "من",    // مَنْ  — who
  "ماذا",  // مَاذَا — what
  "اين",   // أَيْنَ — where
  "متى",   // مَتَى — when
  "لماذا", // لِمَاذَا — why
  "كيف",   // كَيْفَ — how
]);

/**
 * Returns true if tok is one of the six core Arabic question words that
 * should be highlighted in question_words grammar mode.
 */
function isQuestionWord(tok) {
  if (!tok) return false;
  return QUESTION_WORDS_SET.has(normalizeArabic(tok.trim()));
}

// Normalized forms of the core Arabic connectors (lesson 22).
// وَ can appear as a standalone token or prefixed to the next word (e.g., وَأَذْهَبُ).
// For standalone-token detection we check the full normalized form.
// فَـ has no heuristic: any ف-initial word (فَهِمْتُ) would false-positive.
const CONNECTOR_WA  = "و";   // وَ  — and
const CONNECTOR_LAKIN = "لكن"; // لَكِنْ — but
const CONNECTOR_LIANNA = "لأن"; // لِأَنَّ — because (normalizeArabic strips harakat, hamza on alef is preserved)
const CONNECTOR_THUMMA = "ثم"; // ثُمَّ — then

/**
 * Returns the connector type for tok in connectors grammar mode, or null.
 * - "wa"        → وَ standalone token (gray)
 * - "wa_prefix" → وَ clitic prefix on a longer token, e.g. وَأَقْرَأُ (split rendering)
 * - "thumma"    → ثُمَّ (green)
 * - "lakin"     → لَكِنْ (red)
 * - "lianna"    → لِأَنَّ and its pronoun-tailed forms لِأَنَّهُ / لِأَنَّهَا / لِأَنِّي (blue)
 *
 * In Arabic, وَ is almost always written attached to the following word (e.g.
 * وَأَقْرَأُ), so we detect both the standalone form and the prefix form.
 */
function getConnectorType(tok) {
  if (!tok) return null;
  const norm = normalizeArabic(tok.trim());
  if (norm === CONNECTOR_WA) return "wa";
  if (norm === CONNECTOR_LAKIN) return "lakin";
  if (norm === CONNECTOR_THUMMA) return "thumma";
  // لِأَنَّ plus its attached pronoun tails (لِأَنَّهُ → "لأنه", لِأَنِّي → "لأني")
  if (norm.startsWith(CONNECTOR_LIANNA)) return "lianna";
  // وَ as a clitic prefix (e.g. وَأَقْرَأُ → "وأقرأ" after stripping harakat)
  if (norm.startsWith(CONNECTOR_WA) && norm.length >= 2) return "wa_prefix";
  return null;
}

// Normalized form of the Arabic future particle سَوْفَ.
const SAWFA_NORM = "سوف";

/**
 * Returns the future tense role for tok in future_tense grammar mode:
 * - "sawfa"     → سَوْفَ standalone future particle (purple)
 * - "sa_prefix" → present verb with سَ attached prefix, e.g. سَأَذْهَبُ (split purple+green)
 * - null        → not a future tense form
 *
 * Detection rules:
 *  1. If normalised token === "سوف" → "sawfa".
 *  2. If bare token starts with سَ (U+0633 + fatha U+064E) and the remainder
 *     (after removing those two chars) passes isVerbToken() → "sa_prefix".
 *     isVerbToken() enforces its own minimum length guards internally.
 */
function getFutureTenseRole(tok) {
  if (!tok) return null;
  const bare = tok.trim();
  const norm = normalizeArabic(bare);
  if (norm === SAWFA_NORM) return "sawfa";
  // سَ prefix detection: bare starts with سَ (sin + fatha) and the rest is a present verb.
  if (/^\u0633\u064E/.test(bare)) {
    const rest = bare.slice(2); // remove سَ (2 chars: sin + fatha)
    if (rest.length > 0 && isVerbToken(rest)) return "sa_prefix";
  }
  return null;
}

// ── Object Pronouns (Basic) detection ────────────────────────────────────────

/**
 * Object pronoun suffixes for the object_pronouns_basic grammar rule.
 * All six suffixes taught by grammar lesson 17: ـنِي (me), ـكَ/ـكِ (you),
 * ـهُ (him), ـهَا (her), ـنَا (us).
 * Ordered longest-first to avoid partial matches (e.g. نا before ن, ها before ه).
 *
 * Note: كَ (you, masc.) and كِ (you, fem.) both normalize to "ك" after
 * diacritic-stripping, so a single entry covers both.
 */
const OBJECT_PRONOUN_BASIC_SUFFIXES = [
  { suffix: "نا", label: "ـنَا", meaning: "us" },
  { suffix: "ها", label: "ـهَا", meaning: "her" },
  { suffix: "ني", label: "ـنِي", meaning: "me" },
  { suffix: "ك",  label: "ـكَ/كِ", meaning: "you" },
  { suffix: "ه",  label: "ـهُ",  meaning: "him" },
];

/**
 * Detect whether tok is a present-tense verb with an attached object pronoun
 * from the starter set (ـهُ / ـهَا / ـنِي).
 * Returns { suffix, label, meaning, normStemLength } or null.
 *
 * Detection rules:
 *  1. Normalized form must not be in ATTACHED_PRONOUN_EXCLUSIONS.
 *  2. Normalized form must end with one of the three suffixes.
 *  3. The remaining stem (after removing the suffix) must have ≥ 2 chars
 *     and must start with a present-tense verb prefix (أ / ت / ي / ن).
 */
function detectObjectPronounBasic(tok) {
  if (!tok) return null;
  const norm = normalizeArabic(tok.trim());
  if (ATTACHED_PRONOUN_EXCLUSIONS.has(norm)) return null;
  for (const { suffix, label, meaning } of OBJECT_PRONOUN_BASIC_SUFFIXES) {
    if (norm.endsWith(suffix) && norm.length - suffix.length >= 2) {
      const normStem = norm.slice(0, norm.length - suffix.length);
      // Present-tense verb prefixes: يَـ (3rd m), تَـ (2nd/3rd f), نَـ (1st pl), أَـ (1st sg)
      if (/^[يتنأ]/.test(normStem)) {
        return { suffix, label, meaning, normStemLength: normStem.length };
      }
    }
  }
  return null;
}

/**
 * Like detectObjectPronounBasic but without the verb-prefix requirement.
 * Used when an admin forces op:mafool on a token that didn't pass the
 * heuristic — we still need to know where to split the token.
 * Returns { suffix, label, meaning, normStemLength } or null.
 */
function getObjectPronounSuffixForced(tok) {
  if (!tok) return null;
  const norm = normalizeArabic(tok.trim());
  if (ATTACHED_PRONOUN_EXCLUSIONS.has(norm)) return null;
  for (const { suffix, label, meaning } of OBJECT_PRONOUN_BASIC_SUFFIXES) {
    if (norm.endsWith(suffix) && norm.length - suffix.length >= 2) {
      return { suffix, label, meaning, normStemLength: norm.length - suffix.length };
    }
  }
  return null;
}

// ── Object Pronouns (Expanded) detection ─────────────────────────────────────

/**
 * Object pronoun suffixes for the object_pronouns_expanded grammar rule.
 * Extends the basic set with ـكَ/كِ (you) and ـنَا (us).
 * Ordered longest-first to avoid partial matches (e.g. نا before ن).
 *
 * Note: كَ (you, masc.) and كِ (you, fem.) both normalize to "ك" after
 * diacritic-stripping, so a single entry covers both. The actual harakat
 * in the raw token are preserved in the split rendering via splitTokenAtNormPos.
 */
const OBJECT_PRONOUN_EXPANDED_SUFFIXES = [
  { suffix: "نا", label: "ـنَا", meaning: "us" },
  { suffix: "ها", label: "ـهَا", meaning: "her" },
  { suffix: "ني", label: "ـنِي", meaning: "me" },
  { suffix: "ك",  label: "ـكَ/كِ", meaning: "you" },
  { suffix: "ه",  label: "ـهُ",  meaning: "him" },
];

/**
 * Detect whether tok is a present-tense verb with an attached object pronoun
 * from the expanded set (ـهُ / ـهَا / ـنِي / ـكَ / ـكِ / ـنَا).
 * Returns { suffix, label, meaning, normStemLength } or null.
 *
 * Detection rules:
 *  1. Normalized form must not be in ATTACHED_PRONOUN_EXCLUSIONS.
 *  2. Normalized form must end with one of the six suffixes (longest-first).
 *  3. The remaining stem must have ≥ 2 chars and start with a present-tense
 *     verb prefix (أ / ت / ي / ن).
 */
function detectObjectPronounExpanded(tok) {
  if (!tok) return null;
  const norm = normalizeArabic(tok.trim());
  if (ATTACHED_PRONOUN_EXCLUSIONS.has(norm)) return null;
  for (const { suffix, label, meaning } of OBJECT_PRONOUN_EXPANDED_SUFFIXES) {
    if (norm.endsWith(suffix) && norm.length - suffix.length >= 2) {
      const normStem = norm.slice(0, norm.length - suffix.length);
      if (/^[يتنأ]/.test(normStem)) {
        return { suffix, label, meaning, normStemLength: normStem.length };
      }
    }
  }
  return null;
}

/**
 * Like detectObjectPronounExpanded but without the verb-prefix requirement.
 * Used when an admin forces op:mafool on a token in expanded mode.
 * Returns { suffix, label, meaning, normStemLength } or null.
 */
function getObjectPronounSuffixExpandedForced(tok) {
  if (!tok) return null;
  const norm = normalizeArabic(tok.trim());
  if (ATTACHED_PRONOUN_EXCLUSIONS.has(norm)) return null;
  for (const { suffix, label, meaning } of OBJECT_PRONOUN_EXPANDED_SUFFIXES) {
    if (norm.endsWith(suffix) && norm.length - suffix.length >= 2) {
      return { suffix, label, meaning, normStemLength: norm.length - suffix.length };
    }
  }
  return null;
}

// All conjugated forms of لَيْسَ (normalized, no diacritics).
const LAYSA_FORMS = new Set([
  "ليس",   // لَيْسَ   — 3rd m sg (he is not)
  "ليست",  // لَيْسَتْ  — 3rd f sg (she is not)
  "ليسا",  // لَيْسَا  — dual (they two are not)
  "ليسوا", // لَيْسُوا — 3rd m pl (they are not)
  "لست",   // لَسْتُ/لَسْتَ/لَسْتِ — 1st/2nd sg (I/you am/are not)
  "لسنا",  // لَسْنَا  — 1st pl (we are not)
  "لستم",  // لَسْتُمْ — 2nd m pl (you all are not)
  "لستما", // لَسْتُمَا — dual (you two are not)
  "لستن",  // لَسْتُنَّ — 2nd f pl (you all f are not)
]);

/**
 * Returns true if tok is a negation particle that should be highlighted.
 * - لا        → always (present tense negation)
 * - لَيْسَ/conjugations → always (nominal sentence negation)
 * - لم        → always (unambiguous negation particle; always precedes a jussive verb)
 * - ما        → only when followed by a verb (classical past negation; ما can also mean "what")
 */
function isNegationParticle(tok, nextTok) {
  if (!tok) return false;
  const norm = normalizeArabic(tok.trim());
  if (norm === NEGATION_LA || norm === NEGATION_LAM || LAYSA_FORMS.has(norm)) return true;
  if (norm === NEGATION_MA && nextTok) {
    return isVerbToken(nextTok.trim());
  }
  return false;
}

/**
 * Returns true if tok visibly carries the منصوب (accusative) case ending.
 * Only these are worth badging — prepositional phrases, particles, and verbs
 * never show a visible منصوب ending and should not be auto-tagged.
 *   • Indefinite منصوب: has tanwin nasb (ً U+064B), e.g. طَالِبًا، كَبِيرًا
 *   • Definite منصوب:   starts with ال and ends with fatha (َ U+064E), e.g. الطَّالِبَ
 */
function isVisiblyMansuub(tok) {
  if (!tok) return false;
  const bare = tok.trim();
  if (/\u064B/.test(bare)) return true;                           // tanwin nasb
  if (/^ال/.test(bare) && /\u064E$/.test(bare)) return true;     // definite + fatha
  return false;
}


// Both hamza (أ) and plain alef (ا) variants are included to handle common spellings.
const INDEPENDENT_PRONOUNS_SET = new Set([
  "أنا", "انا",          // I
  "أنت", "انت",          // you (m)
  "أنتِ", "انتِ",        // you (f)
  "هو",                  // he
  "هي",                  // she
  "نحن",                 // we
  "أنتم", "انتم",        // you (pl m)
  "أنتن", "انتن",        // you (pl f)
  "هم",                  // they (m)
  "هن",                  // they (f)
]);

// Common Arabic particles, conjunctions, and function words that should not
// receive a masculine/feminine gender label in gender mode.
// These fall outside the existing preposition, pronoun, and verb detection sets.
const GENDER_SKIP_PARTICLES = new Set([
  "هل", "لا", "ما", "لم", "لن", "قد", "لقد",       // interrogative / negation / emphasis
  "من", "ماذا", "اين", "متى", "لماذا", "كيف",      // question words
  "او", "أو", "أم",                                  // or / disjunctive
  "ثم",                                               // then
  "لكن",                                              // but
  "لأن",                                              // because (لِأَنَّ — hamza preserved by normalizeArabic)
  "نعم", "بلى",                                       // yes
  "يا",                                               // vocative
  "إذا", "اذا", "إذ",                                // if / when
  "حتى",                                              // until / even
  "ليس",                                              // is not (copula)
  "إن", "أن", "ان",                                  // that / if
  "كما",                                              // just as
  "أيضا", "ايضا",                                    // also / too
  "هذا", "هذه", "ذلك", "تلك", "هذان", "هؤلاء",      // demonstratives
  "الذي", "التي", "الذين", "اللاتي",                 // relative pronouns
  "كل", "بعض", "كلما",                               // quantifiers
  // أَفْعَلُ elative/comparative adjectives — start with أَ so they would otherwise
  // trigger the present-tense verb heuristic in isVerbToken().
  // Plain-alef (U+0627) forms match text written without diacritics.
  "اكثر", "اقل", "اكبر", "اصغر", "احسن", "افضل",
  "اجمل", "اسرع", "ابطا", "اطول", "اقصر", "اقرب",
  "ابعد", "اعلى", "ادنى", "اول", "اولى", "اخر",
  "اصعب", "ارخص", "اغلى", "اقوى", "احلى", "اطيب", "اشهر",
  // Hamza-prefixed (U+0623) forms — these match normalized tokens from diacritized text
  // (e.g. "أَكْبَر" strips to "أكبر" with U+0623 start, not U+0627).
  "أكثر", "أقل", "أكبر", "أصغر", "أحسن", "أفضل",
  "أجمل", "أسرع", "أبطأ", "أطول", "أقصر", "أقرب",
  "أبعد", "أعلى", "أدنى", "أول", "أولى", "أخر",
  "أصعب", "أرخص", "أغلى", "أقوى", "أحلى", "أطيب", "أشهر",
  "أهم",  // more important (3-char elative — no plain-alef conflict)
  // Common loanwords / brand names starting with present-tense prefix letters.
  "يوتيوب", "يوتيوبر",
]);

// Display forms of independent pronouns (with diacritics) for the grammar panel.
const INDEPENDENT_PRONOUNS_DISPLAY = [
  "أَنا", "أَنْتَ", "أَنْتِ", "هُوَ", "هِيَ",
  "نَحْنُ", "أَنْتُمْ", "أَنْتُنَّ", "هُمْ", "هُنَّ",
];

// Attached-pronoun suffixes ordered longest-first to avoid partial matches.
// Each entry: { suffix (bare), label (with tatweel for display) }
const ATTACHED_PRONOUN_SUFFIXES = [
  { suffix: "كم",  label: "ـكُمْ" },
  { suffix: "كن",  label: "ـكُنَّ" },
  { suffix: "هم",  label: "ـهُمْ" },
  { suffix: "هن",  label: "ـهُنَّ" },
  { suffix: "نا",  label: "ـنا" },
  { suffix: "ها",  label: "ـها" },
  { suffix: "ك",   label: "ـكَ / ـكِ" },
  { suffix: "ه",   label: "ـهُ" },
  { suffix: "ي",   label: "ـي" },
];

// Display labels for attached pronouns shown in the grammar panel.
const ATTACHED_PRONOUNS_DISPLAY = ATTACHED_PRONOUN_SUFFIXES.map((s) => s.label);

// Words that must never be treated as having an attached pronoun suffix,
// even though they coincidentally end in a suffix character.
// Includes demonstratives, particles, and other closed-class words.
const ATTACHED_PRONOUN_EXCLUSIONS = new Set([
  // Demonstratives & relative pronouns
  "هذه",    // this (f) — ends in ه
  "تلك",    // that (f) — ends in ك
  "ذلك",    // that (m) — ends in ك
  "ذلكم",   // that (pl) — ends in كم
  "هناك",   // there — ends in ك
  "كذلك",   // likewise — ends in ك
  "الله",   // God — ends in ه
  "الذي",   // who/which (m) — ends in ي
  "التي",   // who/which (f) — ends in ي
  "الذين",  // who/which (m pl)
  "اللتي",  // who/which (f variant) — ends in ي
  "اللاتي", // who/which (f pl) — ends in ي
  "اللواتي",// who/which (f pl alt) — ends in ي

  // Common nouns/adjectives ending in ك (not attached pronoun)
  "ملك",    // king
  "شريك",   // partner
  "مبارك",  // blessed
  "بنك",    // bank
  "ترك",    // leaving / Turk
  "فلك",    // orbit / astronomy
  "سلك",    // wire
  "مشترك",  // shared / common
  "محرك",   // engine / motivator
  "مالك",   // owner
  "درك",    // comprehension
  "حرك",    // moved
  "هلك",    // perished

  // Common words ending in ه (not attached pronoun — ة taa-marbuta is safe)
  "وجه",    // face
  "فقه",    // jurisprudence
  "شبه",    // resemblance
  "رفاه",   // wellbeing / luxury
  "منبه",   // alarm
  "جاه",    // prestige

  // Common adjectives/nouns ending in ي (not attached possessive ـي)
  "ماضي",   // past (adj)
  "حالي",   // current (adj)
  "قاضي",   // judge
  "رأي",    // opinion
  "تحدي",   // challenge
  "باقي",   // remaining
  "مبني",   // built (adj)
  "علي",    // name / upon (prep form)
  "نادي",   // club

  // Common words ending in كن (not ـكُنَّ)
  "ممكن",   // possible
  "يمكن",   // it is possible
  "مسكن",   // dwelling
  "أمكن",   // was possible
  "تمكن",   // managed

  // Common words ending in هن (not ـهُنَّ)
  "كاهن",   // priest
  "راهن",   // current / pledging

  // Common words ending in هم (not ـهُمْ)
  "أسهم",   // shares / stocks
  "اتهم",   // accused (verb)

  // Dual verb / other forms ending in نا (not ـنا)
  "كانا",   // they two were (dual)

  // Vocative particle ending in ها (not ـها)
  "أيها",   // O you (vocative)

  // Common words ending in ك that are not attached pronouns
  "كعك",    // cake / cookies — ends in ك
  "الكعك",  // the cake

  // Common verbs whose root ends in ك (not attached ـكَ pronoun)
  "يبارك",  // he blesses
  "بارك",   // he blessed
]);

/**
 * Detect whether a normalized Arabic token ends in an attached pronoun suffix.
 * Returns { suffix, label, normStemLength } or null.
 * Requires at least 2 normalized stem characters to avoid false positives on
 * short prepositions (e.g. في, به, له).
 */
function detectAttachedPronoun(normalizedToken) {
  if (ATTACHED_PRONOUN_EXCLUSIONS.has(normalizedToken)) return null;
  if (normalizedToken.length < 3) return null; // need ≥2-char stem + ≥1-char suffix
  for (const { suffix, label } of ATTACHED_PRONOUN_SUFFIXES) {
    if (
      normalizedToken.endsWith(suffix) &&
      normalizedToken.length - suffix.length >= 2
    ) {
      return { suffix, label, normStemLength: normalizedToken.length - suffix.length };
    }
  }
  return null;
}

/**
 * Returns all ATTACHED_PRONOUN_SUFFIXES entries whose suffix matches the end
 * of `normalizedToken` (with ≥2-char stem requirement).  Used to populate the
 * admin pronoun-override options in the tooltip.
 */
function getMatchingSuffixOptions(normalizedToken) {
  if (normalizedToken.length < 3) return [];
  return ATTACHED_PRONOUN_SUFFIXES.filter(
    ({ suffix }) =>
      normalizedToken.endsWith(suffix) &&
      normalizedToken.length - suffix.length >= 2
  );
}

/**
 * Split an original Arabic token (may contain diacritics) at the boundary
 * after `normStemLength` non-diacritic characters.
 * Returns [stem, suffixWithHarakat].
 *
 * Note: uses a local non-global regex instead of ARABIC_DIACRITICS_REGEX because
 * calling .test() in a loop with a /g regex advances lastIndex and causes false negatives.
 */
function splitTokenAtNormPos(original, normStemLength) {
  // Non-global copy of ARABIC_DIACRITICS_REGEX — safe for repeated .test() calls.
  const isDiacritic = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/;
  let count = 0;
  for (let i = 0; i < original.length; i++) {
    if (!isDiacritic.test(original[i])) {
      count++;
      if (count === normStemLength) {
        // Advance past any immediately-following diacritics that belong to the stem char.
        let splitPos = i + 1;
        while (splitPos < original.length && isDiacritic.test(original[splitPos])) {
          splitPos++;
        }
        return [original.slice(0, splitPos), original.slice(splitPos)];
      }
    }
  }
  return [original, ""];
}

/**
 * Heuristic: detect whether an Arabic sentence is likely a nominal sentence (جملة اسمية).
 *
 * A nominal sentence starts with a noun/pronoun (مبتدأ).
 * A verbal sentence (جملة فعلية) starts with a verb.
 *
 * Detection logic (A1-level Arabic):
 *  1. If first word has definite article ال  → nominal.
 *  2. If first word is a known pronoun/demonstrative → nominal.
 *  3. If first word starts with a present-tense verb prefix
 *     (ي/ت/ن/أ + short vowel) → verbal → not nominal.
 *  4. If first word starts with an imperative prefix (اِ/اُ) → verbal.
 *  5. Otherwise → treat as nominal.
 */
function isNominalSentence(arabicText) {
  if (!arabicText) return false;

  const DIACRITICS = /[\u064B-\u065F\u0610-\u061A\u0670\u06D6-\u06ED]/g;
  const strip = (s) => (s || "").replace(DIACRITICS, "");
  const allWords = arabicText.trim().split(/\s+/);

  // Particles that sit before the real sentence and must be skipped when
  // deciding nominal vs verbal: interrogative هَلْ, negation لَمْ/لَنْ/لَا,
  // emphasis قَدْ/لَقَدْ, future سَوْفَ/سَـ, conditional إِذَا/إِذْ.
  const SKIP_PARTICLES = new Set([
    "هل","لم","لن","لا","ألا","أما",
    "قد","لقد","سوف","س",
    "إذا","إذ","لو","لما","حين","عندما","بينما","حتى",
    // Interrogative words that never head a nominal sentence
    "أي","أيّ","كيف","أين","متى","كم","ماذا",
    // Negation / conditional / sequential
    "ما","إن","ثم",
  ]);

  // Find the first word that is not a skip-particle.
  let startIdx = 0;
  for (let i = 0; i < allWords.length; i++) {
    const b = strip(allWords[i]).replace(/^[.,!?؟،؛:"«»()[\]]+/, "");
    if (SKIP_PARTICLES.has(b)) {
      startIdx = i + 1;
    } else {
      break;
    }
  }
  if (startIdx >= allWords.length) return false;

  const firstWord = allWords[startIdx].replace(/^[.,!?؟،؛:"«»()[\]]+/, "");
  if (!firstWord) return false;
  const bare = strip(firstWord);

  // Rule 1: definite article → nominal
  if (bare.startsWith("ال")) return true;

  // Rule 2: known pronouns and demonstratives → nominal
  // (checked before the verb-prefix regex so e.g. أَنَا/تِلْكَ are not misclassified)
  const NOMINAL_STARTERS = new Set([
    "أنا","أنت","أنتِ","أنتم","أنتن",
    "هو","هي","هم","هن","هما","نحن",
    "هذا","هذه","هذان","هذين","هؤلاء",
    "ذلك","تلك","ذلكم","أولئك","تلكما",
    "الذي","التي","الذين","اللواتي","اللاتي",
  ]);
  if (NOMINAL_STARTERS.has(bare)) {
    // If followed by a past-tense verb it's SVO verbal, not nominal
    const nextWord = allWords.length > startIdx + 1 ? allWords[startIdx + 1] : "";
    if (nextWord && isPastVerbToken(nextWord)) return false;
    return true;
  }

  // Rule 2.5: first word is indefinite (carries tanwin ً ٌ ٍ).
  // An indefinite noun CAN be مبتدأ when it heads a coordinated noun phrase
  // (e.g. زَائِتَرَةٌ وَفَتِيرَةٌ — "thyme and flatbread").
  // However, an isolated indefinite adjective/adverb phrase (جَيِّدٌ جِدًّا،
  // مُمْتَازٌ) has an implied subject and is NOT a full جملة اسمية.
  // Rule: if the first word has tanwin AND the next real word does NOT start
  // with وَ/فَ (coordination), reject as non-nominal.
  if (/[\u064B\u064C\u064D]/.test(firstWord)) {
    const nextBare = allWords.length > startIdx + 1
      ? strip(allWords[startIdx + 1]).replace(/^[.,!?؟،؛:"«»()[\]]+/, "")
      : "";
    if (!/^[وف]/.test(nextBare)) return false;
  }

  // Rule 3: present-tense verb prefix — ي/ت/ن/أ + short vowel (fatha/damma/kasra)
  // Unicode: \u064E fatha, \u064F damma, \u0650 kasra
  if (/^[يتنأ][\u064E\u064F\u0650]/.test(firstWord)) return false;

  // Rule 4: imperative — alef + kasra/damma (e.g. اِذهَبْ، اُكتُبْ)
  if (/^[\u0627\u0623\u0625][\u0650\u064F]/.test(firstWord)) return false;

  // Rule 2.9: noun subject followed by a past-tense verb = SVO verbal sentence
  if (allWords.length > startIdx + 1 && isPastVerbToken(allWords[startIdx + 1])) return false;

  // Default: no verbal indicator → treat as nominal
  return true;
}

/**
 * findMubtadaEnd(tokens) — return index where خبر starts (= size of مبتدأ group).
 *
 * For any مبتدأ head (definite ال… or indefinite noun), extend the مبتدأ past:
 *  - Prepositional phrases:  بِـ / فِي / عَلَى / مِن / إِلَى / لِـ / عَنْ / مَعَ / كَـ
 *  - Coordinated nouns/PPs: any token starting with وَ or فَ
 * Always leave at least one token for the خبر.
 *
 * Examples:
 *   الْبِيتْزَا بِالْجُبْنَةِ وَالطَّمَاطِمِ | جَيِّدَةٌ جِدًّا     (3 | 2)
 *   الْخُبْزُ وَالْجُبْنُ وَالطَّمَاطِمُ    | أَسَاسِيَّاتٌ        (3 | 1)
 *   زَائِتَرَةٌ وَفَتِيرَةٌ                 | مَكَوِّنَاتٌ طَبِيعِيَّةٌ (2 | 3)
 *   هُوَ                                   | طَالِبٌ              (1 | 1)
 */
function findMubtadaEnd(tokens) {
  if (!tokens.length) return 0;
  const DIACRITICS = /[\u064B-\u065F\u0610-\u061A\u0670\u06D6-\u06ED]/g;
  const strip = (s) => (s || "").replace(DIACRITICS, "");

  const PRONOUNS = new Set([
    "أنا","أنت","أنتِ","أنتم","أنتن",
    "هو","هي","هم","هن","هما","نحن",
    "هذا","هذه","هذان","هذين","هؤلاء",
    "ذلك","تلك","ذلكم","أولئك","تلكما",
  ]);

  const bare0 = strip(tokens[0]);
  // Pronoun subject → always single token (خبر is everything else)
  if (PRONOUNS.has(bare0)) return 1;

  let end = 1;

  // Idafa (إضافة) detection: if the first word has no ال prefix and no
  // tanwin (ً ٌ ٍ), it is likely in construct state (مضاف).  If the next
  // word starts with ال it is the مضاف إليه — include it in the مبتدأ.
  // e.g. صُنْعُ الْفِيدْيُوهَاتِ → both form the مبتدأ noun phrase.
  const TANWIN = /[\u064B\u064C\u064D]/;
  const hasAl = bare0.startsWith("ال");
  const hasTanwin = TANWIN.test(tokens[0]);
  if (!hasAl && !hasTanwin && end < tokens.length - 1) {
    const b1 = strip(tokens[end]);
    if (b1.startsWith("ال")) {
      end++; // consume the مضاف إليه
    }
  }

  // Coordination + definite-adjective extension loop.
  // Two cases extend the مبتدأ beyond the head noun:
  //   1. Coordinated noun: وَالْجُبْنُ / وَجُبْنٌ (starts with وَ/فَ, length > 2)
  //   2. Definite adjective (نعت): التَّرْفِيهِيَّةُ following a definite head —
  //      when the head starts with ال, consecutive ال-prefixed tokens are
  //      treated as agreeing adjectives and included in the مبتدأ NP.
  // Require length > 2 to exclude standalone فِي (stripped: في = 2 chars).
  while (end < tokens.length - 1) {
    const b = strip(tokens[end]);
    const isCoord = /^[وف]./.test(b) && b.length > 2;
    const isDefAdj = hasAl && b.startsWith("ال");
    if (isCoord || isDefAdj) {
      end++;
    } else {
      break;
    }
  }
  return end;
}

/**
 * findKhabarStart(tokens, mubtadaEnd)
 *
 * Returns the index where the خبر content actually begins, skipping any
 * ضمير فصل (separating/emphasis pronoun) that sits between the مبتدأ and
 * the خبر.  e.g. صُنْعُ الفِيدْيُوهَاتِ [هُوَ] هِوَايَةٌ رَائِعَةٌ
 *                 مبتدأ              فصل   خبر
 */
function findKhabarStart(tokens, mubtadaEnd) {
  const DIACRITICS = /[\u064B-\u065F\u0610-\u061A\u0670\u06D6-\u06ED]/g;
  const strip = (s) => (s || "").replace(DIACRITICS, "");
  const FASL = new Set(["هو","هي","هما","هم","هن","نحن","أنتم","أنتن"]);
  if (mubtadaEnd < tokens.length && FASL.has(strip(tokens[mubtadaEnd]))) {
    return mubtadaEnd + 1;
  }
  return mubtadaEnd;
}

/**
 * getHeuristicRoleForToken(turn, tokenIdx)
 *
 * Returns the role ("mubtada" | "khabar" | null) the heuristic would assign
 * to a given token index within a turn's arabic text.
 * Used to pre-populate the grammar role buttons in the WordBubble so the admin
 * can see and override the auto-computed annotation.
 */
function getHeuristicRoleForToken(turn, tokenIdx) {
  if (tokenIdx === undefined || tokenIdx === null) return null;
  const rawArabic = turn.arabic || "";
  const segmentPattern = /([،.؟!؛,;])/;
  const parts = rawArabic.split(segmentPattern);

  let globalOffset = 0;
  for (let pi = 0; pi < parts.length; pi++) {
    const part = parts[pi];
    if (!part) continue;
    if (segmentPattern.test(part) && part.length === 1) continue;
    const segTokens = part.trim().split(/\s+/).filter(Boolean);
    if (!segTokens.length) continue;

    const segStart = globalOffset;
    const segEnd = globalOffset + segTokens.length;

    if (tokenIdx >= segStart && tokenIdx < segEnd) {
      const localIdx = tokenIdx - segStart;
      const nominal = segTokens.length >= 2 && isNominalSentence(part.trim());
      if (!nominal) return null;
      const mubtadaEnd = findMubtadaEnd(segTokens);
      const khabarStart = findKhabarStart(segTokens, mubtadaEnd);
      if (localIdx < mubtadaEnd) return "mubtada";
      if (localIdx >= khabarStart) return "khabar";
      return null; // ضمير فصل — between مبتدأ and خبر, label neither
    }

    globalOffset = segEnd;
  }
  return null;
}

// ── Pencil icon button ────────────────────────────────────────────────────

function PlayButton({ onClick, loading, playing, title = "Play" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="ml-1 p-1 rounded text-slate-400 hover:text-emerald-600 hover:bg-slate-100 shrink-0 disabled:opacity-40"
      disabled={loading}
    >
      {loading ? (
        // Spinner
        <svg
          className="w-4 h-4 animate-spin text-emerald-500"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      ) : playing ? (
        // Stop square
        <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
          <rect x="5" y="5" width="14" height="14" rx="2" />
        </svg>
      ) : (
        // Play triangle
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 4l14 8-14 8V4z" />
        </svg>
      )}
    </button>
  );
}

function PencilButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-1 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 shrink-0"
      title="Edit"
      aria-label="Edit"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z"
        />
      </svg>
    </button>
  );
}

// ── ScenarioTurn inline editor ────────────────────────────────────────────

function ScenarioTurnEdit({ turn, onSave, onDelete, onCancel, existingSpeakers = [] }) {
  const [arabic, setArabic] = useState(turn.arabic || "");
  const [transliteration, setTransliteration] = useState(turn.transliteration || "");
  const [translation, setTranslation] = useState(turn.translation || "");
  const [speaker, setSpeaker] = useState(turn.speaker || "");
  const [speakerDdOpen, setSpeakerDdOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await patchScenarioTurn(turn.id, {
        arabic,
        transliteration,
        translation,
        speaker: speaker.trim(),
      });
      onSave(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this dialogue line? This cannot be undone.")) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteScenarioTurn(turn.id);
      onDelete(turn.id);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-2 mt-1">
      {/* Speaker */}
      <div className="relative">
        <label className="block text-xs text-slate-500 mb-0.5">Speaker <span className="text-slate-400">(optional)</span></label>
        <input
          type="text"
          value={speaker}
          placeholder="Leave blank for an unattributed line"
          onChange={(e) => { setSpeaker(e.target.value); setSpeakerDdOpen(true); }}
          onFocus={() => setSpeakerDdOpen(true)}
          onBlur={() => setTimeout(() => setSpeakerDdOpen(false), 150)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        {speakerDdOpen && existingSpeakers.filter((s) => s !== turn.speaker || speaker !== turn.speaker).length > 0 && (
          <ul className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-md shadow-md max-h-36 overflow-y-auto">
            {existingSpeakers
              .filter((s) => s.toLowerCase().includes(speaker.toLowerCase()))
              .map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    onMouseDown={() => { setSpeaker(s); setSpeakerDdOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-emerald-50 transition-colors"
                  >
                    {s}
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Arabic</label>
        <input
          type="text"
          value={arabic}
          onChange={(e) => setArabic(e.target.value)}
          dir="rtl"
          lang="ar"
          className="w-full px-2 py-1 rounded border border-slate-300 text-base font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Transliteration</label>
        <input
          type="text"
          value={transliteration}
          onChange={(e) => setTransliteration(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Translation</label>
        <input
          type="text"
          value={translation}
          onChange={(e) => setTranslation(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || deleting}
          className="px-3 py-1 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? "Saving\u2026" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving || deleting}
          className="px-3 py-1 text-xs rounded-md border border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-700 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={saving || deleting}
          className="ml-auto px-3 py-1 text-xs rounded-md bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
        >
          {deleting ? "Deleting\u2026" : "Delete"}
        </button>
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
    </div>
  );
}

// ── Morphology-driven word-type detection ─────────────────────────────────
// The Verb/Noun/Pronoun/Prepositions pills read the per-occurrence word
// structure stored in turn.word_morphology[tokenIdx] (§9 shape, generated via
// the ◇ button). The stem ("root") segment's labels name the word type;
// tokens without a stored structure fall back to the older heuristics.

// ── Word bubble tooltip ───────────────────────────────────────────────────

function WordBubble({ word, anchorRect, onInfoClick, onAddToWordBank, onClose, onPlay, isLoading, isPlaying, showGrammarControls, grammarRole, isManuallyOverridden, onGrammarRoleSet, showPronounControls, pronounSuffixOptions, currentPronounAnnotation, isManualPronounOverride, globalPronounAnnotation, onPronounAnnotationSet, showPrepControls, currentPrepAnnotation, isManualPrepOverride, onPrepAnnotationSet, showDefControls, currentDefAnnotation, isManualDefOverride, onDefAnnotationSet, showWoControls, currentWoRole, currentWoSegType, isManualWoRoleOverride, isManualWoSegOverride, onWoAnnotationSet, showPtControls, currentPtAnnotation, isManualPtOverride, onPtAnnotationSet, showPtiControls, currentPtiAnnotation, isManualPtiOverride, onPtiAnnotationSet, showGenderControls, currentGenderAnnotation, isManualGenderOverride, onGenderAnnotationSet, showAdjControls, currentAdjAnnotation, isManualAdjOverride, onAdjAnnotationSet, showIdafaControls, currentIdafaAnnotation, isManualIdafaOverride, idafaHasPronounSuffix, onIdafaAnnotationSet, showNegControls, currentNegAnnotation, isManualNegOverride, onNegAnnotationSet, showQwControls, currentQwAnnotation, isManualQwOverride, onQwAnnotationSet, showConControls, currentConAnnotation, isManualConOverride, onConAnnotationSet, showVoControls, currentVoAnnotation, isManualVoOverride, onVoAnnotationSet, showFtControls, currentFtAnnotation, isManualFtOverride, onFtAnnotationSet, showOpControls, currentOpAnnotation, isManualOpOverride, onOpAnnotationSet, showCompControls, currentCompAnnotation, isManualCompOverride, onCompAnnotationSet, showDfControls, currentDfAnnotation, isManualDfOverride, onDfAnnotationSet, showBpControls, currentBpAnnotation, isManualBpOverride, onBpAnnotationSet, showImpControls, currentImpAnnotation, isManualImpOverride, onImpAnnotationSet, showLinkControls, linkCandidates, onLinkLemma, linkBusy, hasStructure, onStructureClick, canRegenStructure, onRegenStructure, regenStructureBusy }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null); // null = invisible until measured
  const [applyGlobally, setApplyGlobally] = useState(false);
  // "idle" | "adding" | "added" | "exists" | "error"
  const [wbState, setWbState] = useState("idle");
  const addToWordBank = async () => {
    if (wbState === "adding" || wbState === "added" || wbState === "exists") return;
    setWbState("adding");
    try {
      const res = await onAddToWordBank();
      setWbState(res?.already_in_bank ? "exists" : "added");
    } catch {
      setWbState("error");
    }
  };
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onClose, true); // close on any scroll
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  // Measure the bubble after render, then compute clamped position
  const reposition = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const W = el.offsetWidth;
    const H = el.offsetHeight;
    const MARGIN = 8;
    const centerX = anchorRect.left + anchorRect.width / 2;
    const left = Math.max(MARGIN, Math.min(window.innerWidth - W - MARGIN, centerX - W / 2));
    const arrowLeft = Math.max(8, Math.min(W - 20, centerX - left)) - 4;
    // Use top-based positioning only — avoids window.innerHeight mismatch on mobile
    const showAbove = anchorRect.top > H + 20;
    setPos({
      left,
      arrowLeft,
      showAbove,
      top: showAbove ? anchorRect.top - H - 10 : anchorRect.bottom + 10,
    });
  }, [anchorRect]);

  useLayoutEffect(() => { reposition(); }, [reposition]);

  // The bubble grows after it has been placed — the admin lemma-link candidates
  // arrive from an async lookup, and linking/adding swaps its contents. Since
  // "above" is anchored by its top edge, a bubble that grew taller after the
  // one-shot measure spilled downwards over the very word it points at. Re-run
  // the measure on any size change so it stays anchored (and flips below if the
  // taller bubble no longer fits above).
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => reposition());
    ro.observe(el);
    return () => ro.disconnect();
  }, [reposition]);

  const curatedDefinition = (word.senses?.find(s => s.is_primary)?.definition)
    || word.conjugation_info?.definition
    || word.senses?.[0]?.definition
    || "";

  // No curated definition yet → free probe of the shared word-gloss cache
  // (fed by Reading translations and standalone lookups). Never calls the AI,
  // and a curated definition always wins once an admin adds one.
  const [cachedGloss, setCachedGloss] = useState(null);
  useEffect(() => {
    if (curatedDefinition || !word?.text) return;
    let alive = true;
    analyzeWordBankWord(word.text, { probe: true })
      .then((r) => { if (alive && r.cached && r.definition) setCachedGloss(r.definition); })
      .catch(() => {});
    return () => { alive = false; };
  }, [curatedDefinition, word?.text]);

  const definition = curatedDefinition || cachedGloss || "No definition";

  const posStyle = pos
    ? { position: "fixed", left: pos.left, top: pos.top, zIndex: 9999 }
    : { position: "fixed", visibility: "hidden", zIndex: 9999 }; // hidden until measured

  return ReactDOM.createPortal(
    <div ref={ref} style={posStyle} className="bg-slate-800 text-white rounded-lg shadow-2xl px-3 py-2 max-w-[280px] min-w-[100px]">
      {/* Main row: definition + play + info */}
      <div className="flex items-center gap-2">
        <p className="flex-1 text-sm leading-snug whitespace-normal">{definition}</p>
        {/* Play button (hidden when TTS is blocked, e.g. seatless institute admin) */}
        {isAudioPlaybackAllowed() && (
        <button
          type="button"
          title={isPlaying ? "Stop" : "Play"}
          onClick={() => onPlay(word)}
          disabled={isLoading}
          className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-emerald-400 transition-colors disabled:opacity-40"
        >
          {isLoading ? (
            <svg className="w-4 h-4 animate-spin text-emerald-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : isPlaying ? (
            <svg className="w-4 h-4 text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4l14 8-14 8V4z" />
            </svg>
          )}
        </button>
        )}
        {/* Info button — hidden for stub words (no word-detail page to open) */}
        {onInfoClick && (
        <button
          type="button"
          title="Full word detail"
          onClick={() => { onInfoClick(); onClose(); }}
          className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-emerald-400 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
        </button>
        )}
        {/* Add to Word Bank */}
        {onAddToWordBank && (
          <button
            type="button"
            title={
              wbState === "added" ? "Added to your Word Bank"
              : wbState === "exists" ? "Already in your Word Bank"
              : wbState === "error" ? "Could not add — tap to retry"
              : "Add to Word Bank"
            }
            aria-label="Add to Word Bank"
            onClick={addToWordBank}
            disabled={wbState === "adding"}
            className={`shrink-0 p-0.5 rounded-full transition-colors disabled:opacity-40 ${
              wbState === "added" || wbState === "exists"
                ? "text-emerald-400"
                : wbState === "error"
                ? "text-red-400 hover:text-red-300"
                : "text-slate-400 hover:text-emerald-400"
            }`}
          >
            {wbState === "adding" ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (wbState === "added" || wbState === "exists") ? (
              // filled bookmark
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 2h12a1 1 0 011 1v18l-7-4-7 4V3a1 1 0 011-1z" />
              </svg>
            ) : (
              // outline bookmark
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 3h12a1 1 0 011 1v17l-7-4-7 4V4a1 1 0 011-1z" />
              </svg>
            )}
          </button>
        )}
        {/* Word structure (morphology blow-up) — shown only when data exists.
            Structure is per-sentence occurrence (#92), so it lives on the turn.
            Icon-only to sit alongside the other bubble icons (◇ = diamond). */}
        {hasStructure && (
          <button
            type="button"
            title="View word structure"
            aria-label="View word structure"
            onClick={() => { onStructureClick(); onClose(); }}
            className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-emerald-400 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l9 9-9 9-9-9 9-9z" />
            </svg>
          </button>
        )}
        {/* Admin: (re)generate this word's structure from its sentence context.
            Used to fix a wrong structure, or generate one not yet made. */}
        {canRegenStructure && (
          <button
            type="button"
            disabled={regenStructureBusy}
            title={hasStructure
              ? "Regenerate this word's structure from its sentence (AI)"
              : "Generate this word's structure from its sentence (AI)"}
            aria-label={hasStructure ? "Regenerate word structure" : "Generate word structure"}
            onClick={() => onRegenStructure && onRegenStructure()}
            className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-amber-400 transition-colors disabled:opacity-40"
          >
            {regenStructureBusy ? (
              <svg className="w-4 h-4 animate-spin text-amber-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M19 9a8 8 0 00-14-2.5M5 15a8 8 0 0014 2.5" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Link-definition affordance — admin only. Shown when this word has no
          definition of its own but matches an existing base word (same word
          minus a leading و/ال), so its definition can be shared, not duplicated. */}
      {showLinkControls && (linkCandidates?.length > 0) && (
        <div className="mt-2 pt-2 border-t border-slate-600">
          <p className="text-[10px] text-slate-400 mb-1">No definition — link to an existing word:</p>
          <div className="flex flex-col gap-1">
            {linkCandidates.map((c) => (
              <button
                key={c.id}
                type="button"
                disabled={linkBusy}
                onClick={() => onLinkLemma(c.id)}
                dir="rtl"
                className="text-right px-2 py-1 rounded bg-slate-700 hover:bg-emerald-800 border border-slate-500 hover:border-emerald-400 transition-colors disabled:opacity-40"
              >
                <span className="font-arabic text-sm text-white">{c.text}</span>
                {c.definition && (
                  <span className="block text-[10px] text-slate-300 truncate" dir="ltr">{c.definition}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Linked-lemma indicator — admin only. Lets an admin undo a bad link. */}
      {showLinkControls && word.lemma && (
        <div className="mt-2 pt-2 border-t border-slate-600 flex items-center gap-1.5 text-[10px] text-slate-300">
          <span>🔗 linked to <span className="font-arabic">{word.lemma.text}</span></span>
          <button
            type="button"
            title="Unlink definition"
            disabled={linkBusy}
            onClick={() => onLinkLemma(null)}
            className="ml-auto px-1.5 py-0.5 rounded border border-slate-500 text-slate-400 hover:text-red-300 hover:border-red-400 transition-colors disabled:opacity-40"
          >
            ✕
          </button>
        </div>
      )}

      {/* Grammar role row — admin only, shown when Show Grammar is active */}
      {showGrammarControls && (
        <div className="mt-2 pt-2 border-t border-slate-600 flex items-center gap-1.5" dir="rtl">
          <span className="text-[10px] text-slate-400 shrink-0 font-arabic">دور الكلمة:</span>
          {/* مبتدأ button */}
          <button
            type="button"
            title="Tag as مبتدأ (subject)"
            onClick={() => onGrammarRoleSet(grammarRole === "mubtada" ? null : "mubtada")}
            className={`px-2 py-0.5 rounded text-[10px] font-semibold font-arabic border transition-colors ${
              grammarRole === "mubtada"
                ? "bg-blue-500 border-blue-400 text-white"
                : "bg-slate-700 border-slate-500 text-blue-300 hover:bg-blue-900 hover:border-blue-400"
            }`}
          >
            مبتدأ
          </button>
          {/* خبر button */}
          <button
            type="button"
            title="Tag as خبر (predicate)"
            onClick={() => onGrammarRoleSet(grammarRole === "khabar" ? null : "khabar")}
            className={`px-2 py-0.5 rounded text-[10px] font-semibold font-arabic border transition-colors ${
              grammarRole === "khabar"
                ? "bg-emerald-500 border-emerald-400 text-white"
                : "bg-slate-700 border-slate-500 text-emerald-300 hover:bg-emerald-900 hover:border-emerald-400"
            }`}
          >
            خبر
          </button>
          {/* Clear button — only shown when a role is set */}
          {grammarRole && (
            <button
              type="button"
              title="Remove grammar tag"
              onClick={() => onGrammarRoleSet(null)}
              className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-white hover:border-slate-300 transition-colors"
            >
              ✕
            </button>
          )}
          {/* Reset to auto — only shown when there is a manual override */}
          {isManuallyOverridden && (
            <button
              type="button"
              title="Reset to auto-computed annotation"
              onClick={() => onGrammarRoleSet("auto")}
              className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
              dir="ltr"
            >
              ↺ auto
            </button>
          )}
        </div>
      )}

      {/* Pronoun override controls — admin only, shown in pronounMode */}
      {showPronounControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">ضمير:</span>
            {/* "Not a pronoun" — clears any detection */}
            <button
              type="button"
              title="Mark as not a pronoun"
              onClick={() => onPronounAnnotationSet("pronoun:none", applyGlobally)}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentPronounAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا ضمير
            </button>
            {/* One button per matching suffix */}
            {(pronounSuffixOptions || []).map(({ suffix, label }) => (
              <button
                key={suffix}
                type="button"
                title={`Tag as ${label}`}
                onClick={() => onPronounAnnotationSet("pronoun:" + suffix, applyGlobally)}
                className={`px-2 py-0.5 rounded text-[10px] font-semibold font-arabic border transition-colors ${
                  currentPronounAnnotation === suffix
                    ? "bg-teal-500 border-teal-400 text-white"
                    : "bg-slate-700 border-slate-500 text-teal-300 hover:bg-teal-900 hover:border-teal-400"
                }`}
              >
                {label}
              </button>
            ))}
            {/* Reset to auto — only when there is a manual override */}
            {(isManualPronounOverride || globalPronounAnnotation !== undefined) && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onPronounAnnotationSet("auto", applyGlobally)}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
          {/* Global override indicator + checkbox */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" dir="ltr">
            {globalPronounAnnotation !== undefined && (
              <span className="text-[9px] bg-indigo-900 text-indigo-300 border border-indigo-600 rounded px-1.5 py-0.5">
                🌐 global: {globalPronounAnnotation === "none" ? "suppressed" : globalPronounAnnotation}
              </span>
            )}
            <label className="flex items-center gap-1 cursor-pointer ml-auto">
              <input
                type="checkbox"
                checked={applyGlobally}
                onChange={(e) => setApplyGlobally(e.target.checked)}
                className="w-3 h-3 accent-indigo-400"
              />
              <span className="text-[9px] text-slate-400">Apply to all topics</span>
            </label>
          </div>
        </div>
      )}

      {/* Preposition override controls — admin only, shown in prepositionMode */}
      {showPrepControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">حرف الجر:</span>
            <button
              type="button"
              title="Mark as preposition (حرف جر)"
              disabled={currentPrepAnnotation === "harf_jar"}
              onClick={() => onPrepAnnotationSet("prep:harf_jar")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentPrepAnnotation === "harf_jar"
                  ? "bg-indigo-500 border-indigo-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-indigo-300 hover:bg-indigo-900 hover:border-indigo-400"
              }`}
            >
              حرف جر
            </button>
            <button
              type="button"
              title="Mark as word after preposition (بعد حرف الجر)"
              disabled={currentPrepAnnotation === "baad_harf_jar"}
              onClick={() => onPrepAnnotationSet("prep:baad_harf_jar")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentPrepAnnotation === "baad_harf_jar"
                  ? "bg-orange-500 border-orange-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-orange-300 hover:bg-orange-900 hover:border-orange-400"
              }`}
            >
              بعد حرف جر
            </button>
            <button
              type="button"
              title="Mark as neither (suppress highlight)"
              disabled={currentPrepAnnotation === "none"}
              onClick={() => onPrepAnnotationSet("prep:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentPrepAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualPrepOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onPrepAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Definite article override controls — admin only, shown in definiteMode */}
      {showDefControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">الـ:</span>
            <button
              type="button"
              title="Mark as definite (has الـ)"
              disabled={currentDefAnnotation === "definite"}
              onClick={() => onDefAnnotationSet("def:definite")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentDefAnnotation === "definite"
                  ? "bg-purple-500 border-purple-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-purple-300 hover:bg-purple-900 hover:border-purple-400"
              }`}
            >
              معرفة
            </button>
            <button
              type="button"
              title="Mark as indefinite (no الـ)"
              disabled={currentDefAnnotation === "indefinite"}
              onClick={() => onDefAnnotationSet("def:indefinite")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentDefAnnotation === "indefinite"
                  ? "bg-sky-500 border-sky-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-sky-300 hover:bg-sky-900 hover:border-sky-400"
              }`}
            >
              نكرة
            </button>
            <button
              type="button"
              title="Mark as neither (suppress highlight)"
              disabled={currentDefAnnotation === "none"}
              onClick={() => onDefAnnotationSet("def:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentDefAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualDefOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onDefAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Past-tense verb override controls — admin only, shown in past_tense mode */}
      {showPtControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">فعل:</span>
            <button
              type="button"
              title="Mark as verb (فعل)"
              disabled={currentPtAnnotation === "fiil"}
              onClick={() => onPtAnnotationSet("pt:fiil")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentPtAnnotation === "fiil"
                  ? "bg-rose-500 border-rose-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-rose-300 hover:bg-rose-900 hover:border-rose-400"
              }`}
            >
              فعل
            </button>
            <button
              type="button"
              title="Suppress verb highlight"
              disabled={currentPtAnnotation === "none"}
              onClick={() => onPtAnnotationSet("pt:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentPtAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualPtOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onPtAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Present-tense-intro verb override controls — admin only, shown in present_tense_intro mode */}
      {showPtiControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">فعل:</span>
            <button
              type="button"
              title="Mark as present tense verb (فعل)"
              disabled={currentPtiAnnotation === "fiil"}
              onClick={() => onPtiAnnotationSet("pti:fiil")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentPtiAnnotation === "fiil"
                  ? "bg-green-600 border-green-500 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-green-400 hover:bg-green-900 hover:border-green-400"
              }`}
            >
              فعل
            </button>
            <button
              type="button"
              title="Suppress verb highlight"
              disabled={currentPtiAnnotation === "none"}
              onClick={() => onPtiAnnotationSet("pti:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentPtiAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualPtiOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onPtiAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Gender override controls — admin only, shown in gender mode */}
      {showGenderControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">الجنس:</span>
            <button
              type="button"
              title="Mark as verb (فعل)"
              disabled={currentGenderAnnotation === "fiil"}
              onClick={() => onGenderAnnotationSet("gend:fiil")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentGenderAnnotation === "fiil"
                  ? "bg-rose-500 border-rose-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-rose-300 hover:bg-rose-900 hover:border-rose-400"
              }`}
            >
              فعل
            </button>
            <button
              type="button"
              title="Mark as masculine (مذكر)"
              disabled={currentGenderAnnotation === "masculine"}
              onClick={() => onGenderAnnotationSet("gend:masculine")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentGenderAnnotation === "masculine"
                  ? "bg-sky-500 border-sky-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-sky-300 hover:bg-sky-900 hover:border-sky-400"
              }`}
            >
              مذكر
            </button>
            <button
              type="button"
              title="Mark as feminine (مؤنث)"
              disabled={currentGenderAnnotation === "feminine"}
              onClick={() => onGenderAnnotationSet("gend:feminine")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentGenderAnnotation === "feminine"
                  ? "bg-fuchsia-500 border-fuchsia-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-fuchsia-300 hover:bg-fuchsia-900 hover:border-fuchsia-400"
              }`}
            >
              مؤنث
            </button>
            <button
              type="button"
              title="Suppress gender highlight"
              disabled={currentGenderAnnotation === "none"}
              onClick={() => onGenderAnnotationSet("gend:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentGenderAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualGenderOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onGenderAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Adjective role override controls — admin only, shown in adjectives mode */}
      {showAdjControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">نوع:</span>
            <button
              type="button"
              title="Mark as noun (اسم)"
              disabled={currentAdjAnnotation === "noun"}
              onClick={() => onAdjAnnotationSet("adj:noun")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentAdjAnnotation === "noun"
                  ? "bg-blue-500 border-blue-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-blue-300 hover:bg-blue-900 hover:border-blue-400"
              }`}
            >
              اسم
            </button>
            <button
              type="button"
              title="Mark as adjective (نعت)"
              disabled={currentAdjAnnotation === "adjective"}
              onClick={() => onAdjAnnotationSet("adj:adjective")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentAdjAnnotation === "adjective"
                  ? "bg-emerald-500 border-emerald-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-emerald-300 hover:bg-emerald-900 hover:border-emerald-400"
              }`}
            >
              نعت
            </button>
            <button
              type="button"
              title="Suppress adjective highlight"
              disabled={currentAdjAnnotation === "none"}
              onClick={() => onAdjAnnotationSet("adj:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentAdjAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualAdjOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onAdjAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Idafa role override controls — admin only, shown in idafa mode */}
      {showIdafaControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">نوع:</span>
            <button
              type="button"
              title="Mark as مضاف (possessed noun)"
              disabled={currentIdafaAnnotation === "mudaf"}
              onClick={() => onIdafaAnnotationSet("idafa:mudaf")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentIdafaAnnotation === "mudaf"
                  ? "bg-blue-500 border-blue-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-blue-300 hover:bg-blue-900 hover:border-blue-400"
              }`}
            >
              مضاف
            </button>
            <button
              type="button"
              title="Mark as مضاف إليه (owner noun)"
              disabled={currentIdafaAnnotation === "mudaf_ilaih"}
              onClick={() => onIdafaAnnotationSet("idafa:mudaf_ilaih")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentIdafaAnnotation === "mudaf_ilaih"
                  ? "bg-amber-500 border-amber-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-amber-300 hover:bg-amber-900 hover:border-amber-400"
              }`}
            >
              مضاف إليه
            </button>
            <button
              type="button"
              title="Suppress Idafa highlight"
              disabled={currentIdafaAnnotation === "none"}
              onClick={() => onIdafaAnnotationSet("idafa:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentIdafaAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualIdafaOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onIdafaAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
          {/* Suffix (attached pronoun) مضاف إليه toggle — only when token has a pronoun */}
          {idafaHasPronounSuffix && (
            <div className="flex items-center gap-1 flex-wrap mt-1">
              <span className="text-[10px] text-slate-400 shrink-0 font-arabic">الضمير:</span>
              <button
                type="button"
                title="Pronoun suffix is مضاف إليه (show badge)"
                disabled={currentIdafaAnnotation !== "dual_no_suffix"}
                onClick={() => onIdafaAnnotationSet("auto")}
                className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                  currentIdafaAnnotation !== "dual_no_suffix"
                    ? "bg-amber-500 border-amber-400 text-white cursor-default"
                    : "bg-slate-700 border-slate-500 text-amber-300 hover:bg-amber-900 hover:border-amber-400"
                }`}
              >
                مضاف إليه ✓
              </button>
              <button
                type="button"
                title="Suppress pronoun suffix مضاف إليه badge"
                disabled={currentIdafaAnnotation === "dual_no_suffix"}
                onClick={() => onIdafaAnnotationSet("idafa:dual_no_suffix")}
                className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                  currentIdafaAnnotation === "dual_no_suffix"
                    ? "bg-slate-500 border-slate-400 text-white cursor-default"
                    : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
                }`}
              >
                ✕ لا
              </button>
            </div>
          )}
        </div>
      )}


      {/* Negation particle override controls — admin only, shown in negation mode */}
      {showNegControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">نفي:</span>
            <button
              type="button"
              title="Mark as negation particle (نفي)"
              disabled={currentNegAnnotation === "particle"}
              onClick={() => onNegAnnotationSet("neg:particle")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentNegAnnotation === "particle"
                  ? "bg-red-500 border-red-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-red-300 hover:bg-red-900 hover:border-red-400"
              }`}
            >
              نفي
            </button>
            <button
              type="button"
              title="Mark as jussive verb (مجزوم)"
              disabled={currentNegAnnotation === "majzoom"}
              onClick={() => onNegAnnotationSet("neg:majzoom")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentNegAnnotation === "majzoom"
                  ? "bg-orange-500 border-orange-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-orange-300 hover:bg-orange-900 hover:border-orange-400"
              }`}
            >
              مجزوم
            </button>
            <button
              type="button"
              title="Mark as accusative (منصوب)"
              disabled={currentNegAnnotation === "mansuub"}
              onClick={() => onNegAnnotationSet("neg:mansuub")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentNegAnnotation === "mansuub"
                  ? "bg-orange-500 border-orange-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-orange-300 hover:bg-orange-900 hover:border-orange-400"
              }`}
            >
              منصوب
            </button>
            <button
              type="button"
              title="Suppress negation highlight"
              disabled={currentNegAnnotation === "none"}
              onClick={() => onNegAnnotationSet("neg:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentNegAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualNegOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onNegAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Question-word override controls — admin only, shown in question_words mode */}
      {showQwControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">أداة:</span>
            <button
              type="button"
              title="Mark as question word (أداة استفهام)"
              disabled={currentQwAnnotation === "question_word"}
              onClick={() => onQwAnnotationSet("qw:question_word")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentQwAnnotation === "question_word"
                  ? "bg-yellow-500 border-yellow-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-yellow-300 hover:bg-yellow-900 hover:border-yellow-400"
              }`}
            >
              أداة
            </button>
            <button
              type="button"
              title="Suppress question word highlight"
              disabled={currentQwAnnotation === "none"}
              onClick={() => onQwAnnotationSet("qw:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentQwAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualQwOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onQwAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Connector override controls — admin only, shown in connectors mode */}
      {showConControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">رابط:</span>
            <button
              type="button"
              title="Mark as وَ (and)"
              disabled={currentConAnnotation === "wa" || currentConAnnotation === "wa_prefix"}
              onClick={() => onConAnnotationSet("con:wa")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentConAnnotation === "wa" || currentConAnnotation === "wa_prefix"
                  ? "bg-gray-400 border-gray-300 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-gray-300 hover:bg-gray-700 hover:border-gray-400"
              }`}
            >
              وَ
            </button>
            <button
              type="button"
              title="Mark as ثُمَّ (then)"
              disabled={currentConAnnotation === "thumma"}
              onClick={() => onConAnnotationSet("con:thumma")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentConAnnotation === "thumma"
                  ? "bg-green-500 border-green-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-green-300 hover:bg-green-900 hover:border-green-400"
              }`}
            >
              ثُمَّ
            </button>
            <button
              type="button"
              title="Mark as لَكِن (but)"
              disabled={currentConAnnotation === "lakin"}
              onClick={() => onConAnnotationSet("con:lakin")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentConAnnotation === "lakin"
                  ? "bg-red-500 border-red-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-red-300 hover:bg-red-900 hover:border-red-400"
              }`}
            >
              لَكِن
            </button>
            <button
              type="button"
              title="Mark as لِأَنَّ (because)"
              disabled={currentConAnnotation === "lianna"}
              onClick={() => onConAnnotationSet("con:lianna")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentConAnnotation === "lianna"
                  ? "bg-blue-500 border-blue-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-blue-300 hover:bg-blue-900 hover:border-blue-400"
              }`}
            >
              لِأَنَّ
            </button>
            <button
              type="button"
              title="Suppress connector highlight"
              disabled={currentConAnnotation === "none"}
              onClick={() => onConAnnotationSet("con:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentConAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualConOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onConAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Verb + Object override controls — admin only, shown in verb_object mode */}
      {showVoControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">دور الكلمة:</span>
            <button
              type="button"
              title="Mark as verb (فعل)"
              disabled={currentVoAnnotation === "verb"}
              onClick={() => onVoAnnotationSet("vo:verb")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentVoAnnotation === "verb"
                  ? "bg-green-500 border-green-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-green-300 hover:bg-green-900 hover:border-green-400"
              }`}
            >
              فعل
            </button>
            <button
              type="button"
              title="Mark as object (مفعول به)"
              disabled={currentVoAnnotation === "object"}
              onClick={() => onVoAnnotationSet("vo:object")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentVoAnnotation === "object"
                  ? "bg-sky-500 border-sky-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-sky-300 hover:bg-sky-900 hover:border-sky-400"
              }`}
            >
              مفعول به
            </button>
            <button
              type="button"
              title="Mark as mudaf / possessed noun (مضاف)"
              disabled={currentVoAnnotation === "mudaf"}
              onClick={() => onVoAnnotationSet("vo:mudaf")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentVoAnnotation === "mudaf"
                  ? "bg-blue-500 border-blue-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-blue-300 hover:bg-blue-900 hover:border-blue-400"
              }`}
            >
              مضاف
            </button>
            <button
              type="button"
              title="Mark as mudaf ilaih / possessor noun (مضاف إليه)"
              disabled={currentVoAnnotation === "mudaf_ilaih"}
              onClick={() => onVoAnnotationSet("vo:mudaf_ilaih")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentVoAnnotation === "mudaf_ilaih"
                  ? "bg-amber-500 border-amber-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-amber-300 hover:bg-amber-900 hover:border-amber-400"
              }`}
            >
              مضاف إليه
            </button>
            <button
              type="button"
              title="Mark as both direct object and mudaf (مفعول به + مضاف)"
              disabled={currentVoAnnotation === "object_mudaf"}
              onClick={() => onVoAnnotationSet("vo:object_mudaf")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentVoAnnotation === "object_mudaf"
                  ? "bg-sky-500 border-blue-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-sky-200 hover:bg-sky-900 hover:border-sky-400"
              }`}
            >
              مفعول + مضاف
            </button>
            <button
              type="button"
              title="Suppress verb/object highlight"
              disabled={currentVoAnnotation === "none"}
              onClick={() => onVoAnnotationSet("vo:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentVoAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualVoOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onVoAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Future Tense override controls — admin only, shown in future_tense mode */}
      {showFtControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">المستقبل:</span>
            <button
              type="button"
              title="Mark as سَوْفَ future particle"
              disabled={currentFtAnnotation === "sawfa"}
              onClick={() => onFtAnnotationSet("ft:sawfa")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentFtAnnotation === "sawfa"
                  ? "bg-purple-500 border-purple-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-purple-300 hover:bg-purple-900 hover:border-purple-400"
              }`}
            >
              سَوْفَ
            </button>
            <button
              type="button"
              title="Mark as سَـ prefix verb"
              disabled={currentFtAnnotation === "sa_prefix"}
              onClick={() => onFtAnnotationSet("ft:sa_prefix")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentFtAnnotation === "sa_prefix"
                  ? "bg-purple-500 border-purple-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-purple-300 hover:bg-purple-900 hover:border-purple-400"
              }`}
            >
              سَـ + فعل
            </button>
            <button
              type="button"
              title="Mark as future verb (فعل)"
              disabled={currentFtAnnotation === "verb"}
              onClick={() => onFtAnnotationSet("ft:verb")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentFtAnnotation === "verb"
                  ? "bg-green-500 border-green-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-green-300 hover:bg-green-900 hover:border-green-400"
              }`}
            >
              فعل
            </button>
            <button
              type="button"
              title="Suppress future highlight"
              disabled={currentFtAnnotation === "none"}
              onClick={() => onFtAnnotationSet("ft:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentFtAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualFtOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onFtAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Object Pronouns (Basic) override controls — admin only, shown in object_pronouns_basic mode */}
      {showOpControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">مفعول به:</span>
            <button
              type="button"
              title="Mark as verb (فعل) — green, no split"
              disabled={currentOpAnnotation === "fiil"}
              onClick={() => onOpAnnotationSet("op:fiil")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentOpAnnotation === "fiil"
                  ? "bg-green-500 border-green-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-green-300 hover:bg-green-900 hover:border-green-400"
              }`}
            >
              فعل
            </button>
            <button
              type="button"
              title="Mark as verb + attached object pronoun (مفعول به) — split green+teal"
              disabled={currentOpAnnotation === "mafool"}
              onClick={() => onOpAnnotationSet("op:mafool")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentOpAnnotation === "mafool"
                  ? "bg-teal-500 border-teal-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-teal-300 hover:bg-teal-900 hover:border-teal-400"
              }`}
            >
              فعل + ضمير
            </button>
            <button
              type="button"
              title="Suppress object-pronoun highlight"
              disabled={currentOpAnnotation === "none"}
              onClick={() => onOpAnnotationSet("op:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentOpAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualOpOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onOpAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Comparatives override controls — admin only, shown in comparatives mode */}
      {showCompControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">تفضيل:</span>
            <button
              type="button"
              title="Mark as comparative (اسم تفضيل) — pink"
              disabled={currentCompAnnotation === "comparative"}
              onClick={() => onCompAnnotationSet("comp:comparative")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentCompAnnotation === "comparative"
                  ? "bg-pink-500 border-pink-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-pink-300 hover:bg-pink-900 hover:border-pink-400"
              }`}
            >
              أفعل
            </button>
            <button
              type="button"
              title="Mark as مِنْ particle — indigo"
              disabled={currentCompAnnotation === "min"}
              onClick={() => onCompAnnotationSet("comp:min")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentCompAnnotation === "min"
                  ? "bg-indigo-500 border-indigo-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-indigo-300 hover:bg-indigo-900 hover:border-indigo-400"
              }`}
            >
              مِنْ
            </button>
            <button
              type="button"
              title="Suppress comparatives highlight"
              disabled={currentCompAnnotation === "none"}
              onClick={() => onCompAnnotationSet("comp:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentCompAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualCompOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onCompAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Dual Forms override controls — admin only, shown in dual_forms mode */}
      {showDfControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">مثنى:</span>
            <button
              type="button"
              title="Mark as dual noun (مثنى) — orange only"
              disabled={currentDfAnnotation === "dual"}
              onClick={() => onDfAnnotationSet("df:dual")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentDfAnnotation === "dual"
                  ? "bg-orange-500 border-orange-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-orange-300 hover:bg-orange-900 hover:border-orange-400"
              }`}
            >
              مثنى
            </button>
            <button
              type="button"
              title="Mark as dual adjective (مثنى + نعت) — orange + emerald stacked badges"
              disabled={currentDfAnnotation === "dual_adj"}
              onClick={() => onDfAnnotationSet("df:dual_adj")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentDfAnnotation === "dual_adj"
                  ? "bg-orange-500 border-orange-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-emerald-300 hover:bg-emerald-900 hover:border-emerald-400"
              }`}
            >
              مثنى+نعت
            </button>
            <button
              type="button"
              title="Suppress dual forms highlight"
              disabled={currentDfAnnotation === "none"}
              onClick={() => onDfAnnotationSet("df:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentDfAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualDfOverride && (
              <button
                type="button"
                title="Reset to auto-detected"
                onClick={() => onDfAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Broken Plurals override controls — admin only, shown in broken_plurals_intro mode */}
      {showBpControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">جمع التكسير:</span>
            <button
              type="button"
              title="Mark as plural form (جمع) — rose highlight"
              disabled={currentBpAnnotation === "plural"}
              onClick={() => onBpAnnotationSet("bp:plural")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentBpAnnotation === "plural"
                  ? "bg-rose-500 border-rose-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-rose-300 hover:bg-rose-900 hover:border-rose-400"
              }`}
            >
              جمع
            </button>
            <button
              type="button"
              title="Mark as singular form (مفرد) — sky highlight"
              disabled={currentBpAnnotation === "singular"}
              onClick={() => onBpAnnotationSet("bp:singular")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentBpAnnotation === "singular"
                  ? "bg-sky-500 border-sky-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-sky-300 hover:bg-sky-900 hover:border-sky-400"
              }`}
            >
              مفرد
            </button>
            <button
              type="button"
              title="Suppress broken plurals highlight"
              disabled={currentBpAnnotation === "none"}
              onClick={() => onBpAnnotationSet("bp:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentBpAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualBpOverride && (
              <button
                type="button"
                title="Reset to data-driven detection"
                onClick={() => onBpAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Imperatives override controls — admin only, shown in imperatives mode */}
      {showImpControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">فعل أمر:</span>
            <button
              type="button"
              title="Mark as imperative verb (فعل أمر) — red highlight"
              disabled={currentImpAnnotation === "fiil"}
              onClick={() => onImpAnnotationSet("imp:fiil")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentImpAnnotation === "fiil"
                  ? "bg-red-500 border-red-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-red-300 hover:bg-red-900 hover:border-red-400"
              }`}
            >
              أمر
            </button>
            <button
              type="button"
              title="Suppress imperative highlight"
              disabled={currentImpAnnotation === "none"}
              onClick={() => onImpAnnotationSet("imp:none")}
              className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${
                currentImpAnnotation === "none"
                  ? "bg-slate-500 border-slate-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600 hover:border-slate-400"
              }`}
            >
              ✕ لا
            </button>
            {isManualImpOverride && (
              <button
                type="button"
                title="Reset to data-driven detection"
                onClick={() => onImpAnnotationSet("auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Word-order override controls — admin only, shown in word_order mode */}
      {showWoControls && (
        <div className="mt-2 pt-2 border-t border-slate-600" dir="rtl">
          {/* Row 1: sentence type */}
          <div className="flex items-center gap-1 flex-wrap mb-1.5">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">نوع الجملة:</span>
            <button
              type="button"
              title="Force as nominal sentence (جملة اسمية)"
              disabled={currentWoSegType === "ismia"}
              onClick={() => onWoAnnotationSet("wo:seg:ismia")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentWoSegType === "ismia"
                  ? "bg-blue-500 border-blue-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-blue-300 hover:bg-blue-900 hover:border-blue-400"
              }`}
            >
              جملة اسمية
            </button>
            <button
              type="button"
              title="Force as verbal sentence (جملة فعلية)"
              disabled={currentWoSegType === "filia"}
              onClick={() => onWoAnnotationSet("wo:seg:filia")}
              className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                currentWoSegType === "filia"
                  ? "bg-rose-500 border-rose-400 text-white cursor-default"
                  : "bg-slate-700 border-slate-500 text-rose-300 hover:bg-rose-900 hover:border-rose-400"
              }`}
            >
              جملة فعلية
            </button>
            {isManualWoSegOverride && (
              <button
                type="button"
                title="Reset sentence type to auto-detected"
                onClick={() => onWoAnnotationSet("wo:seg:auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
          {/* Row 2: token role */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400 shrink-0 font-arabic">دور الكلمة:</span>
            {[
              { val: "mubtada",   roleKey: "mubtada",  label: "مبتدأ",     active: "bg-blue-500 border-blue-400",    inactive: "text-blue-300 hover:bg-blue-900 hover:border-blue-400" },
              { val: "khabar",    roleKey: "khabar",   label: "خبر",       active: "bg-emerald-500 border-emerald-400", inactive: "text-emerald-300 hover:bg-emerald-900 hover:border-emerald-400" },
              { val: "wo:fiil",   roleKey: "fiil",     label: "فعل",       active: "bg-rose-500 border-rose-400",    inactive: "text-rose-300 hover:bg-rose-900 hover:border-rose-400" },
              { val: "wo:faail",  roleKey: "faail",    label: "فاعل",      active: "bg-amber-500 border-amber-400",  inactive: "text-amber-300 hover:bg-amber-900 hover:border-amber-400" },
              { val: "wo:mafool", roleKey: "mafool",   label: "مفعول به",  active: "bg-teal-500 border-teal-400",    inactive: "text-teal-300 hover:bg-teal-900 hover:border-teal-400" },
              { val: "wo:none",   roleKey: "none",     label: "✕ لا",      active: "bg-slate-500 border-slate-400",  inactive: "text-slate-300 hover:bg-slate-600 hover:border-slate-400" },
            ].map(({ val, roleKey, label, active, inactive }) => (
              <button
                key={val}
                type="button"
                disabled={currentWoRole === roleKey}
                onClick={() => onWoAnnotationSet(val)}
                className={`px-2 py-0.5 rounded text-[10px] font-arabic border transition-colors ${
                  currentWoRole === roleKey
                    ? `${active} text-white cursor-default`
                    : `bg-slate-700 border-slate-500 ${inactive}`
                }`}
              >
                {label}
              </button>
            ))}
            {isManualWoRoleOverride && (
              <button
                type="button"
                title="Reset token role to auto-detected"
                onClick={() => onWoAnnotationSet("wo:auto")}
                className="px-1.5 py-0.5 rounded text-[10px] border border-slate-500 text-slate-400 hover:text-amber-300 hover:border-amber-400 transition-colors ml-auto"
                dir="ltr"
              >
                ↺ auto
              </button>
            )}
          </div>
        </div>
      )}

      {/* Arrow pointing to the word */}
      {pos && (
        <div
          className={`absolute w-2.5 h-2.5 bg-slate-800 rotate-45 ${
            pos.showAbove ? "bottom-[-5px]" : "top-[-5px]"
          }`}
          style={{ left: pos.arrowLeft }}
        />
      )}
    </div>,
    document.body
  );
}

// ── Main TopicDetail component ────────────────────────────────────────────

export default function TopicDetail({
  topic,
  loading,
  onWordSelect,
  onClearWord,
  selectedWordId,
  isAdmin,
  onTopicUpdate,
  onTurnSaved,
  onStartChat,
  onStartQuiz,
  onStartAiQuiz,
  onWordTypeChange,
  onTopicDeleted,
  focusTurn = null,
  categories = [],
  onCategoryChanged,
}) {
  const [editingTurnId, setEditingTurnId] = useState(null);
  const [refiningTurnId, setRefiningTurnId] = useState(null);
  // Per-line "generate word structures (morphology)" — id of the line in flight.
  const [morphTurnId, setMorphTurnId] = useState(null);
  const [refineModel, setRefineModel] = useState(() => {
    try {
      return localStorage.getItem("refineModel") || "";
    } catch {
      return "";
    }
  });
  const [localTurns, setLocalTurns] = useState(null);
  // Pills start OFF when a scenario loads; the learner enables the one they want.
  const [colorToggles, setColorToggles] = useState({
    new: false,
    forgotten: false,
    verb: false,
    noun: false,
    expression: false,
    pronoun: false,
    prepositions: false,
  });
  const toggleColor = (key) =>
    setColorToggles((prev) => ({ ...prev, [key]: !prev[key] }));

  // Show Grammar toggle — highlights nominal sentences and shows grammar info panel.
  const [showGrammar, setShowGrammar] = useState(false);

  // Admin: delete-topic confirmation state
  const [confirmDeleteTopic, setConfirmDeleteTopic] = useState(false);
  const [deletingTopic, setDeletingTopic] = useState(false);
  const [deleteTopicError, setDeleteTopicError] = useState(null);
  const handleDeleteTopic = async () => {
    if (!topic?.id) return;
    setDeletingTopic(true);
    setDeleteTopicError(null);
    try {
      await deleteTopic(topic.id);
      setConfirmDeleteTopic(false);
      if (onTopicDeleted) onTopicDeleted(topic.id);
    } catch (e) {
      setDeleteTopicError("Failed to delete topic.");
    } finally {
      setDeletingTopic(false);
    }
  };

  const { playingTurnId, loadingTurnId, playTurn, playAll, stop, turnRefsRef } = useTTS();

  const [wordLoadingId, setWordLoadingId] = useState(null);
  const [wordPlayingId, setWordPlayingId] = useState(null);
  const wordAudioRef = useRef(null);
  const [bubble, setBubble] = useState(null); // { word, rect }
  // Admin "link definition" affordance: base words the bubble word (a prefixed
  // و/ال surface form with no definition of its own) could share a definition with.
  const [linkCandidates, setLinkCandidates] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);


  // ── PDF attachment state ───────────────────────────────────────────────────
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [viewingPdf, setViewingPdf] = useState(null); // { id, title, file_url, filename }
  const pdfInputRef = useRef(null);

  // ── Add Dialogue state ─────────────────────────────────────────────────────
  const [showAddTurnForm, setShowAddTurnForm] = useState(false);
  const [addTurnSpeaker, setAddTurnSpeaker] = useState("");
  const [addTurnArabic, setAddTurnArabic] = useState("");
  const [addTurnTranslit, setAddTurnTranslit] = useState("");
  const [addTurnTranslation, setAddTurnTranslation] = useState("");
  const [addTurnSaving, setAddTurnSaving] = useState(false);
  const [addTurnError, setAddTurnError] = useState("");
  // Word-structure "blow-up" modal — holds the tapped word's §9 morphology.
  const [structureWord, setStructureWord] = useState(null);
  // Which "<turnId>:<tokenIdx>" is regenerating its structure right now (admin).
  const [regenStructureKey, setRegenStructureKey] = useState(null);
  const [speakerDropdownOpen, setSpeakerDropdownOpen] = useState(false);


  // ── Dialogue drag-to-reorder ───────────────────────────────────────────────
  const turnDragIndex = useRef(null);
  const turnDragOverIndex = useRef(null);
  const [turnReorderSaving, setTurnReorderSaving] = useState(false);
  const [turnReorderError, setTurnReorderError] = useState("");

  const handleTurnDragStart = (idx) => { turnDragIndex.current = idx; };

  const handleTurnDragEnter = (idx) => {
    turnDragOverIndex.current = idx;
    if (turnDragIndex.current === null || turnDragIndex.current === idx) return;
    setLocalTurns((prev) => {
      const base = prev || topic.scenario_turns || [];
      const next = [...base];
      const [moved] = next.splice(turnDragIndex.current, 1);
      next.splice(idx, 0, moved);
      turnDragIndex.current = idx;
      return next;
    });
  };

  const handleTurnDragEnd = async () => {
    turnDragIndex.current = null;
    turnDragOverIndex.current = null;
    const turns = localTurns || topic.scenario_turns || [];
    setTurnReorderSaving(true);
    setTurnReorderError("");
    try {
      await reorderScenarioTurns(topic.id, turns.map((t) => t.id));
      if (onTopicUpdate) onTopicUpdate({ ...topic, scenario_turns: turns });
    } catch (err) {
      setTurnReorderError("Failed to save order.");
    } finally {
      setTurnReorderSaving(false);
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Derive linked root verb ID from the currently open bubble
  // Persists the root verb ID even after the bubble closes (e.g. on scroll).
  // Only cleared when the user explicitly clicks a non-conjugated token.
  // linkedWordId: the word to highlight in the word list below.
  // - For conjugated verb tokens → root verb's word_id
  // - For all other tokens → match.id (the word itself)
  // Persists after bubble closes (e.g. scroll). Cleared on next token click.
  const [linkedWordId, setLinkedWordId] = useState(null);
  // Persists the last-clicked token's word ID so the grey highlight survives
  // bubble-close events (e.g. scroll). Cleared when a new token is clicked.
  const [clickedTokenId, setClickedTokenId] = useState(null);

  // Global pronoun annotation overrides fetched from the server.
  // Shape: { normalized_text: "none" | "<suffix>", ... }
  const [globalPronounAnnotations, setGlobalPronounAnnotations] = useState({});
  useEffect(() => {
    fetchGlobalPronounAnnotations()
      .then(setGlobalPronounAnnotations)
      .catch(() => {}); // non-fatal — fall back to heuristic
  }, []);

  const playWord = useCallback(async (word) => {
    if (wordAudioRef.current) {
      wordAudioRef.current.pause();
      wordAudioRef.current = null;
    }
    if (wordLoadingId === word.id || wordPlayingId === word.id) {
      setWordLoadingId(null);
      setWordPlayingId(null);
      return;
    }
    setWordLoadingId(word.id);
    setWordPlayingId(null);
    try {
      const data = await synthesizeSpeech(word.text, ""); // empty speaker → default male voice
      setWordLoadingId(null);
      setWordPlayingId(word.id);
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_content}`);
      wordAudioRef.current = audio;
      audio.onended = () => {
        setWordPlayingId(null);
        wordAudioRef.current = null;
      };
      audio.onerror = () => {
        setWordPlayingId(null);
        wordAudioRef.current = null;
      };
      audio.play().catch(() => {
        setWordPlayingId(null);
        wordAudioRef.current = null;
      });
    } catch {
      setWordLoadingId(null);
      setWordPlayingId(null);
    }
  }, [wordLoadingId, wordPlayingId]);

  // When an admin opens the bubble for a word that has no definition of its own,
  // look up base words (same word minus a leading و/ال) it could link to.
  useEffect(() => {
    const w = bubble?.word;
    const hasOwnDefinition = (w?.senses?.length > 0) || !!w?.conjugation_info;
    if (!isAdmin || !w || hasOwnDefinition) {
      setLinkCandidates([]);
      return;
    }
    let cancelled = false;
    fetchLemmaCandidates(w.id)
      .then((res) => { if (!cancelled) setLinkCandidates(res.candidates || []); })
      .catch(() => { if (!cancelled) setLinkCandidates([]); });
    return () => { cancelled = true; };
    // Re-run only when the clicked word changes; word fields are keyed by id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bubble?.word?.id, isAdmin]);

  // Admin: link (lemmaId set) or unlink (lemmaId null) the bubble word's lemma,
  // then refresh so the shared definition + grouped highlighting take effect.
  const handleLinkLemma = useCallback(async (lemmaId) => {
    const w = bubble?.word;
    if (!w) return;
    setLinkBusy(true);
    try {
      const updated = await linkWordLemma(w.id, lemmaId);
      // Reflect the new/cleared definition in the open bubble immediately.
      setBubble((b) => (b && b.word.id === w.id ? { ...b, word: { ...b.word, ...updated } } : b));
      setLinkCandidates([]);
      // Refresh topic.words so scenario matching + highlight see the new lemma link.
      const fresh = await fetchTopicDetail(topic.id);
      if (onTopicUpdate) onTopicUpdate(fresh);
    } catch (e) {
      console.error("Failed to link definition", e);
    } finally {
      setLinkBusy(false);
    }
  }, [bubble, topic?.id, onTopicUpdate]);

  // Sync local turns when topic changes; reset filters to defaults.
  // If the topic has a grammar rule, auto-enable the grammar toggle and disable other filters.
  useEffect(() => {
    setLocalTurns(topic?.scenario_turns || null);
    setEditingTurnId(null);
    // Pills always start OFF; the learner enables the one they need. (Grammar
    // rule topics additionally open the grammar view.)
    setShowGrammar(!!topic?.grammar_rule_code);
    setColorToggles({ new: false, forgotten: false, verb: false, expression: false, pronoun: false, prepositions: false });
  }, [topic]);


  // Broken plurals lookup maps (data-driven, built from topic.words plural relationships).
  // singularByNorm: normalizedPluralForm → singular Word object
  // pluralsByNorm:  normalizedSingularText → array of plural form strings (with harakat)
  const { singularByNorm, pluralsByNorm } = useMemo(() => {
    const sbN = {};
    const pbN = {};
    const topicWords = topic?.words || [];
    topicWords.forEach((w) => {
      if (!w.plurals || w.plurals.length === 0) return;
      const normSingular = normalizeArabic(w.text);
      if (!normSingular) return;
      pbN[normSingular] = w.plurals.map((p) => p.form).filter(Boolean);
      w.plurals.forEach((p) => {
        if (!p.form) return;
        const normPlural = normalizeArabic(p.form);
        if (normPlural) sbN[normPlural] = w;
      });
    });
    return { singularByNorm: sbN, pluralsByNorm: pbN };
  }, [topic?.words]); // eslint-disable-line react-hooks/exhaustive-deps

  // Imperatives lookup map (data-driven, built from verb senses with imperative forms).
  // imperativeByNorm: normalizedImperativeText → verb Word object
  const imperativeByNorm = useMemo(() => {
    const map = {};
    const topicWords = topic?.words || [];
    topicWords.forEach((w) => {
      const senses = w.senses || [];
      senses.forEach((s) => {
        if (s.imperative) {
          const norm = normalizeArabic(s.imperative);
          if (norm) map[norm] = w;
        }
      });
    });
    return map;
  }, [topic?.words]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refs used by the guided journey to scroll-to-section.
  const vocabSectionRef = useRef(null);
  const scenarioSectionRef = useRef(null);

  // ── Synced YouTube transcript player ──────────────────────────────────────
  // When a topic was built from a video and its turns carry caption-derived
  // timing, the source video can play inline and highlight the dialogue in time.
  const [showSyncVideo, setShowSyncVideo] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeTurnId, setActiveTurnId] = useState(null);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const syncPlayerRef = useRef(null);

  // Opened from global search → scroll the chosen line into view and flash it.
  // The turns render only after the topic loads, so poll the ref briefly.
  const [flashTurnId, setFlashTurnId] = useState(null);
  const turnCount = (localTurns || topic?.scenario_turns || []).length;
  useEffect(() => {
    if (!focusTurn?.turnId || !topic || turnCount === 0) return undefined;
    let tries = 0;
    let flashTimer = null;
    const tick = setInterval(() => {
      const el = turnRefsRef.current[focusTurn.turnId];
      tries += 1;
      if (el) {
        clearInterval(tick);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlashTurnId(focusTurn.turnId);
        flashTimer = setTimeout(() => setFlashTurnId(null), 2500);
      } else if (tries > 20) {
        clearInterval(tick);
      }
    }, 100);
    return () => { clearInterval(tick); clearTimeout(flashTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTurn?.turnId, focusTurn?.nonce, topic?.id, turnCount]);
  const lastSyncedTurnRef = useRef(null);
  // True once we've auto-paused at the end of the transcribed segment, so we
  // pause once rather than every poll tick. Reset when playback rewinds.
  const pausedAtEndRef = useRef(false);
  // Latest "scenario reached its end" handler (counts a listening play for
  // video topics). Held in a ref so handleVideoTime needn't depend on it.
  const onScenarioEndRef = useRef(null);
  // Desktop = the lg breakpoint where App.js shows the Word Detail sidebar. On
  // desktop we portal the video into that sidebar; below it the video is inline.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  // The Word Detail sidebar node the player portals into (resolved after mount).
  const [videoSlotEl, setVideoSlotEl] = useState(null);
  // Admins see the learner Vocabulary view by default; this toggles to the full
  // word-manager list (add word, definitions, etc.).

  // Turns carrying caption-derived playback timing, ascending by start. Used to
  // drive line/word highlighting as the embedded video plays. Computed from the
  // same source as displayTurns but kept here so the hooks below run before any
  // of this component's early returns.
  const timedTurns = useMemo(() => {
    const turns = localTurns || topic?.scenario_turns || [];
    return turns
      .filter((t) => t.video_start_ms != null)
      .sort((a, b) => a.video_start_ms - b.video_start_ms);
  }, [localTurns, topic]);

  // Player time → active line + active word (no-op state writes are skipped so
  // we don't re-render every poll tick when nothing changed).
  const handleVideoTime = useCallback(
    (ms) => {
      if (!timedTurns.length) return;
      const FUZZ = 120; // ms of lead-in so highlight isn't perpetually late
      let active = null;
      for (const t of timedTurns) {
        if (t.video_start_ms <= ms + FUZZ) active = t;
        else break;
      }
      if (!active) {
        setActiveTurnId((prev) => (prev === null ? prev : null));
        setActiveWordIndex((prev) => (prev === -1 ? prev : -1));
        return;
      }
      let wi = -1;
      const wt = active.word_timings;
      if (Array.isArray(wt)) {
        for (let i = 0; i < wt.length; i++) {
          if (wt[i].start_ms <= ms + FUZZ) wi = i;
          else break;
        }
      }
      setActiveTurnId((prev) => (prev === active.id ? prev : active.id));
      setActiveWordIndex((prev) => (prev === wi ? prev : wi));
      if (lastSyncedTurnRef.current !== active.id) {
        lastSyncedTurnRef.current = active.id;
        const el = turnRefsRef.current[active.id];
        const container = scenarioSectionRef.current;
        // Scroll the transcript *within its own box* so the video above stays
        // put. Only fall back to scrollIntoView (which can move the window) when
        // the box itself isn't scrollable.
        if (el && container && container.scrollHeight > container.clientHeight + 4) {
          const elRect = el.getBoundingClientRect();
          const cRect = container.getBoundingClientRect();
          const delta =
            elRect.top - cRect.top - (container.clientHeight - el.clientHeight) / 2;
          container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
        } else {
          el?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
        }
      }

      // Stop once playback runs past the end of the transcribed scenario so the
      // video doesn't keep playing untranscribed content. Pause once; re-arm if
      // the user rewinds back into the segment.
      const endMs = timedTurns.reduce((m, t) => Math.max(m, t.video_end_ms), 0);
      if (endMs && ms >= endMs) {
        if (!pausedAtEndRef.current) {
          pausedAtEndRef.current = true;
          syncPlayerRef.current?.pause();
          onScenarioEndRef.current?.(); // count one listening play for video topics
        }
      } else {
        pausedAtEndRef.current = false;
      }
    },
    [timedTurns, turnRefsRef]
  );

  // Clear the highlight when the player is hidden.
  useEffect(() => {
    if (!showSyncVideo) {
      setActiveTurnId(null);
      setActiveWordIndex(-1);
      setIsVideoPlaying(false);
      lastSyncedTurnRef.current = null;
      pausedAtEndRef.current = false;
    }
  }, [showSyncVideo]);

  // Which step's content is shown inside the guided-journey wizard. Defaults to
  // the backend's current step and auto-advances when a step is completed; the
  // user can also tap any unlocked step to review it (see handleStepperSelect).
  const STEP_KEYS = ["reading", "vocabulary", "listening", "roleplay", "quiz"];
  const [viewStep, setViewStep] = useState("reading");
  // AI quiz (docs/AI_TOPIC_QUIZ.md): the config endpoint says whether this
  // learner gets the AI quiz (flag on, or staff) and what the bank holds.
  // Fetched whenever the Quiz step is in view (hooks live above the early
  // returns below; the panel itself renders further down).
  const [aiQuizConfig, setAiQuizConfig] = useState(null);
  const [aiQuizRegen, setAiQuizRegen] = useState(null); // null | "busy" | "done" | "error"
  const [aiQuizBankOpen, setAiQuizBankOpen] = useState(false); // staff: QuizBankManager modal
  const aiQuizTopicId = topic?.id;
  const aiQuizStepDone = topic?.progression?.quiz_completed;
  const aiQuizPanelInView = !!topic?.progression && viewStep === "quiz";
  useEffect(() => {
    if (!aiQuizPanelInView || !aiQuizTopicId) return;
    let cancelled = false;
    fetchAiQuizConfig(aiQuizTopicId)
      .then((cfg) => { if (!cancelled) setAiQuizConfig(cfg); })
      .catch(() => { if (!cancelled) setAiQuizConfig(null); });
    return () => { cancelled = true; };
  }, [aiQuizPanelInView, aiQuizTopicId, aiQuizStepDone]);
  useEffect(() => {
    const cs = topic?.progression?.current_step;
    setViewStep(STEP_KEYS.includes(cs) ? cs : "reading");
  }, [topic?.progression?.current_step, topic?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // For video topics, the synced player is the listening activity. Auto-open it
  // on the Listening step and tear it down (hide) when leaving so it stops.
  useEffect(() => {
    if (!topic?.youtube_video_id) return;
    setShowSyncVideo(viewStep === "listening");
  }, [topic?.youtube_video_id, viewStep]);

  // Resolve the Word Detail sidebar slot when we want the player in it (desktop
  // Listening step of a video topic); clear it otherwise so the player goes
  // back inline.
  useEffect(() => {
    if (viewStep === "listening" && isDesktop && topic?.youtube_video_id) {
      setVideoSlotEl(document.getElementById("topic-listening-video-slot"));
    } else {
      setVideoSlotEl(null);
    }
  }, [viewStep, isDesktop, topic?.youtube_video_id]);

  // Map each vocab word → its backing ReviewItem id so the Step 1 learner quiz
  // can quiz an arbitrary selection of words. Resolved by word id (not the
  // item's owner-topic), so words shared across topics stay quizzable here.
  const [vocabReviewMap, setVocabReviewMap] = useState({});
  useEffect(() => {
    if (!topic?.id) {
      setVocabReviewMap({});
      return;
    }
    let cancelled = false;
    fetchTopicWordReviewItems(topic.id)
      .then((data) => {
        if (cancelled) return;
        const raw = data?.map || {};
        // Normalise string word-id keys to numbers to match word.id.
        const map = {};
        for (const [wid, rid] of Object.entries(raw)) {
          map[Number(wid)] = rid;
        }
        setVocabReviewMap(map);
      })
      .catch(() => {
        if (!cancelled) setVocabReviewMap({});
      });
    return () => {
      cancelled = true;
    };
  }, [topic?.id]);

  // Verb-form drill questions (same source as the Quiz step) so the Step 1
  // learner quiz can test a verb on all its forms. Keyed by verb word id.
  const [verbFormsMap, setVerbFormsMap] = useState({});
  useEffect(() => {
    if (!topic?.id) {
      setVerbFormsMap({});
      return;
    }
    let cancelled = false;
    fetchVerbFormQuestions(topic.id)
      .then((data) => {
        if (cancelled) return;
        const map = {};
        for (const q of data?.questions || []) {
          (map[q.verb_id] = map[q.verb_id] || []).push(q);
        }
        setVerbFormsMap(map);
      })
      .catch(() => {
        if (!cancelled) setVerbFormsMap({});
      });
    return () => {
      cancelled = true;
    };
  }, [topic?.id]);

  if (loading) return (
    <div className="flex justify-center py-16" role="status" aria-label="Loading topic">
      <span className="w-14 h-14 rounded-full animate-spin border-4" style={{ borderColor: "var(--gold-dim)", borderTopColor: "var(--gold)" }} />
    </div>
  );
  if (!topic) return <p>Select a topic to see details.</p>;

  // Topics built from a YouTube video use the inline synced player as their
  // listening activity (instead of TTS playback of the scenario).
  const isYouTubeTopic = !!topic.youtube_video_id;
  // On the Listening step of a video topic, lay the player + transcript out so
  // both stay visible: side-by-side on desktop, video pinned on top on mobile.
  const listeningVideoLayout = viewStep === "listening" && isYouTubeTopic;
  // Desktop: the player lives in the Word Detail sidebar (via portal) so the
  // transcript gets the full main column. Below lg it renders inline instead.
  const videoInSidebar = listeningVideoLayout && !!videoSlotEl;

  const knownIds = new Set(topic.known_word_ids || []);
  const forgottenIds = new Set(topic.forgotten_word_ids || []);
  const newIds = new Set(topic.new_word_ids || []);
  const words = topic.words || [];

  // Exclude conjugated verb forms (has conjugation_info) even if word_type is still "vocab" on this topic.
  // Also hide entries marked show_in_list=false (used by video-transcription dedupe to keep duplicates clickable
  // in the scenario without cluttering the lists).
  const isInList = (w) => w.show_in_list !== false;
  const vocabWords = words.filter((w) => (w.word_type === "vocab" || !w.word_type) && !w.conjugation_info && isInList(w));
  const allVerbWords = words.filter((w) => w.word_type === "verb" && isInList(w));
  // Build a set of root verb IDs already in the list so conjugated forms can be filtered out.
  const rootVerbIds = new Set(allVerbWords.map((w) => w.id));
  // Exclude verbs whose conjugation_info points to a root verb already in this topic's verb list.
  const verbWords = allVerbWords.filter(
    (w) => !w.conjugation_info || !rootVerbIds.has(w.conjugation_info.word_id)
  );
  const expressionWords = words.filter((w) => w.word_type === "expression" && isInList(w));

  const expressionIds = new Set(expressionWords.map((w) => w.id));
  // Also treat conjugated forms (linked via VerbConjugation table) as verbs for highlighting,
  // but exclude nominal derivations: masdar (noun-from-verb) and participles.
  const verbIds = new Set([
    ...verbWords.map((w) => w.id),
    ...words.filter((w) => {
      if (!w.conjugation_info) return false;
      const tags = new Set(w.conjugation_info.tags || []);
      if (tags.has("noun-from-verb") || tags.has("participle")) return false;
      return true;
    }).map((w) => w.id),
  ]);

  const findWordForToken = (token) => {
    const normToken = normalizeArabic(token);
    if (!normToken) return null;
    return words.find((w) => normalizeArabic(w.text) === normToken) || null;
  };

  const displayTurns = localTurns || topic.scenario_turns || [];
  const hasSyncedVideo = !!topic.youtube_video_id && timedTurns.length > 0;
  const progression = topic.progression || null;
  const currentStep = progression?.current_step || null;
  // Quiz unlock: use the step payload's lock flag (so book topics, which skip
  // Role Play, unlock the quiz after Listening), falling back to roleplay.
  const quizStepLocked = progression
    ? (progression.steps?.find((s) => s.key === "quiz")?.locked ?? !progression.roleplay_completed)
    : false;
  // What Listening unlocks: Role Play normally, or the Quiz directly for
  // book / YouTube topics (whose journey has no Role Play step).
  const stepAfterListening = progression?.steps
    ? (progression.steps.some((s) => s.key === "roleplay") ? "Role Play" : "Quiz")
    : "Role Play";
  const vocabularyDone = !!progression?.vocabulary_completed;
  const readingDone = !!progression?.reading_completed;
  const listeningDone = !!progression?.listening_completed;
  const rolePlayLocked = progression ? !listeningDone : false;
  const listeningLocked = progression ? !readingDone : false;

  const refreshProgression = () => {
    if (onTurnSaved) onTurnSaved();
  };

  const handleVocabularyComplete = async () => {
    if (!topic?.id || vocabularyDone) return;
    try {
      const res = await markVocabularyComplete(topic.id);
      if (onTopicUpdate && res?.progression) {
        onTopicUpdate({ ...topic, progression: res.progression });
      }
      refreshProgression();
    } catch (e) {
      alert(e.message || "Could not complete vocabulary.");
    }
  };

  const handleReadingComplete = async () => {
    if (!topic?.id || readingDone) return;
    try {
      const res = await markReadingComplete(topic.id);
      if (onTopicUpdate && res?.progression) {
        onTopicUpdate({ ...topic, progression: res.progression });
      }
      refreshProgression();
    } catch (e) {
      alert(e.message || "Could not complete reading.");
    }
  };

  // Fired by useTTS *only* on a fully-completed scenario playback (no user-stop).
  // The audio element's `ended` events chain through `playAll`; this callback runs
  // once after the final turn finishes, so it counts as exactly one "play".
  //
  // The backend caps `listening_play_count` at 3 via an atomic UPDATE with a
  // `listening_play_count__lt=3` filter, so concurrent or rapid plays are safe
  // — no in-flight guard is needed here (which would otherwise drop a legitimate
  // second play that completed while the first request was still in flight).
  const handleListeningPlay = async () => {
    if (!topic?.id || listeningDone) return;
    try {
      const res = await incrementListeningPlay(topic.id);
      if (onTopicUpdate && res?.progression) {
        onTopicUpdate({ ...topic, progression: res.progression });
      }
      refreshProgression();
    } catch (e) {
      // Silent: progression sync failures must not interrupt UX.
    }
  };

  // A full video playthrough (reaching the end of the transcript) counts as one
  // listening play for video topics — mirrors a completed TTS play-through.
  onScenarioEndRef.current = () => {
    if (isYouTubeTopic && !listeningLocked) handleListeningPlay();
  };

  const handlePlayScenario = () => {
    if (listeningLocked) {
      alert("Finish reading the scenario before listening.");
      return;
    }
    // The increment happens via the onComplete callback below, AFTER the audio
    // sequence has finished playing — not on button click.
    // On the Listening step the transcript follows along inside its own
    // scrollable container, so suppress the page-level auto-scroll. Elsewhere
    // (Reading) keep the existing behaviour.
    const autoScroll = viewStep !== "listening";
    playAll(displayTurns, { onComplete: handleListeningPlay, autoScroll });
  };

  // Scroll helpers for the stepper's onSelectStep prop.
  // Tapping a step in the stepper swaps the content shown inside the wizard.
  // The actual role-play / quiz activities are launched from their step panels.
  const handleStepperSelect = (key) => {
    setViewStep(key);
  };

  const handleTurnSaved = (updatedTurn) => {
    const newTurns = (localTurns || []).map((t) =>
      t.id === updatedTurn.id ? updatedTurn : t
    );
    setLocalTurns(newTurns);
    setEditingTurnId(null);
    if (onTopicUpdate) {
      onTopicUpdate({ ...topic, scenario_turns: newTurns });
    }
    // Re-fetch full topic so newly linked words become clickable immediately
    if (onTurnSaved) {
      onTurnSaved();
    }
  };

  // Generate word-structure (morphology) for one scenario line's words (AI).
  // Single request per line: the backend runs the per-word AI calls
  // concurrently and saves each result as it completes, and nginx allows
  // these admin endpoints a long timeout.
  const handleGenerateTurnMorphology = async (turn) => {
    if (!turn?.id || morphTurnId) return;
    // Without force the backend only fills in missing tokens; existing (possibly
    // stale) structures would be silently kept. Let the admin choose.
    const existingCount = Object.keys(turn.word_morphology || {}).length;
    const force =
      existingCount > 0 &&
      window.confirm(
        `${existingCount} word(s) on this line already have structures.\n\n` +
          "OK — regenerate ALL words from this sentence (overwrites existing)\n" +
          "Cancel — only fill in missing words"
      );
    setMorphTurnId(turn.id);
    try {
      const r = await generateTurnMorphology(turn.id, { model: refineModel || "", force });
      const fresh = await fetchTopicDetail(topic.id);
      if (onTopicUpdate) onTopicUpdate(fresh);
      if (r.failed) {
        alert(
          `Word structures for this line: ${r.generated} generated, ${r.failed} failed.` +
            (r.failures?.[0]?.error ? `\nFirst error: ${r.failures[0].error}` : "")
        );
      }
    } catch (e) {
      alert(`Word structures failed: ${e?.message || e}`);
    } finally {
      setMorphTurnId(null);
    }
  };

  // Regenerate (or first-time generate) the §9 structure for ONE word in a
  // line, in that sentence's context (#92), then re-apply it: merge the fresh
  // per-token map into local state and open the structure modal on it.
  const handleRegenerateStructure = async (turnId, tokenIdx) => {
    if (turnId === undefined || tokenIdx === undefined) return;
    const key = `${turnId}:${tokenIdx}`;
    if (regenStructureKey) return;
    setRegenStructureKey(key);
    try {
      const r = await generateTurnMorphology(turnId, {
        model: refineModel || "",
        tokenIndex: tokenIdx,
        force: true,
      });
      setLocalTurns((ts) =>
        (ts || []).map((t) =>
          t.id === turnId ? { ...t, word_morphology: r.word_morphology } : t
        )
      );
      const entry = (r.word_morphology || {})[String(tokenIdx)];
      if (r.failed || !entry) {
        alert(`Structure generation failed${r.failures?.[0]?.error ? `: ${r.failures[0].error}` : "."}`);
      } else {
        // Close the bubble first so it doesn't show through the modal.
        setBubble(null);
        setStructureWord(entry);
      }
    } catch (e) {
      alert(`Structure generation failed: ${e?.message || e}`);
    } finally {
      setRegenStructureKey(null);
    }
  };

  const handleRefineTurn = async (turn) => {
    if (!turn?.id || !turn?.arabic) return;
    if (refiningTurnId) return;
    setRefiningTurnId(turn.id);
    try {
      const res = await refineVideoTranscript({ transcript: turn.arabic, model: refineModel || "" });
      const sentences = Array.isArray(res?.sentences) ? res.sentences : [];
      // The refine endpoint is built for full transcripts and may split a
      // single line into multiple sentences. Join them all back together so
      // we never clip the line.
      const joinedArabic = sentences
        .map((s) => (s?.arabic || "").trim())
        .filter(Boolean)
        .join(" ");
      const joinedTrans = sentences
        .map((s) => (s?.transliteration || "").trim())
        .filter(Boolean)
        .join(" ");
      const joinedTranslation = sentences
        .map((s) => (s?.translation || "").trim())
        .filter(Boolean)
        .join(" ");
      const arabic =
        (res?.refined_transcript && res.refined_transcript.trim()) ||
        joinedArabic ||
        "";
      if (!arabic) {
        alert("Refine returned no Arabic text.");
        return;
      }
      const payload = { arabic };
      if (joinedTrans) payload.transliteration = joinedTrans;
      if (joinedTranslation) payload.translation = joinedTranslation;
      const updated = await patchScenarioTurn(turn.id, payload);
      handleTurnSaved(updated);
    } catch (e) {
      alert(`Refine failed: ${e?.message || e}`);
    } finally {
      setRefiningTurnId(null);
    }
  };

  const handleTurnDeleted = (deletedId) => {
    const newTurns = (localTurns || []).filter((t) => t.id !== deletedId);
    setLocalTurns(newTurns);
    setEditingTurnId(null);
    if (onTopicUpdate) {
      onTopicUpdate({ ...topic, scenario_turns: newTurns });
    }
    if (onTurnSaved) {
      onTurnSaved();
    }
  };

  const handleTurnCreated = (newTurn) => {
    const base = localTurns || topic.scenario_turns || [];
    const newTurns = [...base, newTurn];
    setLocalTurns(newTurns);
    setShowAddTurnForm(false);
    setAddTurnSpeaker("");
    setAddTurnArabic("");
    setAddTurnTranslit("");
    setAddTurnTranslation("");
    setAddTurnError("");
    if (onTopicUpdate) {
      onTopicUpdate({ ...topic, scenario_turns: newTurns });
    }
    if (onTurnSaved) {
      onTurnSaved();
    }
  };

  // Admin: set grammar role (mubtada | khabar | null) for a specific token
  // in a specific turn.  Persisted via PATCH and mirrored in localTurns.
  //
  // When role === null (clear), we check whether the heuristic would assign
  // a role to this token.  If it would, we store the sentinel value "none"
  // so the manual path suppresses the heuristic for this token.
  // If the heuristic would assign nothing either, we simply remove the key.
  const handleGrammarRoleSet = async (role) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;
    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (role === "auto") {
      // Reset: unconditionally remove the key so the heuristic takes over again.
      delete next[String(tokenIdx)];
    } else if (role === null) {
      const heuristicRole = getHeuristicRoleForToken(turn, tokenIdx);
      if (heuristicRole) {
        // Heuristic would show a role — store "none" to explicitly suppress it.
        next[String(tokenIdx)] = "none";
      } else {
        delete next[String(tokenIdx)];
      }
    } else {
      next[String(tokenIdx)] = role;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    // Optimistic update
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save grammar annotation", err);
      // Rollback
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override pronoun detection for a specific token.
  // value: "pronoun:none" (suppress), "pronoun:<suffix>" (force suffix), or "auto" (reset).
  // applyGlobally: if true, also persist to the server-side global override table.
  const handlePronounAnnotationSet = async (value, applyGlobally = false) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    // Derive the normalized token text for global operations
    const tok = (turn.arabic || "").split(/\s+/)[tokenIdx] || "";
    const normTok = normalizeArabic(tok);

    // ── Per-turn annotation ──────────────────────────────────────────────
    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save pronoun annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
      return; // don't attempt global save if per-turn failed
    }

    // ── Global annotation (optional) ────────────────────────────────────
    if (applyGlobally && normTok) {
      try {
        if (value === "auto") {
          await deleteGlobalPronounAnnotation(normTok);
          setGlobalPronounAnnotations((prev) => {
            const next = { ...prev };
            delete next[normTok];
            return next;
          });
        } else {
          // Store annotation without the "pronoun:" prefix in the global table
          const annotation = value.startsWith("pronoun:") ? value.slice("pronoun:".length) : value;
          await setGlobalPronounAnnotation(normTok, annotation);
          setGlobalPronounAnnotations((prev) => ({ ...prev, [normTok]: annotation }));
        }
      } catch (err) {
        console.error("Failed to save global pronoun annotation", err);
      }
    }
  };

  // Admin: manually override preposition highlighting for a specific token.
  // value: "prep:harf_jar" (force indigo), "prep:baad_harf_jar" (force orange),
  //        "prep:none" (suppress), or "auto" (reset to heuristic).
  const handlePrepAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save prep annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override past-tense verb highlighting for a specific token.
  // value: "pt:fiil" (force verb/rose), "pt:none" (suppress), or "auto" (reset).
  const handlePtAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save past-tense annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override present-tense-intro highlighting for a specific token.
  // value: "pti:fiil" (force green), "pti:none" (suppress), or "auto" (reset).
  const handlePtiAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save present-tense-intro annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override gender highlighting for a specific token.
  // value: "gend:masculine", "gend:feminine", "gend:none" (suppress), or "auto" (reset).
  const handleGenderAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save gender annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override definite article highlighting for a specific token.
  // value: "def:definite" (force purple), "def:none" (suppress), or "auto" (reset).
  const handleDefAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save definite annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override adjective rule highlighting for a specific token.
  // value: "adj:noun" (force blue), "adj:adjective" (force emerald), "adj:none" (suppress), or "auto" (reset).
  const handleAdjAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save adjective annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override Idafa role for a token.
  // value: "idafa:mudaf" | "idafa:mudaf_ilaih" | "idafa:none" | "idafa:dual_no_suffix" | "auto" (reset).
  const handleIdafaAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save Idafa annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override negation highlight for a token.
  // value: "neg:particle" (force red نفي), "neg:majzoom" (force orange مجزوم),
  //        "neg:mansuub" (force orange منصوب), "neg:none" (suppress), or "auto" (reset).
  const handleNegationAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save negation annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override question-word highlight for a token.
  // value: "qw:question_word" (force yellow أداة), "qw:none" (suppress), or "auto" (reset).
  const handleQuestionWordsAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save question word annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override connector highlight for a token.
  // value: "con:wa" | "con:thumma" | "con:lakin" | "con:lianna" | "con:none" (suppress), or "auto" (reset).
  const handleConnectorAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save connector annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override verb-object role for a token.
  // value: "vo:verb" | "vo:object" | "vo:none" (suppress), or "auto" (reset).
  const handleVerbObjectAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save verb-object annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override future tense role for a token.
  // value: "ft:sawfa" | "ft:sa_prefix" | "ft:verb" | "ft:none" (suppress), or "auto" (reset).
  const handleFutureTenseAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save future tense annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override object-pronoun highlight for a token.
  // value: "op:fiil" (force green verb) | "op:mafool" (force split green+teal) |
  //        "op:none" (suppress) | "auto" (reset to heuristic).
  const handleObjectPronounsBasicAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save object-pronoun annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override comparatives highlight for a token.
  // value: "comp:comparative" (force pink comparative) | "comp:min" (force indigo مِنْ) |
  //        "comp:none" (suppress) | "auto" (reset to heuristic).
  const handleComparativesAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save comparatives annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override dual forms highlight for a token.
  // value: "df:dual" (force orange dual highlight) | "df:none" (suppress) | "auto" (reset to heuristic).
  const handleDualFormsAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save dual forms annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override broken plurals highlight for a token.
  // value: "bp:plural" (force rose plural) | "bp:singular" (force sky singular) |
  //        "bp:none" (suppress) | "auto" (reset to data-driven detection).
  const handleBrokenPluralsAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save broken plurals annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override imperatives highlight for a token.
  // value: "imp:fiil" (force red highlight) | "imp:none" (suppress) | "auto" (reset to data-driven).
  const handleImperativesAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "auto") {
      delete next[String(tokenIdx)];
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save imperatives annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Admin: manually override word-order role for a token or sentence type for a segment.
  // Token role values: "wo:fiil" | "wo:faail" | "wo:mafool" | "wo:none" | "wo:auto" (reset).
  // Sentence type values: "wo:seg:filia" | "wo:seg:ismia" | "wo:seg:auto" (reset).
  // Sentence-type annotations are stored on the segment's first token (bubble.segFirstIdx).
  const handleWoAnnotationSet = async (value) => {
    if (!bubble?.turnId || bubble.tokenIdx === undefined) return;
    const { turnId, tokenIdx } = bubble;
    const segFirstIdx = bubble.segFirstIdx ?? tokenIdx;
    const turn = (localTurns || []).find((t) => t.id === turnId);
    if (!turn) return;

    const prev = turn.word_annotations || {};
    const next = { ...prev };
    if (value === "wo:auto") {
      delete next[String(tokenIdx)];
    } else if (value === "wo:seg:auto") {
      delete next[String(segFirstIdx)];
    } else if (value.startsWith("wo:seg:")) {
      next[String(segFirstIdx)] = value;
    } else {
      next[String(tokenIdx)] = value;
    }
    const payload = Object.keys(next).length > 0 ? next : null;
    setLocalTurns((ts) =>
      (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: payload } : t)
    );
    try {
      await patchScenarioTurn(turnId, { word_annotations: payload });
    } catch (err) {
      console.error("Failed to save word-order annotation", err);
      setLocalTurns((ts) =>
        (ts || []).map((t) => t.id === turnId ? { ...t, word_annotations: prev } : t)
      );
    }
  };

  // Single primary action for the current step, surfaced inside the journey
  // card (consolidates what used to be CTAs scattered across sections).
  // Pure presentation — every onClick reuses an existing handler, so backend
  // behaviour is unchanged.
  const journeyAction = (() => {
    if (!progression || progression.topic_completed) return null;
    // Reviewing a step other than the current one: offer a way back, no CTA.
    if (viewStep !== currentStep) {
      const vs = (progression.steps || []).find((s) => s.key === viewStep);
      return {
        label: "Back to current step →",
        onClick: () => setViewStep(currentStep),
        tone: "primary",
        hint: vs?.completed
          ? "✓ You've completed this step — you're reviewing it."
          : "This step isn't unlocked yet — previewing it.",
      };
    }
    switch (currentStep) {
      case "vocabulary":
        // Both learners and admins use the learner-style Vocabulary view, which
        // auto-completes once every word is learnt — so there's no action-band
        // button (and no "view each section" gating) here.
        return null;
      case "reading":
        return {
          label: "I've finished reading →",
          onClick: handleReadingComplete,
          tone: "primary",
          hint: "Read the scenario above, then continue to unlock Vocabulary.",
        };
      case "listening":
        if (isYouTubeTopic) {
          return {
            label: isVideoPlaying ? "❚❚ Pause Video" : "▶ Play Video",
            onClick: () => {
              const p = syncPlayerRef.current;
              if (!p) return;
              if (isVideoPlaying) {
                p.pause();
                return;
              }
              const first = timedTurns[0];
              if (first) p.seekToMs(first.video_start_ms);
              else p.play();
            },
            tone: "emerald",
            hint:
              (progression.listening_play_count || 0) < 3
                ? `Listening ${Math.min(progression.listening_play_count || 0, 3)}/3 — a play counts once the video reaches the end of the transcript.`
                : `✅ Listening complete — ${stepAfterListening} is unlocked.`,
          };
        }
        return {
          label: "▶ Play Scenario",
          onClick: handlePlayScenario,
          // Also disabled when TTS is blocked (seatless institute admin).
          disabled: !!(playingTurnId || loadingTurnId) || !isAudioPlaybackAllowed(),
          tone: "emerald",
          hint: !isAudioPlaybackAllowed()
            ? "Audio playback requires a learner seat."
            : (progression.listening_play_count || 0) < 3
              ? `Listening ${Math.min(progression.listening_play_count || 0, 3)}/3 — a play counts once the whole scenario finishes.`
              : `✅ Listening complete — ${stepAfterListening} is unlocked.`,
        };
      // roleplay & quiz are launch-only steps — their action lives in the
      // step panel itself (see below), so no action band is shown for them.
      default:
        return null;
    }
  })();

  // Which step's content renders inside the wizard. With no progression we fall
  // back to showing every section (topics that don't use guided progression).
  const noGuide = !progression;
  const showVocabContent = noGuide || viewStep === "vocabulary";
  const showScenarioContent = noGuide || viewStep === "reading" || viewStep === "listening";
  const showRoleplayPanel = !noGuide && viewStep === "roleplay";
  const showQuizPanel = !noGuide && viewStep === "quiz";
  const useAiQuiz = !!(aiQuizConfig?.enabled && onStartAiQuiz);

  // The learner-style Vocabulary view. Shown to normal users, and to admins by
  // default (admins can flip to the full word-manager via adminVocabManage).
  // Owners (and admins) get the inline remove + move-between-lists controls.
  const canEditWords = isAdmin || !!topic.is_owner;
  const vocabularyStepView = (
    <VocabularyStep
      sections={[
        { label: "Words", items: vocabWords },
        { label: "Verbs", items: verbWords },
        { label: "Expressions", items: expressionWords },
      ]}
      knownIds={new Set(topic.vocab_learned_word_ids || [])}
      forgottenIds={new Set(topic.forgotten_word_ids || [])}
      reviewItemByWordId={vocabReviewMap}
      verbFormsByWordId={verbFormsMap}
      selectedWordId={selectedWordId}
      onWordSelect={(id, text, word_type) => {
        onWordSelect && onWordSelect(id, text, word_type);
        setClickedTokenId(null);
        setLinkedWordId(word_type === "verb" ? id : null);
      }}
      onPlayWord={(w) => playWord(w)}
      onBatchComplete={() => onTurnSaved && onTurnSaved()}
      onAllLearnt={handleVocabularyComplete}
      completed={!!topic.progression?.vocabulary_completed}
      stepLabel={`step 1 of 5`}
      canRemove={canEditWords}
      onRemoveWord={async (w) => {
        if (!window.confirm(`Remove "${w.text}" from this list?\n\nIt stays clickable in the dialogue but won't appear in this list or the topic quiz.`)) return;
        try {
          await patchTopicWord(topic.id, w.id, { show_in_list: false });
          if (onTurnSaved) onTurnSaved();
        } catch (e) {
          alert(e.message || "Could not remove the word.");
        }
      }}
      onReclassify={async (w, type) => {
        if (w.word_type === type) return;
        try {
          await patchTopicWord(topic.id, w.id, { word_type: type });
          if (onTurnSaved) onTurnSaved();
        } catch (e) {
          alert(e.message || "Could not move the word.");
        }
      }}
    />
  );

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      {/* Topic header */}
      <div>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <h2 className="text-2xl font-display font-semibold mb-1">{topic.name}</h2>
          {isAdmin && (
            <TopicCategoryPicker topicId={topic.id} categoryId={topic.category} categories={categories} onChanged={onCategoryChanged} />
          )}
          {isAdmin && <ShareTopicButton topicId={topic.id} step={viewStep} />}
          {(isAdmin || topic.is_owner) && (
            <button
              type="button"
              onClick={() => { setConfirmDeleteTopic(true); setDeleteTopicError(null); }}
              className="shrink-0 flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors hover:brightness-110"
              style={{ color: "var(--tag-rose)", borderColor: "var(--tag-rose)", background: "var(--tag-rose-bg)" }}
              title="Delete this topic and all its content"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
              </svg>
              Delete Topic
            </button>
          )}
        </div>
        {topic.description && (
          <p className="text-base font-display text-slate-600">{topic.description}</p>
        )}
        {confirmDeleteTopic && (
          <div className="mt-2 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-900">
            <div className="font-medium mb-1">Delete this topic?</div>
            <div className="text-xs text-red-800 mb-2">
              This will permanently remove the scenario, vocabulary, verbs, expressions, quizzes, and all user progress for <span className="font-semibold">{topic.name}</span>. Words used by other topics will be kept; words exclusive to this topic will be deleted. This cannot be undone.
            </div>
            {deleteTopicError && <div className="text-xs text-red-700 mb-2">{deleteTopicError}</div>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDeleteTopic}
                disabled={deletingTopic}
                className="px-3 py-1 text-xs rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {deletingTopic ? "Deleting…" : "Yes, delete topic"}
              </button>
              <button
                type="button"
                onClick={() => { setConfirmDeleteTopic(false); setDeleteTopicError(null); }}
                disabled={deletingTopic}
                className="px-3 py-1 text-xs rounded-md bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <GuidedTopicJourney
        progression={progression}
        viewStep={viewStep}
        onSelectStep={handleStepperSelect}
        action={journeyAction}
      >

      {/* Listening + Reading scenario region.
          On the LISTENING step: player + scenario sit in a row on desktop
          (md+) and stack on mobile, with the scenario scrolling in its own box.
          On other steps the wrapper is layout-neutral (display:contents) so the
          scenario renders exactly as before. */}
      <div
        className={
          viewStep !== "listening"
            ? "contents"
            : listeningVideoLayout && !videoInSidebar
            ? "flex flex-col md:flex-row md:items-start gap-3 mb-3"
            : "flex flex-col gap-3 mb-3"
        }
      >

      {viewStep === "listening" && (displayTurns.length > 0) && !isYouTubeTopic && (
        <ListeningPanel
          playCount={progression?.listening_play_count || 0}
          required={3}
          isPlaying={!!(playingTurnId || loadingTurnId)}
          locked={listeningLocked || !isAudioPlaybackAllowed()}
          done={listeningDone}
          onPlay={handlePlayScenario}
          onStop={stop}
        />
      )}

      {/* Source-video player with transcript sync. On desktop it portals into
          the Word Detail sidebar (videoInSidebar); below lg it renders inline —
          stacked + sticky on mobile, beside the transcript on tablets. */}
      {(viewStep === "listening" || noGuide) && topic.youtube_video_id && (() => {
        const playerInner = (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setShowSyncVideo((v) => !v)}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors ${
                  showSyncVideo
                    ? "bg-red-600 border-red-600 text-white hover:bg-red-700"
                    : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                }`}
                title="Play the source video and follow the transcript in time"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
                {showSyncVideo ? "Hide video" : "Play with synced transcript"}
              </button>
              {showSyncVideo && !hasSyncedVideo && (
                <span className="text-xs text-slate-400">
                  No synced timing for this video — the transcript won't auto-highlight.
                </span>
              )}
            </div>
            {showSyncVideo && (
              <YouTubeSyncPlayer
                ref={syncPlayerRef}
                videoId={topic.youtube_video_id}
                startSeconds={topic.youtube_start_seconds || 0}
                onTime={handleVideoTime}
                onStateChange={(s) => setIsVideoPlaying(s === 1)}
              />
            )}
          </>
        );

        if (videoInSidebar) {
          return ReactDOM.createPortal(
            <div className="mb-4 pb-4 border-b border-parchment-300">{playerInner}</div>,
            videoSlotEl
          );
        }

        return (
          <div
            className={
              listeningVideoLayout
                ? "sticky top-0 z-20 bg-parchment-50 pb-2 mb-1 md:static md:bg-transparent md:pb-0 md:mb-0 md:w-[44%] md:flex-none md:self-start"
                : "mb-3"
            }
          >
            {playerInner}
          </div>
        );
      })()}

      {/* Reading & Listening — scenario dialogue */}
      {showScenarioContent && (displayTurns.length > 0 || isAdmin) && (
        <section
          ref={scenarioSectionRef}
          style={{ background: "var(--ink)", borderColor: "var(--panel-line)" }}
          className={
            videoInSidebar
              ? "dark-scope p-4 rounded-2xl border overflow-y-auto max-h-[32rem] scroll-elegant"
              : listeningVideoLayout
              ? "dark-scope p-4 rounded-2xl border overflow-y-auto max-h-[50vh] md:max-h-[32rem] md:flex-1 md:min-w-0 scroll-elegant"
              : viewStep === "listening"
              ? "dark-scope p-4 rounded-2xl border overflow-y-auto max-h-[28rem] scroll-elegant"
              : "dark-scope p-4 rounded-2xl border overflow-x-auto"
          }
        >
          <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-sm font-cormorant font-semibold" style={{ color: "var(--parchment)" }}>
                Scenario / Dialogue
              </h3>
              {/* Stop button — shown only while audio is playing (playback is
                  started from the Listening step or per-turn play controls). */}
              {(playingTurnId || loadingTurnId) && (
                <button
                  type="button"
                  onClick={stop}
                  className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border transition-colors"
                  style={{ color: "var(--tag-rose)", background: "var(--tag-rose-bg)", borderColor: "transparent" }}
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="5" y="5" width="14" height="14" rx="2" />
                  </svg>
                  Stop
                </button>
              )}
              {/* YouTube source link — shown for any topic created from a video transcription */}
              {topic.youtube_url && (
                <a
                  href={topic.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border transition-colors"
                  style={{ color: "var(--tag-rose)", background: "var(--tag-rose-bg)", borderColor: "transparent" }}
                  title="Open the source YouTube video at the scenario start"
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                  Watch on YouTube
                </a>
              )}
              {/* Show Grammar toggle — only for topics with a grammar focus */}
              {topic.grammar_rule_code && (
                <button
                  type="button"
                  onClick={() => {
                    const next = !showGrammar;
                    setShowGrammar(next);
                    // Grammar mode owns the colouring; pills stay off either way
                    // (they default off and the learner enables what they need).
                    if (next) {
                      setColorToggles({ new: false, forgotten: false, verb: false, noun: false, expression: false, pronoun: false, prepositions: false });
                    }
                  }}
                  title={showGrammar ? "Hide grammar info" : "Show grammar info and highlight nominal sentences"}
                  className={showGrammar
                    ? "flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border transition-colors"
                    : "dt-ghost-btn flex items-center gap-1 px-2 py-0.5 text-xs rounded-md"}
                  style={showGrammar ? { color: "var(--gold-bright)", background: "var(--gold-bg)", borderColor: "rgba(201,162,39,0.3)" } : undefined}
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  {showGrammar ? "Hide Grammar" : "Show Grammar"}
                </button>
              )}
              {/* Word type filter badges — morphology-driven word coloring.
                  Pill "on" colors mirror the tokc-* token colors in theme.css.
                  New / Forgotten / Expression pills are hidden for now; the
                  colorToggles keys stay so they can be re-enabled later. */}
              {[
                { key: "verb",         label: "Verb",         on: { color: "var(--tag-violet)", background: "var(--tag-violet-bg)", borderColor: "transparent" } },
                { key: "noun",         label: "Noun",         on: { color: "var(--tag-rose)",   background: "var(--tag-rose-bg)",   borderColor: "transparent" } },
                { key: "pronoun",      label: "Pronoun",      on: { color: "var(--tag-teal)",   background: "var(--tag-teal-bg)",   borderColor: "transparent" } },
                { key: "prepositions", label: "Prepositions", on: { color: "var(--tag-orange)", background: "var(--tag-orange-bg)", borderColor: "transparent" } },
              ].map(({ key, label, on }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => !showGrammar && toggleColor(key)}
                  disabled={showGrammar}
                  title={showGrammar ? "Disabled while grammar mode is on" : (colorToggles[key] ? `Hide ${label}` : `Show ${label}`)}
                  className={`text-[0.74rem] px-[11px] py-1 rounded-2xl border transition-colors ${showGrammar ? "cursor-not-allowed" : ""}`}
                  style={showGrammar
                    ? { color: "var(--parchment-dim)", background: "transparent", borderColor: "var(--panel-line)", opacity: 0.5 }
                    : colorToggles[key]
                      ? on
                      : { color: "var(--parchment-dim)", background: "transparent", borderColor: "var(--panel-line)" }}
                >
                  {label}
                </button>
              ))}
              {/* Word structures (morphology) are generated per scenario line via
                  the ◇ button on each line — the whole-topic batch timed out on
                  long scenarios. */}
              {/* Add Dialogue — admin only */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTurnForm((v) => !v);
                    setAddTurnError("");
                  }}
                  className={showAddTurnForm
                    ? "ml-auto text-xs px-2 py-0.5 rounded-md border transition-colors"
                    : "dt-ghost-btn ml-auto text-xs px-2 py-0.5 rounded-md"}
                  style={showAddTurnForm ? { color: "var(--parchment-dim)", background: "var(--forest-soft)", borderColor: "var(--panel-line)" } : undefined}
                >
                  {showAddTurnForm ? "✕ Cancel" : "+ Add Dialogue"}
                </button>
              )}
          </div>

          {/* Grammar info panel — shown when Show Grammar is toggled on */}
          {showGrammar && topic.grammar_rule_code && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <div className="font-semibold text-amber-800 mb-1">
                Grammar Focus:&nbsp;
                <span dir="rtl" lang="ar" className="font-arabic">{topic.grammar_rule_arabic}</span>
                {" — "}{topic.grammar_rule_name}
              </div>
              {topic.grammar_explanation && (
                <div className="text-amber-700">{topic.grammar_explanation}</div>
              )}
              {topic.grammar_rule_code === "prepositions" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    Prepositions are highlighted in{" "}
                    <span className="font-semibold text-indigo-700">indigo</span>.
                    The word after each preposition is highlighted in{" "}
                    <span className="font-semibold text-orange-600">orange</span>.
                    Look for the pattern — the word after في changes slightly.
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-amber-700 mb-1">
                      Core prepositions (حُرُوف الجَر):
                    </div>
                    <div className="flex flex-wrap gap-2" dir="rtl">
                      {PREPOSITIONS_DISPLAY.map(({ ar, en }) => (
                        <span key={ar} className="inline-flex items-center gap-1">
                          <span className="bg-indigo-100 text-indigo-800 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-arabic">{ar}</span>
                          <span className="text-xs text-amber-600">({en})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "personal_pronouns" ? (
                <div className="mt-2 space-y-2">
                  <div>
                    <div className="text-xs font-semibold text-amber-700 mb-1">
                      Independent pronouns (ضمائر منفصلة) — highlighted in&nbsp;
                      <span className="font-semibold text-purple-700">purple</span>:
                    </div>
                    <div className="flex flex-wrap gap-1" dir="rtl">
                      {INDEPENDENT_PRONOUNS_DISPLAY.map((p) => (
                        <span key={p} className="bg-purple-100 text-purple-800 border border-purple-300 rounded px-1.5 py-0.5 text-sm font-arabic">{p}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-amber-700 mb-1">
                      Attached pronouns (ضمائر متصلة) — suffix highlighted in&nbsp;
                      <span className="font-semibold text-teal-700">teal</span>:
                    </div>
                    <div className="flex flex-wrap gap-1" dir="rtl">
                      {ATTACHED_PRONOUNS_DISPLAY.map((p) => (
                        <span key={p} className="bg-teal-100 text-teal-800 border border-teal-300 rounded px-1.5 py-0.5 text-sm font-arabic">{p}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "definite_indefinite" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    Definite nouns (with <span className="font-semibold text-purple-700 font-arabic">الـ</span>) are highlighted in{" "}
                    <span className="font-semibold text-purple-700">purple</span>.
                    Indefinite nouns (without الـ) are highlighted in{" "}
                    <span className="font-semibold text-sky-600">blue</span> — meaning "a" instead of "the".
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-purple-100 text-purple-900 border border-purple-300 rounded px-1.5 py-0.5 text-sm font-arabic">الْبَيْت</span>
                      <span className="text-xs text-amber-600">(the house)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">بَيْت</span>
                      <span className="text-xs text-amber-600">(a house)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-purple-100 text-purple-900 border border-purple-300 rounded px-1.5 py-0.5 text-sm font-arabic">الْمَدْرَسَة</span>
                      <span className="text-xs text-amber-600">(the school)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">مَدْرَسَة</span>
                      <span className="text-xs text-amber-600">(a school)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "word_order" ? (
                <div className="mt-2 space-y-3">
                  {/* Sentence types */}
                  <div className="text-xs text-amber-700">
                    Arabic sentences are either{" "}
                    <span className="font-semibold text-rose-700">verbal (جملة فعلية)</span> — starting with a verb — or{" "}
                    <span className="font-semibold text-blue-700">nominal (جملة اسمية)</span> — starting with a noun or pronoun.
                  </div>
                  {/* Verbal sentence roles */}
                  <div>
                    <div className="text-xs font-semibold text-rose-700 mb-1">جملة فعلية — verbal sentence</div>
                    <div className="flex flex-wrap gap-2 mb-1" dir="rtl">
                      <span className="inline-flex items-center gap-1">
                        <span className="bg-rose-200 text-rose-900 border border-rose-300 rounded px-1.5 py-0.5 text-sm font-arabic">كَتَبَ</span>
                        <span className="text-xs text-amber-600">فعل — verb (ends in fatha ـَ)</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="bg-amber-100 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5 text-sm font-arabic">الطَّالِبُ</span>
                        <span className="text-xs text-amber-600">فاعل — subject (ends in damma ـُ)</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="bg-teal-100 text-teal-900 border border-teal-300 rounded px-1.5 py-0.5 text-sm font-arabic">الدَّرْسَ</span>
                        <span className="text-xs text-amber-600">مفعول به — object (ends in fatha ـَ)</span>
                      </span>
                    </div>
                    <div className="text-xs text-amber-600">
                      Tip: the <span className="font-semibold text-amber-700">فاعل</span> carries a damma (ـُ/ـٌ); the <span className="font-semibold text-teal-700">مفعول به</span> carries a fatha (ـَ/ـً).
                    </div>
                  </div>
                  {/* Nominal sentence */}
                  <div>
                    <div className="text-xs font-semibold text-blue-700 mb-1">جملة اسمية — nominal sentence</div>
                    <div className="flex flex-wrap gap-2" dir="rtl">
                      <span className="inline-flex items-center gap-1">
                        <span className="bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">هُوَ</span>
                        <span className="text-xs text-amber-600">مبتدأ — subject</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="bg-emerald-100 text-emerald-900 border border-emerald-300 rounded px-1.5 py-0.5 text-sm font-arabic">طَالِبٌ</span>
                        <span className="text-xs text-amber-600">خبر — predicate</span>
                      </span>
                    </div>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "past_tense" ? (
                <div className="mt-2 space-y-3">
                  <div className="text-xs text-amber-700">
                    Past tense verbs are highlighted in{" "}
                    <span className="font-semibold text-rose-700">rose</span>.
                    The verb ending (suffix) showing who did the action is in{" "}
                    <span className="font-semibold text-pink-700">pink</span>.
                  </div>
                  {/* Verb conjugation table */}
                  <div>
                    <div className="text-xs font-semibold text-amber-700 mb-3">Past tense endings — using ذَهَبَ (to go):</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3" dir="rtl">
                      {[
                        // ── Masculine ─────────────────────────
                        { stem: "ذَهَبْ",   suffix: "تُ",   en: "أَنَا — I went" },
                        { stem: "ذَهَبْ",   suffix: "تَ",   en: "أَنْتَ — you (m) went" },
                        { stem: "ذَهَبْ",   suffix: "تُمَا", en: "أَنْتُمَا — you two went" },
                        { stem: "ذَهَبْ",   suffix: "تُمْ", en: "أَنْتُمْ — you all (m) went" },
                        { stem: "ذَهَبَ",   suffix: null,   en: "هُوَ — he went" },
                        { stem: "ذَهَبَ",   suffix: "ا",    en: "هُمَا — they two (m) went" },
                        { stem: "ذَهَبُ",   suffix: "وا",   en: "هُمْ — they (m) went" },
                        // ── Feminine ──────────────────────────
                        { stem: "ذَهَبْ",   suffix: "تِ",   en: "أَنْتِ — you (f) went" },
                        { stem: "ذَهَبْ",   suffix: "تُنَّ", en: "أَنْتُنَّ — you all (f) went" },
                        { stem: "ذَهَبَ",   suffix: "تْ",   en: "هِيَ — she went" },
                        { stem: "ذَهَبَتَ", suffix: "ا",    en: "هُمَا — they two (f) went" },
                        { stem: "ذَهَبْ",   suffix: "نَ",   en: "هُنَّ — they (f) went" },
                        // ── Shared ────────────────────────────
                        { stem: "ذَهَبْ",   suffix: "نَا",  en: "نَحْنُ — we went" },
                      ].map(({ stem, suffix, en }) => (
                        <span key={en} className="flex flex-col gap-2 items-start">
                          <span className="whitespace-nowrap text-sm font-arabic">
                            <span className={`bg-rose-100 text-rose-900 ${suffix ? "rounded-r" : "rounded"}`}>{stem}</span>{suffix && <span className="bg-pink-200 text-pink-900 rounded-l">{suffix}</span>}
                          </span>
                          <span className="text-xs text-amber-600">{en}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "present_tense_intro" ? (
                <div className="mt-2 space-y-3">
                  <div className="text-xs text-amber-700">
                    Present tense verbs describe actions happening now. The verb stem is highlighted in{" "}
                    <span className="font-semibold text-rose-700">rose</span>.
                    The prefix (أَ / تَ / يَ / نَ) showing who does the action is in{" "}
                    <span className="font-semibold text-sky-700">sky blue</span>.
                  </div>
                  {/* Verb conjugation table */}
                  <div>
                    <div className="text-xs font-semibold text-amber-700 mb-3">Present tense prefixes — using ذَهَبَ (to go):</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3" dir="rtl">
                      {[
                        // ── Masculine ─────────────────────────
                        { prefix: "أَ", stem: "ذْهَبُ",    en: "أَنَا — I go" },
                        { prefix: "تَ", stem: "ذْهَبُ",    en: "أَنْتَ — you (m) go" },
                        { prefix: "تَ", stem: "ذْهَبَانِ",  en: "أَنْتُمَا — you two go" },
                        { prefix: "تَ", stem: "ذْهَبُونَ",  en: "أَنْتُمْ — you all (m) go" },
                        { prefix: "يَ", stem: "ذْهَبُ",    en: "هُوَ — he goes" },
                        { prefix: "يَ", stem: "ذْهَبَانِ",  en: "هُمَا — they two (m) go" },
                        { prefix: "يَ", stem: "ذْهَبُونَ",  en: "هُمْ — they (m) go" },
                        // ── Feminine ──────────────────────────
                        { prefix: "تَ", stem: "ذْهَبِينَ",  en: "أَنْتِ — you (f) go" },
                        { prefix: "تَ", stem: "ذْهَبْنَ",   en: "أَنْتُنَّ — you all (f) go" },
                        { prefix: "تَ", stem: "ذْهَبُ",    en: "هِيَ — she goes" },
                        { prefix: "تَ", stem: "ذْهَبَانِ",  en: "هُمَا — they two (f) go" },
                        { prefix: "يَ", stem: "ذْهَبْنَ",   en: "هُنَّ — they (f) go" },
                        // ── Shared ────────────────────────────
                        { prefix: "نَ", stem: "ذْهَبُ",    en: "نَحْنُ — we go" },
                      ].map(({ prefix, stem, en }) => (
                        <span key={en} className="flex flex-col gap-2 items-start">
                          <span className="whitespace-nowrap text-sm font-arabic">
                            <span className="bg-sky-200 text-sky-900 rounded-r">{prefix}</span><span className="bg-rose-100 text-rose-900 rounded-l">{stem}</span>
                          </span>
                          <span className="text-xs text-amber-600">{en}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "gender" ? (
                <div className="mt-2 space-y-3">
                  <div className="text-xs text-amber-700">
                    <span className="font-semibold text-sky-700">Masculine</span> words are highlighted in sky blue.{" "}
                    <span className="font-semibold text-fuchsia-700">Feminine</span> words are highlighted in fuchsia.{" "}
                    The feminine marker{" "}
                    <span className="font-semibold text-pink-700 font-arabic">ة</span> is highlighted in{" "}
                    <span className="font-semibold text-pink-700">pink</span>.
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَحْمَد</span>
                      <span className="text-xs text-amber-600">(masculine)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-fuchsia-100 text-fuchsia-900 rounded-r">فَاطِم</span><span className="bg-pink-200 text-pink-900 rounded-l">ة</span>
                      </span>
                      <span className="text-xs text-amber-600">(feminine)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-rose-100 text-rose-900 rounded-r">ذَهَبَ</span><span className="bg-pink-200 text-pink-900 rounded-l">تْ</span>
                      </span>
                      <span className="text-xs text-amber-600">(fem. verb)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "adjectives" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    The describing word comes after the noun.{" "}
                    <span className="font-semibold text-blue-700">Nouns</span> are highlighted in blue,{" "}
                    <span className="font-semibold text-emerald-700">adjectives</span> in emerald.{" "}
                    The feminine marker{" "}
                    <span className="font-semibold text-pink-700 font-arabic">ة</span> is shown in{" "}
                    <span className="font-semibold text-pink-700">pink</span>.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">بَيْتٌ</span>
                      <span className="bg-emerald-100 text-emerald-900 border border-emerald-300 rounded px-1.5 py-0.5 text-sm font-arabic">كَبِيرٌ</span>
                      <span className="text-xs text-amber-600">(a big house)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">بِنْتٌ</span>
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-emerald-100 text-emerald-900 rounded-r">جَمِيل</span><span className="bg-pink-200 text-pink-900 rounded-l">ةٌ</span>
                      </span>
                      <span className="text-xs text-amber-600">(a beautiful girl)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "adjective_agreement" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    Adjectives must match the noun in <span className="font-semibold">gender</span>,{" "}
                    <span className="font-semibold">number</span>, and{" "}
                    <span className="font-semibold">definiteness</span>.{" "}
                    <span className="font-semibold text-blue-700">Nouns</span> are highlighted in blue,{" "}
                    <span className="font-semibold text-emerald-700">adjectives</span> in emerald.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">الْكِتَابُ</span>
                      <span className="bg-emerald-100 text-emerald-900 border border-emerald-300 rounded px-1.5 py-0.5 text-sm font-arabic">الْجَدِيدُ</span>
                      <span className="text-xs text-amber-600">(the new book)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">السَّيَّارَةُ</span>
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-emerald-100 text-emerald-900 rounded-r">الْجَدِيد</span><span className="bg-pink-200 text-pink-900 rounded-l">َةُ</span>
                      </span>
                      <span className="text-xs text-amber-600">(the new car)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">الْكُتُبُ</span>
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-emerald-100 text-emerald-900 rounded-r">الْجَدِيد</span><span className="bg-pink-200 text-pink-900 rounded-l">َةُ</span>
                      </span>
                      <span className="text-xs text-amber-600">(the new books)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "idafa" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    The first noun belongs to the second noun.{" "}
                    <span className="font-semibold text-blue-700">مضاف</span> (possessed) is highlighted in blue,{" "}
                    <span className="font-semibold text-amber-700">مضاف إليه</span> (owner) in amber.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-blue-50 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">كِتَابُ</span>
                      <span className="bg-amber-50 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5 text-sm font-arabic">الطَّالِبِ</span>
                      <span className="text-xs text-amber-600">(the student's book)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-blue-50 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">بَيْتُ</span>
                      <span className="bg-amber-50 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5 text-sm font-arabic">الرَّجُلِ</span>
                      <span className="text-xs text-amber-600">(the man's house)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-blue-50 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">اِسْمُ</span>
                      <span className="bg-amber-50 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5 text-sm font-arabic">الصَّدِيقِ</span>
                      <span className="text-xs text-amber-600">(the friend's name)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "negation" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    Arabic uses different words to say{" "}
                    <span className="font-semibold">"not"</span> depending on the sentence type.
                    Negation particles are highlighted in{" "}
                    <span className="font-semibold text-red-700">red</span>.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-red-100 text-red-900 border border-red-300 rounded px-1.5 py-0.5 text-sm font-arabic">لَا</span>
                      <span className="text-sm font-arabic">أَذْهَبُ إِلَى الْمَدْرَسَة</span>
                      <span className="text-xs text-amber-600">(I do not go to school)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="text-sm font-arabic">هُوَ</span>
                      <span className="bg-red-100 text-red-900 border border-red-300 rounded px-1.5 py-0.5 text-sm font-arabic">لَيْسَ</span>
                      <span className="text-sm font-arabic">طَالِبًا</span>
                      <span className="text-xs text-amber-600">(He is not a student)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-red-100 text-red-900 border border-red-300 rounded px-1.5 py-0.5 text-sm font-arabic">مَا</span>
                      <span className="text-sm font-arabic">ذَهَبَ إِلَى السُّوق</span>
                      <span className="text-xs text-amber-600">(He did not go to the market)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "question_words" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    These words are used at the beginning of sentences to ask questions.
                    Question words are highlighted in{" "}
                    <span className="font-semibold text-yellow-700">yellow</span>.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-yellow-100 text-yellow-900 border border-yellow-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَيْنَ</span>
                      <span className="text-sm font-arabic">تَذْهَبُ؟</span>
                      <span className="text-xs text-amber-600">(Where are you going?)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-yellow-100 text-yellow-900 border border-yellow-300 rounded px-1.5 py-0.5 text-sm font-arabic">مَاذَا</span>
                      <span className="text-sm font-arabic">تَفْعَلُ؟</span>
                      <span className="text-xs text-amber-600">(What are you doing?)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-yellow-100 text-yellow-900 border border-yellow-300 rounded px-1.5 py-0.5 text-sm font-arabic">كَيْفَ</span>
                      <span className="text-sm font-arabic">حَالُكَ؟</span>
                      <span className="text-xs text-amber-600">(How are you?)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "connectors" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    These words join ideas into stories and reasons.
                    Connectors are highlighted by type:{" "}
                    <span className="font-semibold text-gray-700">وَ</span> (and),{" "}
                    <span className="font-semibold text-green-700">ثُمَّ</span> (then),{" "}
                    <span className="font-semibold text-red-700">لَكِنْ</span> (but),{" "}
                    <span className="font-semibold text-blue-700">لِأَنَّ</span> (because, with its pronoun tails).
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="text-sm font-arabic">أَكَلْتُ</span>
                      <span className="bg-gray-200 text-gray-900 border border-gray-300 rounded px-1.5 py-0.5 text-sm font-arabic">وَشَرِبْتُ</span>
                      <span className="text-xs text-amber-600">(I ate and drank)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="text-sm font-arabic">أَكَلْنَا</span>
                      <span className="bg-green-100 text-green-900 border border-green-300 rounded px-1.5 py-0.5 text-sm font-arabic">ثُمَّ</span>
                      <span className="text-sm font-arabic">ذَهَبْنَا</span>
                      <span className="text-xs text-amber-600">(we ate, then we went)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="text-sm font-arabic">أُرِيدُ أَنْ أَذْهَبَ</span>
                      <span className="bg-red-100 text-red-900 border border-red-300 rounded px-1.5 py-0.5 text-sm font-arabic">لَكِنْ</span>
                      <span className="text-sm font-arabic">لَا أَسْتَطِيعُ</span>
                      <span className="text-xs text-amber-600">(... but I cannot)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="text-sm font-arabic">أُحِبُّ الشَّايَ</span>
                      <span className="bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">لِأَنَّهُ</span>
                      <span className="text-sm font-arabic">لَذِيذٌ</span>
                      <span className="text-xs text-amber-600">(... because it is delicious)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "verb_object" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    The verb is followed by something that receives the action.{" "}
                    <span className="font-semibold text-green-700">Verbs</span> are highlighted in green,{" "}
                    <span className="font-semibold text-sky-700">objects</span> in sky blue.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-green-100 text-green-900 border border-green-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَقْرَأُ</span>
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">الْكِتَاب</span>
                      <span className="text-xs text-amber-600">(I read the book)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-green-100 text-green-900 border border-green-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَكْتُبُ</span>
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">الدَّرْس</span>
                      <span className="text-xs text-amber-600">(I write the lesson)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-green-100 text-green-900 border border-green-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَزُورُ</span>
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">بَيْتَ</span>
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">الصَّدِيقِ</span>
                      <span className="text-xs text-amber-600">(I visit the friend's house)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "future_tense" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    Use{" "}
                    <span className="font-semibold text-purple-700">سَـ</span> (prefix) or{" "}
                    <span className="font-semibold text-purple-700">سَوْفَ</span> (separate word) before a present verb to talk about the future.
                    The future marker is highlighted in{" "}
                    <span className="font-semibold text-purple-700">purple</span> and the verb in{" "}
                    <span className="font-semibold text-green-700">green</span>.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic"><span className="bg-purple-100 text-purple-900 rounded-r">سَ</span><span className="bg-green-100 text-green-900 rounded-l">أَذْهَبُ</span></span>
                      <span className="text-xs text-amber-600 mr-1">(I will go)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic"><span className="bg-purple-100 text-purple-900 rounded-r">سَ</span><span className="bg-green-100 text-green-900 rounded-l">نَذْهَبُ</span></span>
                      <span className="text-xs text-amber-600 mr-1">(we will go)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-purple-100 text-purple-900 border border-purple-300 rounded px-1.5 py-0.5 text-sm font-arabic">سَوْفَ</span>
                      <span className="bg-green-100 text-green-900 border border-green-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَقْرَأُ</span>
                      <span className="text-xs text-amber-600">(I will read)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "object_pronouns_basic" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    Pronouns attach to verbs so you don't repeat the noun.{" "}
                    The <span className="font-semibold text-green-700">verb</span> is highlighted in green and the{" "}
                    <span className="font-semibold text-teal-700">pronoun suffix</span> in teal.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic"><span className="bg-green-100 text-green-900 rounded-r">أَعْرِفُ</span><span className="bg-teal-200 text-teal-900 rounded-l">هُ</span></span>
                      <span className="text-xs text-amber-600 mr-1">(I know him)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic"><span className="bg-green-100 text-green-900 rounded-r">أُحِبُّ</span><span className="bg-teal-200 text-teal-900 rounded-l">هَا</span></span>
                      <span className="text-xs text-amber-600 mr-1">(I love her)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic"><span className="bg-green-100 text-green-900 rounded-r">يَفْهَمُ</span><span className="bg-teal-200 text-teal-900 rounded-l">نِي</span></span>
                      <span className="text-xs text-amber-600 mr-1">(he understands me)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "object_pronouns_expanded" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    You can attach pronouns to verbs to refer to people like you and us.{" "}
                    The <span className="font-semibold text-green-700">verb</span> is highlighted in green and the{" "}
                    <span className="font-semibold text-teal-700">pronoun suffix</span> in teal.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic"><span className="bg-green-100 text-green-900 rounded-r">أَرَا</span><span className="bg-teal-200 text-teal-900 rounded-l">كَ</span></span>
                      <span className="text-xs text-amber-600 mr-1">(I see you m)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic"><span className="bg-green-100 text-green-900 rounded-r">أَرَا</span><span className="bg-teal-200 text-teal-900 rounded-l">كِ</span></span>
                      <span className="text-xs text-amber-600 mr-1">(I see you f)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic"><span className="bg-green-100 text-green-900 rounded-r">يَرَا</span><span className="bg-teal-200 text-teal-900 rounded-l">نَا</span></span>
                      <span className="text-xs text-amber-600 mr-1">(he sees us)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "comparatives" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    Arabic uses the <span className="font-semibold text-pink-700">أفعل</span> pattern to compare two things.
                    The <span className="font-semibold text-pink-700">comparative word</span> is highlighted in pink and the{" "}
                    <span className="font-semibold text-indigo-700">مِنْ</span> (than) particle in indigo.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-pink-100 text-pink-900 border border-pink-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَفْضَل</span>
                      <span className="bg-indigo-100 text-indigo-900 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-arabic">مِنْ</span>
                      <span className="text-xs text-amber-600">(better than)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-pink-100 text-pink-900 border border-pink-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَكْبَر</span>
                      <span className="bg-indigo-100 text-indigo-900 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-arabic">مِنْ</span>
                      <span className="text-xs text-amber-600">(bigger than)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-pink-100 text-pink-900 border border-pink-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَسْهَل</span>
                      <span className="bg-indigo-100 text-indigo-900 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-arabic">مِنْ</span>
                      <span className="text-xs text-amber-600">(easier than)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-pink-100 text-pink-900 border border-pink-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَصْعَب</span>
                      <span className="bg-indigo-100 text-indigo-900 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-arabic">مِنْ</span>
                      <span className="text-xs text-amber-600">(harder than)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-pink-100 text-pink-900 border border-pink-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَرْخَص</span>
                      <span className="bg-indigo-100 text-indigo-900 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-arabic">مِنْ</span>
                      <span className="text-xs text-amber-600">(cheaper than)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-pink-100 text-pink-900 border border-pink-300 rounded px-1.5 py-0.5 text-sm font-arabic">أَكْثَر</span>
                      <span className="bg-indigo-100 text-indigo-900 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-arabic">مِنْ</span>
                      <span className="text-xs text-amber-600">(more than)</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "dual_forms" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    Arabic has a special form for exactly <span className="font-semibold text-orange-700">two</span> of something.
                    The <span className="font-semibold text-orange-700">dual suffix</span> (ـانِ or ـيْنِ) is highlighted in orange.
                    When an adjective follows a dual noun it must also take the dual suffix —
                    this <span className="font-semibold text-emerald-700">adjective agreement</span> is shown with a stacked{" "}
                    <span className="font-semibold text-emerald-700">نعت</span> badge.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-orange-100 text-orange-900 rounded-r">كِتَاب</span><span className="bg-orange-200 text-orange-900 rounded-l">َانِ</span>
                      </span>
                      <span className="text-xs text-amber-600">(two books)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-orange-100 text-orange-900 rounded-r">طَالِب</span><span className="bg-orange-200 text-orange-900 rounded-l">َانِ</span>
                      </span>
                      <span className="text-xs text-amber-600">(two students)</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-orange-100 text-orange-900 rounded-r">طَالِب</span><span className="bg-orange-200 text-orange-900 rounded-l">َيْنِ</span>
                      </span>
                      <span className="text-xs text-amber-600">(two students — after verb/prep)</span>
                    </span>
                  </div>
                  {/* Adjective agreement example */}
                  <div className="flex flex-wrap gap-1 items-center mt-1" dir="rtl">
                    <span className="inline-flex flex-col items-center mx-0.5">
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-orange-100 text-orange-900 rounded-r">طَبَق</span><span className="bg-orange-200 text-orange-900 rounded-l">َانِ</span>
                      </span>
                      <span className="text-[9px] bg-orange-100 text-orange-700 border border-orange-300 rounded px-1 font-arabic leading-none mt-0.5">مثنى</span>
                    </span>
                    <span className="inline-flex flex-col items-center mx-0.5">
                      <span className="whitespace-nowrap text-sm font-arabic">
                        <span className="bg-orange-100 text-orange-900 rounded-r">لَذِيذ</span><span className="bg-orange-200 text-orange-900 rounded-l">َانِ</span>
                      </span>
                      <span className="text-[9px] bg-orange-100 text-orange-700 border border-orange-300 rounded px-1 font-arabic leading-none mt-0.5">مثنى</span>
                      <span className="text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-300 rounded px-1 font-arabic leading-none mt-0.5">نعت</span>
                    </span>
                    <span className="text-xs text-amber-600 mr-1">(two delicious dishes)</span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "broken_plurals_intro" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    <span className="font-semibold text-rose-700">Broken plurals</span> (جمع التكسير) change the internal structure of the word — they cannot be guessed from rules and must be learned as vocabulary pairs.{" "}
                    <span className="font-semibold text-sky-700">Singular</span> forms are shown in sky blue, <span className="font-semibold text-rose-700">plural</span> forms in rose.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">كِتَاب</span>
                      <span className="text-xs text-slate-400">→</span>
                      <span className="bg-rose-100 text-rose-900 border border-rose-300 rounded px-1.5 py-0.5 text-sm font-arabic">كُتُب</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">طَالِب</span>
                      <span className="text-xs text-slate-400">→</span>
                      <span className="bg-rose-100 text-rose-900 border border-rose-300 rounded px-1.5 py-0.5 text-sm font-arabic">طُلَّاب</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="bg-sky-100 text-sky-900 border border-sky-300 rounded px-1.5 py-0.5 text-sm font-arabic">مَسْجِد</span>
                      <span className="text-xs text-slate-400">→</span>
                      <span className="bg-rose-100 text-rose-900 border border-rose-300 rounded px-1.5 py-0.5 text-sm font-arabic">مَسَاجِد</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "imperatives" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    <span className="font-semibold text-red-700">Imperative verbs</span> (فعل الأمر) are used to give commands or instructions.{" "}
                    Known imperative forms (stored on verb entries) are highlighted in <span className="font-semibold text-red-700">red</span>.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex flex-col items-center">
                      <span className="bg-red-100 text-red-900 border border-red-300 rounded px-1.5 py-0.5 text-sm font-arabic">اِذْهَبْ</span>
                      <span className="text-xs text-amber-600 mt-0.5">go!</span>
                    </span>
                    <span className="inline-flex flex-col items-center">
                      <span className="bg-red-100 text-red-900 border border-red-300 rounded px-1.5 py-0.5 text-sm font-arabic">اِقْرَأْ</span>
                      <span className="text-xs text-amber-600 mt-0.5">read!</span>
                    </span>
                    <span className="inline-flex flex-col items-center">
                      <span className="bg-red-100 text-red-900 border border-red-300 rounded px-1.5 py-0.5 text-sm font-arabic">اُكْتُبْ</span>
                      <span className="text-xs text-amber-600 mt-0.5">write!</span>
                    </span>
                  </div>
                </div>
              ) : topic.grammar_rule_code === "case_endings" ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-700">
                    <span className="font-semibold">Case endings</span> (الإعراب) — the final short vowel is the noun's name tag:{" "}
                    <span className="font-semibold text-blue-700">-u</span> for the subject,{" "}
                    <span className="font-semibold text-emerald-700">-a</span> for the object, and{" "}
                    <span className="font-semibold text-rose-700">-i</span> after a preposition. Without الـ the tag doubles (tanwīn): -un / -an / -in.
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1" dir="rtl">
                    <span className="inline-flex flex-col items-center">
                      <span className="bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 text-sm font-arabic">الْكِتَابُ جَدِيدٌ</span>
                      <span className="text-xs text-amber-600 mt-0.5">subject: -u</span>
                    </span>
                    <span className="inline-flex flex-col items-center">
                      <span className="bg-emerald-100 text-emerald-900 border border-emerald-300 rounded px-1.5 py-0.5 text-sm font-arabic">أُرِيدُ الْكِتَابَ</span>
                      <span className="text-xs text-amber-600 mt-0.5">object: -a</span>
                    </span>
                    <span className="inline-flex flex-col items-center">
                      <span className="bg-rose-100 text-rose-900 border border-rose-300 rounded px-1.5 py-0.5 text-sm font-arabic">فِي الْكِتَابِ</span>
                      <span className="text-xs text-amber-600 mt-0.5">after a preposition: -i</span>
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mt-1.5 text-xs text-amber-600">
                  Nominal sub-sentences are highlighted below. The <span className="font-semibold text-blue-600">مبتدأ</span> (subject) and <span className="font-semibold text-emerald-600">خبر</span> (predicate) of each are labelled.
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            {displayTurns.map((turn, turnIdx) => {
              // When showGrammar is active for nominal_sentence rule, parse the arabic
              // text into sub-sentences and detect which ones are nominal.
              // Otherwise fall back to the flat token list used by the standard renderer.
              const grammarMode = showGrammar && topic.grammar_rule_code === "nominal_sentence";
              // The Pronoun/Preposition toolbar PILLS only add an underline (handled
              // in renderToken); the rich stem/suffix badge rendering is reserved for
              // the admin "Show Grammar" rule views.
              const pronounMode = showGrammar && topic.grammar_rule_code === "personal_pronouns";
              const prepositionMode = showGrammar && topic.grammar_rule_code === "prepositions";
              const definiteMode = showGrammar && topic.grammar_rule_code === "definite_indefinite";
              const wordOrderMode = showGrammar && topic.grammar_rule_code === "word_order";
              const pastTenseMode = showGrammar && topic.grammar_rule_code === "past_tense";
              const presentTenseIntroMode = showGrammar && topic.grammar_rule_code === "present_tense_intro";
              const genderMode = showGrammar && topic.grammar_rule_code === "gender";
              const adjMode = showGrammar && (topic.grammar_rule_code === "adjectives" || topic.grammar_rule_code === "adjective_agreement");
              const idafaMode = showGrammar && topic.grammar_rule_code === "idafa";
              const negationMode = showGrammar && topic.grammar_rule_code === "negation";
              const questionWordsMode = showGrammar && topic.grammar_rule_code === "question_words";
              const connectorsMode = showGrammar && topic.grammar_rule_code === "connectors";
              const verbObjectMode = showGrammar && topic.grammar_rule_code === "verb_object";
              const futureTenseMode = showGrammar && topic.grammar_rule_code === "future_tense";
              const objectPronounsBasicMode = showGrammar && topic.grammar_rule_code === "object_pronouns_basic";
              const objectPronounsExpandedMode = showGrammar && topic.grammar_rule_code === "object_pronouns_expanded";
              const comparativesMode = showGrammar && topic.grammar_rule_code === "comparatives";
              const dualFormsMode = showGrammar && topic.grammar_rule_code === "dual_forms";
              const brokenPluralsMode = showGrammar && topic.grammar_rule_code === "broken_plurals_intro";
              const imperativesMode = showGrammar && topic.grammar_rule_code === "imperatives";
              const tokens = (turn.arabic || "").split(/\s+/);

              return (
                <React.Fragment key={turn.id}>
                <DiamondDivider className="my-4" />
                <div
                  ref={(el) => { turnRefsRef.current[turn.id] = el; }}
                  draggable={isAdmin && editingTurnId !== turn.id}
                  onDragStart={() => isAdmin && handleTurnDragStart(turnIdx)}
                  onDragEnter={() => isAdmin && handleTurnDragEnter(turnIdx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnd={() => isAdmin && handleTurnDragEnd()}
                  className={`pb-2 transition-colors rounded ${
                    activeTurnId === turn.id || playingTurnId === turn.id || flashTurnId === turn.id ? "-mx-1 px-1" : ""
                  } ${flashTurnId === turn.id ? "gs-flash" : ""}`}
                  style={{
                    borderColor: "var(--panel-line)",
                    ...(activeTurnId === turn.id
                      ? { background: "var(--gold-bg)", boxShadow: "0 0 0 1px rgba(201,162,39,0.35)" }
                      : playingTurnId === turn.id
                      ? { background: "var(--forest-soft)" }
                      : {}),
                  }}
                >
                  {editingTurnId === turn.id ? (
                    <ScenarioTurnEdit
                      turn={turn}
                      onSave={handleTurnSaved}
                      onDelete={handleTurnDeleted}
                      onCancel={() => setEditingTurnId(null)}
                      existingSpeakers={[...new Set((localTurns || topic.scenario_turns || []).map((t) => t.speaker).filter(Boolean))]}
                    />
                  ) : (
                    <>
                      {/* Speaker name + play + edit buttons */}
                      <div className="flex items-center justify-end gap-1 mb-1">
                        {isAdmin && (
                          <span
                            title="Drag to reorder"
                            className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 select-none px-0.5 mr-auto"
                          >
                            ⠿
                          </span>
                        )}
                        {isAdmin && (
                          <>
                            <select
                              value={refineModel}
                              onChange={(e) => {
                                const v = e.target.value;
                                setRefineModel(v);
                                try { localStorage.setItem("refineModel", v); } catch {}
                              }}
                              disabled={refiningTurnId === turn.id}
                              title="AI model used for Refine"
                              className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-600 hover:border-amber-300 focus:outline-none focus:border-amber-400 disabled:opacity-50"
                            >
                              <option value="">Default</option>
                              <option value="gpt-5">GPT-5</option>
                              <option value="gpt-5-mini">GPT-5 Mini</option>
                              <option value="gpt-5-nano">GPT-5 Nano</option>
                              <option value="gpt-4.1">GPT-4.1</option>
                              <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                              <option value="gpt-4o">GPT-4o</option>
                              <option value="gpt-4o-mini">GPT-4o Mini</option>
                              <option value="gpt-4-turbo">GPT-4 Turbo</option>
                              <option value="gpt-4">GPT-4</option>
                              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => handleRefineTurn(turn)}
                              disabled={refiningTurnId === turn.id}
                              title={`Refine: add harakat + fix spelling (AI${refineModel ? ` — ${refineModel}` : ""})`}
                              aria-label="Refine line"
                              className="ml-1 p-1 rounded text-amber-500 hover:text-amber-700 hover:bg-amber-50 shrink-0 disabled:opacity-50 disabled:cursor-wait"
                            >
                              {refiningTurnId === turn.id ? (
                                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                              ) : (
                                <span className="text-base leading-none">✨</span>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleGenerateTurnMorphology(turn)}
                              disabled={morphTurnId === turn.id}
                              title="Generate word structures (morphology) for the words in this line (AI)"
                              aria-label="Generate word structures for this line"
                              className="ml-0.5 p-1 rounded shrink-0 disabled:opacity-50 disabled:cursor-wait transition-colors hover:brightness-110"
                              style={{ color: "var(--gold-bright)" }}
                            >
                              {morphTurnId === turn.id ? (
                                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                              ) : (
                                <span className="text-base leading-none">◇</span>
                              )}
                            </button>
                          </>
                        )}
                        {isAdmin && (
                          <PencilButton onClick={() => setEditingTurnId(turn.id)} />
                        )}
                        {showSyncVideo && turn.video_start_ms != null && (() => {
                          // This line is the one currently playing in the video.
                          const isActiveLine = activeTurnId === turn.id;
                          const isThisPlaying = isVideoPlaying && isActiveLine;
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                const p = syncPlayerRef.current;
                                if (!p) return;
                                if (isThisPlaying) p.pause();
                                else if (isActiveLine) p.play(); // resume from here
                                else p.seekToMs(turn.video_start_ms); // jump + play
                              }}
                              title={isThisPlaying ? "Pause video" : "Play the video from this line"}
                              aria-label={isThisPlaying ? "Pause video" : "Play video from this line"}
                              className="p-1 rounded text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                            >
                              {isThisPlaying ? (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              )}
                            </button>
                          );
                        })()}
                        {!isYouTubeTopic && isAudioPlaybackAllowed() && (
                          <PlayButton
                            onClick={() =>
                              playingTurnId === turn.id || loadingTurnId === turn.id
                                ? stop()
                                : playTurn(turn)
                            }
                            loading={loadingTurnId === turn.id}
                            playing={playingTurnId === turn.id}
                            title={playingTurnId === turn.id ? "Stop" : "Play this line"}
                          />
                        )}
                        {turn.speaker && (
                          <span className="text-xs font-medium font-arabic" style={{ color: "var(--parchment-dim)" }} dir="rtl">
                            {turn.speaker}
                          </span>
                        )}
                      </div>

                      {/* Arabic line with clickable tokens (RTL) — flex-wrap word
                          layout matching the Quran verses. */}
                      <div
                        dir="rtl"
                        className="flex flex-wrap gap-x-1 gap-y-2 justify-start mb-2 font-amiri text-2xl leading-relaxed"
                        style={{ color: "var(--parchment)" }}
                      >
                        {(() => {
                          // Helper: render a single token as a clickable button or plain span.
                          const renderToken = (tok, idx, globalOffset) => {
                            const match = findWordForToken(tok);
                            // Word being spoken right now in the synced video.
                            const isActiveWord =
                              activeTurnId === turn.id && globalOffset === activeWordIndex;
                            const activeWordCls = isActiveWord
                              ? " bg-amber-300 text-ink-900 rounded shadow-sm"
                              : "";

                            // Pill-driven category color: read the word structure
                            // (§9 morphology) for this token when one exists; fall
                            // back to the older word-list/heuristic detection so
                            // lines without generated structures still highlight.
                            const morphEntry = (turn.word_morphology || {})[String(globalOffset)];
                            const normTok = normalizeArabic(tok);
                            let typeCls = "";
                            if (morphEntry) {
                              if (colorToggles.verb && morphIsVerb(morphEntry)) typeCls = " tokc-verb";
                              else if (colorToggles.pronoun && morphIsPronounWord(morphEntry)) typeCls = " tokc-pronoun";
                              else if (colorToggles.prepositions && morphIsPreposition(morphEntry)) typeCls = " tokc-prep";
                              else if (colorToggles.noun && morphIsNoun(morphEntry)) typeCls = " tokc-noun";
                            } else {
                              if (colorToggles.verb && match && verbIds.has(match.id)) typeCls = " tokc-verb";
                              else if (colorToggles.pronoun && (INDEPENDENT_PRONOUNS_SET.has(normTok) || detectAttachedPronoun(normTok))) typeCls = " tokc-pronoun";
                              else if (colorToggles.prepositions && isPrepositionToken(tok)) typeCls = " tokc-prep";
                            }
                            // Attached pronoun → tint only the suffix letters
                            // (needs the structure to know where they start).
                            const pronSplit = colorToggles.pronoun && morphEntry
                              ? morphPronounSuffixSplit(tok, morphEntry)
                              : null;
                            const tokenBody = !isActiveWord && pronSplit
                              ? (
                                <>
                                  {pronSplit.stem}
                                  <span className="tokc-pron-suffix">{pronSplit.suffix}</span>
                                  {pronSplit.tail}
                                </>
                              )
                              : null;

                            if (!match) {
                              // Not in the topic word list (capped list, plural/
                              // al-prefixed form, etc.) — still tappable: the
                              // bubble opens with a stub word and falls back to
                              // the shared word-gloss cache for its definition.
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  className={isActiveWord
                                    ? "cursor-pointer px-2 py-0.5 rounded-lg" + activeWordCls
                                    : "cursor-pointer px-2 py-0.5 rounded-lg tok-word" + typeCls}
                                  style={{ border: "none" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setBubble({
                                      word: { id: `tok-${globalOffset}`, text: stripPunctuationOnly(tok), senses: [] },
                                      rect, turnId: turn.id, tokenIdx: globalOffset,
                                    });
                                  }}
                                >
                                  {tokenBody || tok}
                                </button>
                              );
                            }

                            const isNew = newIds.has(match.id);
                            const isForgotten = forgottenIds.has(match.id);
                            const isExpression = expressionIds.has(match.id);
                            const isSelected =
                              selectedWordId === match.id ||
                              bubble?.word?.id === match.id ||
                              clickedTokenId === match.id ||
                              // Group forms sharing a lemma/root so و/ال-prefixed variants
                              // and conjugations highlight together with their base word.
                              (linkedWordId !== null && (
                                match.id === linkedWordId ||
                                match.lemma?.id === linkedWordId ||
                                match.conjugation_info?.word_id === linkedWordId
                              ));

                            // Every reading word is the same quiet gold-bright;
                            // an enabled pill recolors the whole word (tokc-*):
                            // Verb→violet, Noun→rose, Pronoun→teal, Preposition→orange.
                            // The hidden New/Forgotten/Expression pills keep their
                            // old underline classes for when they return.
                            let classes =
                              "cursor-pointer px-2 py-0.5 rounded-lg tok-word" + typeCls;
                            if (!typeCls) {
                              if (isExpression && colorToggles.expression) {
                                classes += " tok-expr";
                              } else if (isForgotten && colorToggles.forgotten) {
                                classes += " tok-forgotten";
                              } else if (isNew && colorToggles.new) {
                                classes += " tok-new";
                              }
                            }
                            if (isSelected) {
                              classes += " tok-selected";
                            }
                            // Synced-playback highlight wins so the current word
                            // is unmistakable.
                            if (isActiveWord) {
                              classes = "cursor-pointer px-2 py-0.5 rounded-lg" + activeWordCls;
                            }

                            return (
                              <button
                                key={idx}
                                type="button"
                                className={classes}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setBubble({ word: match, rect, turnId: turn.id, tokenIdx: globalOffset });
                                  if (selectedWordId && selectedWordId !== match.id) {
                                    onClearWord && onClearWord();
                                  }
                                  // Track which word to highlight in the list (persists after scroll/bubble-close).
                                  // Conjugated forms → highlight root verb; all others → highlight self.
                                  setClickedTokenId(match.id);
                                  // Highlight the whole group: linked (و/ال) forms → lemma;
                                  // conjugated forms → root verb; everything else → self.
                                  setLinkedWordId(
                                    match.lemma?.id ??
                                    (match.conjugation_info ? match.conjugation_info.word_id : match.id)
                                  );
                                }}
                                style={{ border: "none", padding: undefined }}
                              >
                                {(() => {
                                  // Attached-pronoun tint wins over the linked-prefix tint.
                                  if (tokenBody) return tokenBody;
                                  // For a linked word (shares its base form's definition),
                                  // tint the leading و/ال clitic so it reads as not part of the word.
                                  if (!match.lemma) return tok;
                                  const { prefix, rest } = splitLinkedPrefix(tok);
                                  if (!prefix) return tok;
                                  return (
                                    <>
                                      <span className="text-sky-500" title="Prefix — not part of the word">{prefix}</span>
                                      {rest}
                                    </>
                                  );
                                })()}
                              </button>
                            );
                          };

                          if (!grammarMode && !pronounMode && !prepositionMode && !definiteMode && !wordOrderMode && !pastTenseMode && !presentTenseIntroMode && !genderMode && !adjMode && !idafaMode && !negationMode && !questionWordsMode && !connectorsMode && !verbObjectMode && !futureTenseMode && !objectPronounsBasicMode && !objectPronounsExpandedMode && !comparativesMode && !dualFormsMode && !brokenPluralsMode && !imperativesMode) {
                            // Standard rendering — flat list of tokens.
                            return tokens.map((tok, idx) => renderToken(tok, idx, idx));
                          }

                          // ── Personal pronoun rendering ────────────────────────────
                          // Independent pronouns → purple background + "ضمير" badge.
                          // Words with an attached-pronoun suffix → stem plain,
                          //   suffix highlighted in teal, label badge below.
                          // All other tokens → standard renderToken.
                          if (pronounMode) {
                            return tokens.map((tok, idx) => {
                              const normTok = normalizeArabic(tok);
                              const isIndependent = INDEPENDENT_PRONOUNS_SET.has(normTok);

                              // Check for admin manual pronoun annotation first
                              let attachedInfo = null;
                              if (!isIndependent) {
                                const manualVal = (turn.word_annotations || {})[String(idx)];
                                if (manualVal && manualVal.startsWith("pronoun:")) {
                                  const suffix = manualVal.slice("pronoun:".length);
                                  if (suffix !== "none") {
                                    const entry = ATTACHED_PRONOUN_SUFFIXES.find((s) => s.suffix === suffix);
                                    if (entry) {
                                      attachedInfo = { ...entry, normStemLength: normTok.length - entry.suffix.length };
                                    }
                                  }
                                  // suffix === "none" → attachedInfo stays null (explicitly cleared)
                                } else {
                                  // Check global annotation before heuristic
                                  const globalVal = globalPronounAnnotations[normTok];
                                  if (globalVal !== undefined) {
                                    if (globalVal !== "none") {
                                      const entry = ATTACHED_PRONOUN_SUFFIXES.find((s) => s.suffix === globalVal);
                                      if (entry) {
                                        attachedInfo = { ...entry, normStemLength: normTok.length - entry.suffix.length };
                                      }
                                    }
                                    // globalVal === "none" → suppress, attachedInfo stays null
                                  } else {
                                    attachedInfo = detectAttachedPronoun(normTok);
                                  }
                                }
                              }

                              if (!isIndependent && !attachedInfo) {
                                return renderToken(tok, idx, idx);
                              }

                              // Shared click handler (mirrors renderToken's click logic)
                              const match = findWordForToken(tok);
                              const handleClick = match
                                ? (e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setBubble({ word: match, rect, turnId: turn.id, tokenIdx: idx });
                                    if (selectedWordId && selectedWordId !== match.id) {
                                      onClearWord && onClearWord();
                                    }
                                  }
                                : undefined;

                              if (isIndependent) {
                                return (
                                  <span key={`pr-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button
                                      type="button"
                                      className="cursor-pointer px-1 py-0.5 rounded bg-purple-100 text-purple-900 hover:bg-purple-200 transition-colors"
                                      onClick={handleClick}
                                      style={{ border: "none" }}
                                    >
                                      {tok}
                                    </button>
                                    <span className="text-[9px] bg-purple-100 text-purple-600 border border-purple-300 rounded px-1 font-arabic leading-none mt-0.5">ضمير</span>
                                  </span>
                                );
                              }

                              // Attached pronoun: split original token into stem + suffix.
                              // Rendered as a single inline word so Arabic shaping connects
                              // the stem and suffix characters; only the suffix is highlighted.
                              // The stem also picks up any active word-level color (new/forgotten/verb/expression)
                              // so both highlights are visible simultaneously.
                              const wIsForgotten = match && forgottenIds.has(match.id);
                              const wIsNew = match && newIds.has(match.id);
                              const wIsVerb = match && verbIds.has(match.id);
                              const wIsExpression = match && expressionIds.has(match.id);
                              let stemBg = "";
                              if (wIsForgotten && colorToggles.forgotten) {
                                stemBg = "bg-red-100 text-red-800";
                              } else if (wIsNew && colorToggles.new) {
                                if (wIsVerb && colorToggles.verb) stemBg = "bg-pink-100 text-pink-800";
                                else if (wIsExpression && colorToggles.expression) stemBg = "bg-amber-100 text-amber-800";
                                else stemBg = "bg-green-100 text-green-800";
                              } else if (wIsVerb && colorToggles.verb) {
                                stemBg = "bg-pink-50 text-pink-700";
                              } else if (wIsExpression && colorToggles.expression) {
                                stemBg = "bg-amber-50 text-amber-700";
                              }
                              const { normStemLength, label } = attachedInfo;
                              const [stemPart, suffixPart] = splitTokenAtNormPos(tok, normStemLength);
                              return (
                                <span key={`pr-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                  <button
                                    type="button"
                                    className="cursor-pointer py-0.5 px-0.5 rounded hover:bg-teal-50 transition-colors"
                                    onClick={handleClick}
                                    style={{ border: "none" }}
                                  >
                                    <span className={stemBg ? `${stemBg} rounded-r` : ""}>{stemPart}</span><span className="bg-teal-200 text-teal-900 rounded-l">{suffixPart}</span>
                                  </button>
                                  <span className="text-[9px] bg-teal-100 text-teal-700 border border-teal-300 rounded px-1 font-arabic leading-none mt-0.5">{label}</span>
                                </span>
                              );
                            });
                          }

                          // ── Preposition rendering ─────────────────────────────────────
                          // Prepositions → indigo background + "حرف جر" badge.
                          // The token immediately after a preposition → orange background.
                          // All other tokens → standard renderToken.
                          if (prepositionMode) {
                            return tokens.map((tok, idx) => {

                              // Manual annotation takes priority over heuristic.
                              const annotation = ((turn.word_annotations || {})[String(idx)] || "");
                              const prepRole = annotation.startsWith("prep:") ? annotation.slice(5) : null;
                              const prevAnnotation = idx > 0 ? ((turn.word_annotations || {})[String(idx - 1)] || "") : "";
                              const prevPrepRole = prevAnnotation.startsWith("prep:") ? prevAnnotation.slice(5) : null;

                              const isPrep = prepRole === "harf_jar" ||
                                (prepRole === null && isPrepositionToken(tok));
                              const prevIsPrep = prevPrepRole === "harf_jar" ||
                                (prevPrepRole === null && idx > 0 && isPrepositionToken(tokens[idx - 1]));
                              const isAffected = prepRole === "baad_harf_jar" ||
                                (prepRole === null && !isPrep && idx > 0 && prevIsPrep);
                              const isSuppressed = prepRole === "none";

                              if (isSuppressed || (!isPrep && !isAffected)) {
                                // In prepositionMode, plain tokens are still clickable so
                                // the admin can manually assign a prep role via the bubble.
                                if (isAdmin) {
                                  const match = findWordForToken(tok);
                                  return (
                                    <button
                                      key={idx}
                                      type="button"
                                      className="px-1 py-0.5 rounded hover:bg-slate-100 transition-colors font-arabic"
                                      style={{ border: "none" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                        setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                        if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                                      }}
                                    >
                                      {tok}
                                    </button>
                                  );
                                }
                                return renderToken(tok, idx, idx);
                              }

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                // Open bubble even for unmatched tokens so admin can set annotation.
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) {
                                  onClearWord && onClearWord();
                                }
                              };

                              if (isPrep) {
                                return (
                                  <span key={`prep-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button
                                      type="button"
                                      className="cursor-pointer px-1 py-0.5 rounded bg-indigo-100 text-indigo-900 hover:bg-indigo-200 transition-colors"
                                      onClick={handleClick}
                                      style={{ border: "none" }}
                                    >
                                      {tok}
                                    </button>
                                    <span className="text-[9px] bg-indigo-100 text-indigo-600 border border-indigo-300 rounded px-1 font-arabic leading-none mt-0.5">حرف جر</span>
                                  </span>
                                );
                              }

                              // Affected word (follows the preposition)
                              return (
                                <span key={`prep-aff-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                  <button
                                    type="button"
                                    className="cursor-pointer px-1 py-0.5 rounded bg-orange-100 text-orange-900 hover:bg-orange-200 transition-colors"
                                    onClick={handleClick}
                                    style={{ border: "none" }}
                                  >
                                    {tok}
                                  </button>
                                  <span className="text-[9px] bg-orange-100 text-orange-500 border border-orange-300 rounded px-1 font-arabic leading-none mt-0.5">بعد حرف الجر</span>
                                </span>
                              );
                            });
                          }

                          // ── Definite article rendering ────────────────────────────────
                          // Words starting with "ال" → purple background + "الـ" badge.
                          if (definiteMode) {
                            return tokens.map((tok, idx) => {
                              const annotation = ((turn.word_annotations || {})[String(idx)] || "");
                              const defRole = annotation.startsWith("def:") ? annotation.slice(4) : null;

                              const isDefinite = defRole === "definite" ||
                                (defRole === null && isDefiniteToken(tok));
                              const isIndefinite = defRole === "indefinite";
                              const isSuppressed = defRole === "none";

                              // Manually-marked indefinite tokens → sky-blue highlight + "نكرة" badge.
                              if (isIndefinite) {
                                const match = findWordForToken(tok);
                                const handleClick = (e) => {
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                  setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                  if (match && selectedWordId && selectedWordId !== match.id) {
                                    onClearWord && onClearWord();
                                  }
                                };
                                return (
                                  <span key={`indef-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button
                                      type="button"
                                      className="cursor-pointer px-1 py-0.5 rounded bg-sky-100 text-sky-900 hover:bg-sky-200 transition-colors"
                                      onClick={handleClick}
                                      style={{ border: "none" }}
                                    >
                                      {tok}
                                    </button>
                                    <span className="text-[9px] bg-sky-100 text-sky-600 border border-sky-300 rounded px-1 font-arabic leading-none mt-0.5">نكرة</span>
                                  </span>
                                );
                              }

                              if (isSuppressed || !isDefinite) {
                                if (isAdmin) {
                                  const match = findWordForToken(tok);
                                  return (
                                    <button
                                      key={idx}
                                      type="button"
                                      className="px-1 py-0.5 rounded hover:bg-slate-100 transition-colors font-arabic"
                                      style={{ border: "none" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                        setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                        if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                                      }}
                                    >
                                      {tok}
                                    </button>
                                  );
                                }
                                return renderToken(tok, idx, idx);
                              }

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) {
                                  onClearWord && onClearWord();
                                }
                              };

                              return (
                                <span key={`def-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                  <button
                                    type="button"
                                    className="cursor-pointer px-1 py-0.5 rounded bg-purple-100 text-purple-900 hover:bg-purple-200 transition-colors"
                                    onClick={handleClick}
                                    style={{ border: "none" }}
                                  >
                                    {tok}
                                  </button>
                                  <span className="text-[9px] bg-purple-100 text-purple-600 border border-purple-300 rounded px-1 font-arabic leading-none mt-0.5">الـ</span>
                                </span>
                              );
                            });
                          }

                          // ── Word order rendering ──────────────────────────────────────
                          // Classifies each segment as جملة فعلية (verbal) or جملة اسمية
                          // (nominal).  Verbal: first token = فعل (rose), second = فاعل
                          // (amber), remaining tokens are layered with preposition (indigo),
                          // definite article (purple), or plain rendering.
                          // Nominal: reuses mubtada/khabar grouping + layered prep/definite.
                          if (wordOrderMode) {
                            const woAnnotations = turn.word_annotations || {};

                            // Effective mubtada/khabar roles for nominal segments
                            // (ignore wo: token annotations — those are for word-order mode only).
                            const woRoles = tokens.map((_, i) => {
                              const m = woAnnotations[String(i)];
                              if (m === "none") return null;
                              if (m === "mubtada" || m === "khabar") return m;
                              if (m && m.startsWith("wo:")) return null;
                              return getHeuristicRoleForToken(turn, i);
                            });

                            // Render a nominal bracket group (mubtada or khabar)
                            const renderWoBracketGroup = (groupToks, groupStart, role, keyPfx) => {
                              const isMubtada = role === "mubtada";
                              const lineColor = isMubtada ? "border-blue-400" : "border-emerald-400";
                              const dropColor = isMubtada ? "bg-blue-400" : "bg-emerald-400";
                              const badgeClass = isMubtada
                                ? "bg-blue-100 text-blue-700 border-blue-300"
                                : "bg-emerald-100 text-emerald-700 border-emerald-300";
                              const bgClass = isMubtada ? "bg-blue-50 border-blue-300" : "bg-emerald-50 border-emerald-300";
                              return (
                                <span
                                  key={keyPfx}
                                  className={`inline-flex flex-col items-center gap-0 rounded-md border px-1 mx-0.5 ${bgClass}`}
                                  dir="rtl"
                                >
                                  <span className="inline-flex flex-wrap justify-center gap-0 pb-0.5">
                                    {groupToks.map((tok, ti) =>
                                      renderToken(tok, `${keyPfx}-${ti}`, groupStart + ti)
                                    )}
                                  </span>
                                  <span className={`self-stretch border-t-2 ${lineColor}`} />
                                  <span className={`w-0.5 h-2.5 ${dropColor}`} />
                                  <span className={`px-1.5 py-px rounded text-[10px] font-semibold leading-none border font-arabic ${badgeClass}`} dir="rtl">
                                    {isMubtada ? "مبتدأ" : "خبر"}
                                  </span>
                                </span>
                              );
                            };

                            // Plain token render (no prep/definite colouring in word-order mode)
                            const renderWoToken = (tok, keyId, globalIdx) => renderToken(tok, keyId, globalIdx);

                            const rawArabicWo = turn.arabic || "";
                            const segPatternWo = /([،.؟!؛,;])/;
                            const partsWo = rawArabicWo.split(segPatternWo);
                            let globalOffsetWo = 0;
                            const renderedWo = [];

                            for (let pi = 0; pi < partsWo.length; pi++) {
                              const part = partsWo[pi];
                              if (!part) continue;
                              if (segPatternWo.test(part) && part.length === 1) {
                                renderedWo.push(<span key={`wo-delim-${pi}`}>{part} </span>);
                                continue;
                              }
                              const segTokens = part.trim().split(/\s+/).filter(Boolean);
                              if (!segTokens.length) continue;
                              const segOffset = globalOffsetWo;

                              // Sentence type priority:
                              // 1. Explicit wo:seg: override on first token
                              // 2. Implicit: any token in segment has wo:fiil/faail/mafool → verbal
                              // 3. Implicit: any token in segment has mubtada/khabar → nominal
                              // 4. Heuristic (isVerbToken)
                              const segTypeAnn = woAnnotations[String(segOffset)] || "";
                              let isVerbalSeg;
                              if (segTypeAnn === "wo:seg:filia") {
                                isVerbalSeg = true;
                              } else if (segTypeAnn === "wo:seg:ismia") {
                                isVerbalSeg = false;
                              } else {
                                const hasVerbalRoleAnn = segTokens.some((_, i) => {
                                  const a = woAnnotations[String(segOffset + i)] || "";
                                  return a === "wo:fiil" || a === "wo:faail" || a === "wo:mafool";
                                });
                                const hasNominalRoleAnn = segTokens.some((_, i) => {
                                  const a = woAnnotations[String(segOffset + i)] || "";
                                  return a === "mubtada" || a === "khabar";
                                });
                                if (hasVerbalRoleAnn) {
                                  isVerbalSeg = true;
                                } else if (hasNominalRoleAnn) {
                                  isVerbalSeg = false;
                                } else {
                                  // Skip leading particles (هَلْ, لَمْ, قَدْ, …) before verb-checking,
                                  // mirroring the SKIP_PARTICLES logic in isNominalSentence.
                                  const WO_SKIP = new Set(["هل","لم","لن","لا","ألا","أما","قد","لقد","سوف","س","إذا","إذ","لو","لما","حين","عندما","أي","كيف","أين","متى","كم","ما","إن","ثم","ماذا","بينما","حتى"]);
                                  let vci = 0;
                                  while (vci < segTokens.length - 1 && WO_SKIP.has(normalizeArabic(segTokens[vci]))) vci++;
                                  const vcNorm = normalizeArabic(segTokens[vci] || "");
                                  const vcIsConj = vcNorm === "و" || vcNorm === "ف";
                                  const verbCheckTok = vcIsConj && vci + 1 < segTokens.length
                                    ? segTokens[vci + 1]
                                    : segTokens[vci];
                                  isVerbalSeg = isVerbToken(verbCheckTok);
                                  if (!isVerbalSeg && segTokens.length >= 2) {
                                    // SVO: subject noun/pronoun precedes the verb — scan tokens after the first
                                    isVerbalSeg = segTokens.slice(vci + 1).some(t => isPastVerbToken(t));
                                  }
                                }
                              }
                              const segWoRoles = segTokens.map((_, i) => woRoles[segOffset + i]);
                              const hasNominalRoles = segWoRoles.some((r) => r !== null);
                              const sentenceLabel = isVerbalSeg ? "جملة فعلية" : "جملة اسمية";
                              const segElements = [];

                              // Shared click handler factory — includes segFirstIdx for admin controls
                              const makeHandleClick = (tok, globalIdx) => (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const matchWo = findWordForToken(tok);
                                const wordForBubble = matchWo || { id: `tok-${globalIdx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: globalIdx, segFirstIdx: segOffset });
                                if (matchWo && selectedWordId && selectedWordId !== matchWo.id) onClearWord && onClearWord();
                              };

                              if (isVerbalSeg) {
                                // Skip leading particles + conjunctions (هَلْ, لَمْ, وَ, …) to find the real verb index.
                                const WO_SKIP_ROLES = new Set(["هل","لم","لن","لا","ألا","أما","قد","لقد","سوف","س","إذا","إذ","لو","لما","حين","عندما","أي","كيف","أين","متى","كم","ما","إن","ثم","ماذا","بينما","حتى","و","ف"]);
                                let verbTokIdx = 0;
                                while (verbTokIdx < segTokens.length - 1 && WO_SKIP_ROLES.has(normalizeArabic(segTokens[verbTokIdx]))) verbTokIdx++;

                                // Build per-token roles: manual wo: annotation > heuristic (case ending).
                                const segRoles = new Array(segTokens.length).fill(null);
                                let mafoolSeen = false;
                                let afterPrep = false; // track if inside a prepositional phrase
                                for (let i = 0; i < segTokens.length; i++) {
                                  const ann = woAnnotations[String(segOffset + i)] || "";
                                  if (ann === "wo:fiil")   { segRoles[i] = "fiil";   afterPrep = false; continue; }
                                  if (ann === "wo:faail")  { segRoles[i] = "faail";  afterPrep = false; continue; }
                                  if (ann === "wo:mafool") { segRoles[i] = "mafool"; mafoolSeen = true; afterPrep = false; continue; }
                                  if (ann === "wo:none")   { segRoles[i] = "none";   continue; }
                                  // Heuristics
                                  if (i === verbTokIdx) { segRoles[i] = "fiil"; afterPrep = false; continue; }
                                  const t = segTokens[i];
                                  if (isPrepositionToken(t)) { afterPrep = true; continue; }
                                  // Independent pronouns are never مفعول به or فاعل by case-ending heuristic
                                  if (INDEPENDENT_PRONOUNS_SET.has(normalizeArabic(t))) { afterPrep = false; continue; }
                                  // Common adverbs ending in ـًا that are never مفعول به
                                  const WO_ADVERBS = new Set(["أيضا","جدا","أبدا","دائما","أحيانا","كثيرا","قليلا","سريعا","فورا","معا","طبعا","حقا","تماما","أولا","ثانيا","أخيرا","فعلا","مثلا","عموما","خصوصا","عادة","غالبا","نادرا","مجددا","دوما","فقط"]);
                                  if (WO_ADVERBS.has(normalizeArabic(t))) { afterPrep = false; continue; }
                                  if (/[\u064F\u064C]$/.test(t)) {
                                    segRoles[i] = "faail";
                                    afterPrep = false;
                                  } else if (!mafoolSeen && (
                                    /[\u064E\u064B]$/.test(t) ||
                                    /\u064B\u0627$/.test(t) ||
                                    // Sound feminine plural accusative (ـٍ tanwin kasra)
                                    // only label as مفعول به if not inside a prepositional phrase
                                    (!afterPrep && /\u064D$/.test(t))
                                  )) {
                                    segRoles[i] = "mafool";
                                    mafoolSeen = true;
                                    afterPrep = false;
                                  } else {
                                    afterPrep = false;
                                  }
                                }

                                segTokens.forEach((tok, i) => {
                                  const globalIdx = segOffset + i;
                                  const role = segRoles[i];
                                  const handleClickWo = makeHandleClick(tok, globalIdx);
                                  if (role === "fiil") {
                                    segElements.push(
                                      <span key={`wo-fb-${globalIdx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                        <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-rose-100 text-rose-900 hover:bg-rose-200 transition-colors" onClick={handleClickWo} style={{ border: "none" }}>{tok}</button>
                                        <span className="text-[9px] bg-rose-100 text-rose-600 border border-rose-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                      </span>
                                    );
                                  } else if (role === "faail") {
                                    segElements.push(
                                      <span key={`wo-sb-${globalIdx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                        <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors" onClick={handleClickWo} style={{ border: "none" }}>{tok}</button>
                                        <span className="text-[9px] bg-amber-100 text-amber-700 border border-amber-300 rounded px-1 font-arabic leading-none mt-0.5">فاعل</span>
                                      </span>
                                    );
                                  } else if (role === "mafool") {
                                    segElements.push(
                                      <span key={`wo-mb-${globalIdx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                        <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-teal-100 text-teal-900 hover:bg-teal-200 transition-colors" onClick={handleClickWo} style={{ border: "none" }}>{tok}</button>
                                        <span className="text-[9px] bg-teal-100 text-teal-700 border border-teal-300 rounded px-1 font-arabic leading-none mt-0.5">مفعول به</span>
                                      </span>
                                    );
                                  } else {
                                    segElements.push(renderWoToken(tok, `wo-t-${globalIdx}`, globalIdx));
                                  }
                                });
                              } else {
                                // Nominal sentence: mubtada/khabar grouping.
                                if (hasNominalRoles) {
                                  let gi = 0;
                                  while (gi < segTokens.length) {
                                    const role = segWoRoles[gi];
                                    if (!role) {
                                      segElements.push(renderWoToken(segTokens[gi], `wons${pi}-p${gi}`, segOffset + gi));
                                      segElements.push(<span key={`wons${pi}-sp${gi}`}> </span>);
                                      gi++;
                                      continue;
                                    }
                                    const groupStart = gi;
                                    const groupToks = [];
                                    while (gi < segTokens.length && segWoRoles[gi] === role) {
                                      groupToks.push(segTokens[gi]);
                                      gi++;
                                    }
                                    segElements.push(
                                      renderWoBracketGroup(groupToks, segOffset + groupStart, role, `wons${pi}-grp${groupStart}`)
                                    );
                                  }
                                } else {
                                  segTokens.forEach((tok, ti) => {
                                    segElements.push(renderWoToken(tok, `wons${pi}-t${ti}`, segOffset + ti));
                                  });
                                }
                              }

                              const segBg = isVerbalSeg
                                ? "bg-rose-50 border-rose-300"
                                : "bg-blue-50 border-blue-300";
                              const segLabelClass = isVerbalSeg
                                ? "text-[8px] text-rose-500 font-arabic whitespace-nowrap shrink-0"
                                : "text-[8px] text-blue-500 font-arabic whitespace-nowrap shrink-0";
                              renderedWo.push(
                                <span
                                  key={`woseg-${pi}`}
                                  className={`inline-flex flex-wrap items-start gap-1 rounded-md border px-2 py-1 mx-0.5 my-0.5 max-w-full ${segBg}`}
                                  dir="rtl"
                                >
                                  {segElements}
                                  <span className={segLabelClass}>{sentenceLabel}</span>
                                </span>
                              );
                              globalOffsetWo += segTokens.length;
                            }

                            return renderedWo;
                          }

                          // ── Past tense rendering ─────────────────────────────────────
                          // Verbs → rose background + pink suffix; all other tokens → standard renderToken.
                          if (pastTenseMode) {
                            return tokens.map((tok, idx) => {
                              const ann = ((turn.word_annotations || {})[String(idx)] || "");
                              const ptRole = ann.startsWith("pt:") ? ann.slice(3) : null;
                              const isVerb = ptRole === "fiil" || (ptRole === null && isVerbToken(tok));
                              const isSuppressed = ptRole === "none";

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (!isSuppressed && isVerb) {
                                const suffixInfo = detectPastTenseSuffix(tok);
                                if (suffixInfo) {
                                  const [stemPart, suffixPart] = splitTokenAtNormPos(tok, suffixInfo.normStemLength);
                                  return (
                                    <span key={`pt-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                      <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                        <span className="bg-rose-100 text-rose-900 rounded-r">{stemPart}</span><span className="bg-pink-200 text-pink-900 rounded-l">{suffixPart}</span>
                                      </button>
                                      <span className="text-[9px] bg-rose-100 text-rose-600 border border-rose-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                    </span>
                                  );
                                }
                                return (
                                  <span key={`pt-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-rose-100 text-rose-900 hover:bg-rose-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-rose-100 text-rose-600 border border-rose-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Present tense intro rendering ────────────────────────────
                          // Verbs with أَ/تَ/يَ/نَ prefix → sky prefix + rose stem + فعل badge.
                          // All other tokens → standard renderToken.
                          if (presentTenseIntroMode) {
                            return tokens.map((tok, idx) => {
                              const ann = ((turn.word_annotations || {})[String(idx)] || "");
                              const ptiRole = ann.startsWith("pti:") ? ann.slice(4) : null;
                              const isVerb = ptiRole === "fiil" || (ptiRole === null && isPresentTenseIntroToken(tok));
                              const isSuppressed = ptiRole === "none";

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (!isSuppressed && isVerb) {
                                const prefixInfo = detectPresentTensePrefix(tok);
                                if (prefixInfo) {
                                  const [prefixPart, stemPart] = splitTokenAtNormPos(tok, prefixInfo.normPrefixLength);
                                  return (
                                    <span key={`pti-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                      <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                        <span className="bg-sky-200 text-sky-900 rounded-r">{prefixPart}</span><span className="bg-rose-100 text-rose-900 rounded-l">{stemPart}</span>
                                      </button>
                                      <span className="text-[9px] bg-rose-100 text-rose-600 border border-rose-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                    </span>
                                  );
                                }
                                return (
                                  <span key={`pti-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-rose-100 text-rose-900 hover:bg-rose-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-rose-100 text-rose-600 border border-rose-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Gender (Masculine vs Feminine) rendering ──────────────
                          // Feminine words (ending with ة) → fuchsia + pink ة suffix.
                          // Masculine content words → sky background.
                          // Verbs → rose (+ pink suffix if feminine تْ form).
                          if (genderMode) {
                            return tokens.map((tok, idx) => {
                              const ann = ((turn.word_annotations || {})[String(idx)] || "");
                              const gendRole = ann.startsWith("gend:") ? ann.slice(5) : null;
                              const isVerb = isVerbToken(tok);
                              const isPrep = isPrepositionToken(tok);
                              const isAfterPrep = !isVerb && !isPrep && idx > 0 && isPrepositionToken(tokens[idx - 1]);
                              const norm = normalizeArabic(tok);
                              const isPronoun = INDEPENDENT_PRONOUNS_SET.has(norm);
                              const isParticle = GENDER_SKIP_PARTICLES.has(norm);
                              // Manual annotation overrides heuristic
                              const isSuppressed = gendRole === "none";
                              const isVerbHighlight = gendRole === "fiil" || (!gendRole && isVerb && !isSuppressed);
                              const isFeminineWord = gendRole === "feminine" || (!gendRole && !isVerb && !isPrep && !isAfterPrep && !isPronoun && !isParticle && norm.length >= 2 && norm.endsWith("ة"));
                              const isMasculineWord = gendRole === "masculine" || (!gendRole && !isVerb && !isPrep && !isAfterPrep && !isFeminineWord && !isPronoun && !isParticle && norm.length >= 2);

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              // Verbs: rose with optional pink feminine suffix (تْ)
                              if (isVerbHighlight) {
                                const suffixInfo = detectPastTenseSuffix(tok);
                                if (suffixInfo) {
                                  const [stemPart, suffixPart] = splitTokenAtNormPos(tok, suffixInfo.normStemLength);
                                  return (
                                    <span key={`gend-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                      <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                        <span className="bg-rose-100 text-rose-900 rounded-r">{stemPart}</span><span className="bg-pink-200 text-pink-900 rounded-l">{suffixPart}</span>
                                      </button>
                                      <span className="text-[9px] bg-rose-100 text-rose-600 border border-rose-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                    </span>
                                  );
                                }
                                return (
                                  <span key={`gend-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-rose-100 text-rose-900 hover:bg-rose-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-rose-100 text-rose-600 border border-rose-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              // Prepositions: indigo (only if prepositionMode is also active)
                              if (prepositionMode && isPrep) {
                                return (
                                  <span key={`gend-prep-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-indigo-100 text-indigo-900 hover:bg-indigo-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-indigo-100 text-indigo-600 border border-indigo-300 rounded px-1 font-arabic leading-none mt-0.5">حرف جر</span>
                                  </span>
                                );
                              }

                              // After preposition: orange (only if prepositionMode is also active)
                              if (prepositionMode && isAfterPrep) {
                                return (
                                  <span key={`gend-paff-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-orange-100 text-orange-900 hover:bg-orange-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-orange-100 text-orange-500 border border-orange-300 rounded px-1 font-arabic leading-none mt-0.5">بعد حرف الجر</span>
                                  </span>
                                );
                              }

                              // Feminine word (ends with ة or forced): fuchsia stem + pink ة suffix — connected like pronouns
                              if (!isSuppressed && isFeminineWord) {
                                const splitPos = norm.endsWith("ة") ? norm.length - 1 : null;
                                const [stemPart, suffixPart] = splitPos ? splitTokenAtNormPos(tok, splitPos) : [tok, null];
                                if (suffixPart) {
                                  return (
                                    <span key={`gend-fem-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                      <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                        <span className="bg-fuchsia-100 text-fuchsia-900 rounded-r">{stemPart}</span><span className="bg-pink-200 text-pink-900 rounded-l">{suffixPart}</span>
                                      </button>
                                      <span className="text-[9px] bg-fuchsia-100 text-fuchsia-600 border border-fuchsia-300 rounded px-1 font-arabic leading-none mt-0.5">مؤنث</span>
                                    </span>
                                  );
                                }
                                return (
                                  <span key={`gend-fem-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-fuchsia-100 text-fuchsia-900 hover:bg-fuchsia-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-fuchsia-100 text-fuchsia-600 border border-fuchsia-300 rounded px-1 font-arabic leading-none mt-0.5">مؤنث</span>
                                  </span>
                                );
                              }

                              // Masculine content word: sky
                              if (!isSuppressed && isMasculineWord) {
                                return (
                                  <span key={`gend-masc-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-sky-100 text-sky-900 hover:bg-sky-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-sky-100 text-sky-600 border border-sky-300 rounded px-1 font-arabic leading-none mt-0.5">مذكر</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Adjectives (النعت) rendering ──────────────────────────
                          // Noun → blue background + "اسم" badge.
                          //   Feminine noun (ends with ة): blue stem + fuchsia ة suffix.
                          // Adjective → emerald background + "نعت" badge.
                          //   Feminine adjective (ends with ة): emerald stem + pink ة suffix.
                          // Other tokens → standard rendering.
                          if (adjMode) {
                            const adjAnnotations = turn.word_annotations || {};
                            // Compute effective role for each token
                            const adjRoles = tokens.map((tok, i) => {
                              const ann = adjAnnotations[String(i)] || "";
                              if (ann.startsWith("adj:")) {
                                const role = ann.slice(4);
                                if (role === "none") return null;
                                if (role === "noun" || role === "adjective") return role;
                              }
                              // Heuristic: identify content words
                              const norm = normalizeArabic(tok);
                              const isContent = (
                                norm.length >= 2 &&
                                !isPrepositionToken(tok) &&
                                !INDEPENDENT_PRONOUNS_SET.has(norm) &&
                                !GENDER_SKIP_PARTICLES.has(norm) &&
                                !isVerbToken(tok)
                              );
                              if (!isContent) return null;
                              // Check if this could be adjective of previous content word
                              if (i > 0) {
                                const prevAnn = adjAnnotations[String(i - 1)] || "";
                                const prevSuppressed = prevAnn === "adj:none";
                                const prevTok = tokens[i - 1];
                                const prevNorm = normalizeArabic(prevTok);
                                const prevIsContent = (
                                  !prevSuppressed &&
                                  prevNorm.length >= 2 &&
                                  !isPrepositionToken(prevTok) &&
                                  !INDEPENDENT_PRONOUNS_SET.has(prevNorm) &&
                                  !GENDER_SKIP_PARTICLES.has(prevNorm) &&
                                  !isVerbToken(prevTok)
                                );
                                if (prevIsContent) {
                                  const tokDef = isDefiniteToken(tok);
                                  const prevDef = isDefiniteToken(prevTok);
                                  const tokFem = norm.endsWith("ة");
                                  const prevFem = prevNorm.endsWith("ة");
                                  // Agree in both definiteness and gender → this is an adjective
                                  if (tokDef === prevDef && tokFem === prevFem) return "adjective";
                                }
                              }
                              // Check if next token could be adjective of this (makes this a noun)
                              if (i < tokens.length - 1) {
                                const nextAnn = adjAnnotations[String(i + 1)] || "";
                                const nextSuppressed = nextAnn === "adj:none";
                                const nextTok = tokens[i + 1];
                                const nextNorm = normalizeArabic(nextTok);
                                const nextIsContent = (
                                  !nextSuppressed &&
                                  nextNorm.length >= 2 &&
                                  !isPrepositionToken(nextTok) &&
                                  !INDEPENDENT_PRONOUNS_SET.has(nextNorm) &&
                                  !GENDER_SKIP_PARTICLES.has(nextNorm) &&
                                  !isVerbToken(nextTok)
                                );
                                if (nextIsContent) {
                                  const tokDef = isDefiniteToken(tok);
                                  const nextDef = isDefiniteToken(nextTok);
                                  const tokFem = norm.endsWith("ة");
                                  const nextFem = nextNorm.endsWith("ة");
                                  // Agree in both definiteness and gender → this is a noun
                                  if (tokDef === nextDef && tokFem === nextFem) return "noun";
                                }
                              }
                              return null;
                            });

                            return tokens.map((tok, idx) => {
                              const role = adjRoles[idx];
                              const norm = normalizeArabic(tok);
                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (role === "noun") {
                                // Feminine noun: blue stem + fuchsia ة suffix
                                const isFem = norm.endsWith("ة") && norm.length >= 2;
                                if (isFem) {
                                  const [stemPart, suffixPart] = splitTokenAtNormPos(tok, norm.length - 1);
                                  if (suffixPart) {
                                    return (
                                      <span key={`adj-noun-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                        <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                          <span className="bg-blue-100 text-blue-900 rounded-r">{stemPart}</span><span className="bg-fuchsia-200 text-fuchsia-900 rounded-l">{suffixPart}</span>
                                        </button>
                                        <span className="text-[9px] bg-blue-100 text-blue-600 border border-blue-300 rounded px-1 font-arabic leading-none mt-0.5">اسم</span>
                                      </span>
                                    );
                                  }
                                }
                                return (
                                  <span key={`adj-noun-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-blue-100 text-blue-900 hover:bg-blue-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-blue-100 text-blue-600 border border-blue-300 rounded px-1 font-arabic leading-none mt-0.5">اسم</span>
                                  </span>
                                );
                              }

                              if (role === "adjective") {
                                // Feminine adjective: emerald stem + pink ة suffix
                                const isFem = norm.endsWith("ة") && norm.length >= 2;
                                if (isFem) {
                                  const [stemPart, suffixPart] = splitTokenAtNormPos(tok, norm.length - 1);
                                  if (suffixPart) {
                                    return (
                                      <span key={`adj-adj-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                        <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                          <span className="bg-emerald-100 text-emerald-900 rounded-r">{stemPart}</span><span className="bg-pink-200 text-pink-900 rounded-l">{suffixPart}</span>
                                        </button>
                                        <span className="text-[9px] bg-emerald-100 text-emerald-600 border border-emerald-300 rounded px-1 font-arabic leading-none mt-0.5">نعت</span>
                                      </span>
                                    );
                                  }
                                }
                                return (
                                  <span key={`adj-adj-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-emerald-100 text-emerald-900 hover:bg-emerald-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-emerald-100 text-emerald-600 border border-emerald-300 rounded px-1 font-arabic leading-none mt-0.5">نعت</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Idafa / Possession (الإضافة) rendering ────────────────
                          // مضاف (first noun / possessed) → blue background + "مضاف" badge.
                          // مضاف إليه (second noun / owner) → amber background + "مضاف إليه" badge.
                          // Detection: [noun without ال] + [definite noun with ال OR proper noun]
                          // The first noun must NOT start with ال; the second noun usually starts
                          // with ال (definite) or is a proper noun ending with tanween kasra (ٍ).
                          // Adjective distinction: adjective pairs agree in both definiteness AND
                          // gender — Idafa pairs do NOT agree in definiteness (first is indefinite,
                          // second is definite).
                          if (idafaMode) {
                            const idafaAnnotations = turn.word_annotations || {};

                            // Compute effective Idafa role for each token.
                            // Priority: manual annotation > heuristic.
                            const idafaRoles = tokens.map((tok, i) => {
                              const ann = idafaAnnotations[String(i)] || "";
                              if (ann.startsWith("idafa:")) {
                                const role = ann.slice(6);
                                if (role === "none") return null;
                                if (role === "mudaf" || role === "mudaf_ilaih") return role;
                                // dual_no_suffix: keep dual role but suffix badge will be suppressed at render
                                if (role === "dual_no_suffix") return "mudaf_ilaih_and_mudaf";
                              }

                              // Heuristic: detect [indefinite content noun] + [definite content noun]
                              const norm = normalizeArabic(tok);
                              const isContent = (
                                norm.length >= 2 &&
                                !isPrepositionToken(tok) &&
                                !INDEPENDENT_PRONOUNS_SET.has(norm) &&
                                !GENDER_SKIP_PARTICLES.has(norm) &&
                                !isVerbToken(tok)
                              );
                              if (!isContent) return null;

                              const tokDef = isDefiniteForIdafa(tok);

                              // Check if this is مضاف إليه: previous token is indefinite content noun
                              if (i > 0) {
                                const prevAnn = idafaAnnotations[String(i - 1)] || "";
                                const prevSuppressed = prevAnn === "idafa:none";
                                const prevTok = tokens[i - 1];
                                const prevNorm = normalizeArabic(prevTok);
                                const prevIsContent = (
                                  !prevSuppressed &&
                                  prevNorm.length >= 2 &&
                                  !isPrepositionToken(prevTok) &&
                                  !INDEPENDENT_PRONOUNS_SET.has(prevNorm) &&
                                  !GENDER_SKIP_PARTICLES.has(prevNorm) &&
                                  !isVerbToken(prevTok)
                                );
                                const prevDef = isDefiniteForIdafa(prevTok);
                                // Idafa: prev is indefinite, current is definite → current is مضاف إليه
                                // Adjective: both same definiteness → skip
                                if (prevIsContent && !prevDef && tokDef) {
                                  // If definiteness comes from an attached pronoun, this token is
                                  // simultaneously مضاف (possessed) with the pronoun as مضاف إليه.
                                  const hasPronoun = !isDefiniteToken(tok) && detectAttachedPronoun(norm) !== null;
                                  return hasPronoun ? "mudaf_ilaih_and_mudaf" : "mudaf_ilaih";
                                }
                              }

                              // Check if this is مضاف: next token is definite content noun
                              if (i < tokens.length - 1) {
                                const nextAnn = idafaAnnotations[String(i + 1)] || "";
                                const nextSuppressed = nextAnn === "idafa:none";
                                const nextTok = tokens[i + 1];
                                const nextNorm = normalizeArabic(nextTok);
                                const nextIsContent = (
                                  !nextSuppressed &&
                                  nextNorm.length >= 2 &&
                                  !isPrepositionToken(nextTok) &&
                                  !INDEPENDENT_PRONOUNS_SET.has(nextNorm) &&
                                  !GENDER_SKIP_PARTICLES.has(nextNorm) &&
                                  !isVerbToken(nextTok)
                                );
                                const nextDef = isDefiniteForIdafa(nextTok);
                                // Idafa: current is indefinite, next is definite → current is مضاف
                                if (!tokDef && nextIsContent && nextDef) return "mudaf";
                              }

                              return null;
                            });

                            return tokens.map((tok, idx) => {
                              const role = idafaRoles[idx];
                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (role === "mudaf") {
                                return (
                                  <span key={`idafa-mudaf-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-blue-50 text-blue-900 border border-blue-300 hover:bg-blue-100 transition-colors" onClick={handleClick}>{tok}</button>
                                    <span className="text-[9px] bg-blue-50 text-blue-600 border border-blue-300 rounded px-1 font-arabic leading-none mt-0.5">مضاف</span>
                                  </span>
                                );
                              }

                                 if (role === "mudaf_ilaih_and_mudaf") {
                                   // Single element — no DOM split, no iOS hairline gap.
                                   // Arabic is RTL so the suffix (كَ) is visually on the LEFT of the token.
                                   // We compute suffixPct ≈ suffix chars / total chars and apply a hard-stop
                                   // inline gradient: amber [LEFT=suffix] | blue→amber [RIGHT=stem dual role].
                                   const pronoun = detectAttachedPronoun(normalizeArabic(tok));
                                   const norm = normalizeArabic(tok);
                                   const suffixPct = pronoun && norm.length > 0
                                     ? Math.round((pronoun.suffix.length / norm.length) * 100)
                                     : 0;
                                   const PUNC_RE = /[\u060C\u061B\u061F\u06D4!?.،,؟]+$/;
                                   const trailingPunc = tok.match(PUNC_RE)?.[0] ?? "";
                                   const tokClean = trailingPunc ? tok.slice(0, tok.length - trailingPunc.length) : tok;
                                   const bgStyle = suffixPct > 0
                                     ? { background: `linear-gradient(to right, #fef3c7 ${suffixPct}%, #dbeafe ${suffixPct}%, #fef3c7 100%)` }
                                     : { background: "linear-gradient(to right, #dbeafe, #fef3c7)" };
                                   const suffixLabel = pronoun
                                     ? splitTokenAtNormPos(tokClean, pronoun.normStemLength)[1].replace(PUNC_RE, "")
                                     : null;
                                   const suppressSuffixBadge = (idafaAnnotations[String(idx)] || "") === "idafa:dual_no_suffix";
                                   return (
                                     <span key={`idafa-dual-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                       <span className="flex items-baseline">
                                         <button type="button" className="cursor-pointer px-1 py-0.5 rounded text-slate-900 border border-amber-300 hover:opacity-80 transition-opacity" onClick={handleClick} style={bgStyle}>{tokClean}</button>
                                         {trailingPunc && <span className="text-slate-700 ml-0.5">{trailingPunc}</span>}
                                       </span>
                                       <span className="flex flex-col items-center gap-0.5 mt-0.5">
                                         <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-300 rounded px-0.5 font-arabic leading-none">مضاف إليه</span>
                                         <span className="text-[9px] bg-blue-50 text-blue-600 border border-blue-300 rounded px-0.5 font-arabic leading-none">مضاف</span>
                                         {suffixLabel && !suppressSuffixBadge && (
                                           <span className="text-[9px] bg-amber-50 text-amber-700 border border-dashed border-amber-400 rounded px-0.5 font-arabic leading-none">{suffixLabel} مضاف إليه</span>
                                         )}
                                       </span>
                                     </span>
                                   );
                                 }

                              if (role === "mudaf_ilaih") {
                                return (
                                  <span key={`idafa-ilaih-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-300 hover:bg-amber-100 transition-colors" onClick={handleClick}>{tok}</button>
                                    <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-300 rounded px-1 font-arabic leading-none mt-0.5">مضاف إليه</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Negation (النفي) rendering ────────────────────────────────
                          // لا / لَيْسَ / مَا / لَمْ (before a verb) → red background + نفي badge.
                          // All other tokens → standard renderToken.
                          if (negationMode) {
                            const negAnnotations = turn.word_annotations || {};
                            return tokens.map((tok, idx) => {
                              const ann = (negAnnotations[String(idx)] || "");
                              const negRole = ann.startsWith("neg:") ? ann.slice(4) : null;
                              const nextTok = tokens[idx + 1];
                              const isSuppressed = negRole === "none";
                              const isParticle = !isSuppressed && (negRole === "particle" || (negRole === null && isNegationParticle(tok, nextTok)));

                              // Grammatical effect: manual annotation takes priority, then heuristic.
                              // لَمْ  → following verb = مجزوم (jussive)
                              // لَيْسَ → following word = منصوب (accusative)
                              let negEffect = null;
                              if (!isSuppressed && !isParticle) {
                                if (negRole === "majzoom") negEffect = "مجزوم";
                                else if (negRole === "mansuub") negEffect = "منصوب";
                                else if (negRole === null) {
                                  const prevTokRaw = tokens[idx - 1];
                                  if (prevTokRaw) {
                                    const prevAnn = (negAnnotations[String(idx - 1)] || "");
                                    const prevNegRole = prevAnn.startsWith("neg:") ? prevAnn.slice(4) : null;
                                    const prevIsParticle = prevNegRole === "particle" || (prevNegRole === null && isNegationParticle(prevTokRaw, tok));
                                    if (prevIsParticle) {
                                      const prevNorm = normalizeArabic(prevTokRaw.trim());
                                      if (prevNorm === NEGATION_LAM) negEffect = "مجزوم";
                                      else if (LAYSA_FORMS.has(prevNorm) && isVisiblyMansuub(tok)) negEffect = "منصوب";
                                    }
                                  }
                                }
                              }

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (isParticle) {
                                return (
                                  <span key={`neg-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-red-100 text-red-900 hover:bg-red-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-red-100 text-red-600 border border-red-300 rounded px-1 font-arabic leading-none mt-0.5">نفي</span>
                                  </span>
                                );
                              }

                              if (negEffect) {
                                return (
                                  <span key={`neg-effect-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-orange-100 text-orange-900 hover:bg-orange-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-orange-100 text-orange-700 border border-orange-300 rounded px-1 font-arabic leading-none mt-0.5">{negEffect}</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Question Words (أدوات الاستفهام) rendering ──────────────
                          // مَنْ / مَاذَا / أَيْنَ / مَتَى / لِمَاذَا / كَيْفَ → yellow background + أداة badge.
                          // All other tokens → standard renderToken.
                          if (questionWordsMode) {
                            const qwAnnotations = turn.word_annotations || {};
                            return tokens.map((tok, idx) => {
                              const ann = (qwAnnotations[String(idx)] || "");
                              const qwRole = ann.startsWith("qw:") ? ann.slice(3) : null;
                              const isSuppressed = qwRole === "none";
                              const isQW = !isSuppressed && (qwRole === "question_word" || (qwRole === null && isQuestionWord(tok)));

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (isQW) {
                                return (
                                  <span key={`qw-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-yellow-100 text-yellow-900 hover:bg-yellow-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-yellow-100 text-yellow-700 border border-yellow-300 rounded px-1 font-arabic leading-none mt-0.5">أداة</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Connectors (الرَّوَابِط) rendering ─────────────────────
                          // وَ → gray, ثُمَّ → green, لَكِنْ → red, لِأَنَّ → blue.
                          // All other tokens → standard renderToken.
                          if (connectorsMode) {
                            const conAnnotations = turn.word_annotations || {};
                            return tokens.map((tok, idx) => {
                              const ann = (conAnnotations[String(idx)] || "");
                              const conRole = ann.startsWith("con:") ? ann.slice(4) : null;
                              const isSuppressed = conRole === "none";
                              const heuristic = isSuppressed ? null : getConnectorType(tok);
                              // If annotation forces "wa" on a prefix token (e.g. وَأَقْرَأُ),
                              // use split rendering so only the وَ prefix is highlighted gray.
                              const forcedWa = conRole === "wa"
                                ? (normalizeArabic(tok.trim()) !== CONNECTOR_WA ? "wa_prefix" : "wa")
                                : null;
                              const activeType = isSuppressed ? null : (
                                forcedWa ||
                                (conRole === "thumma" ? "thumma" : null) ||
                                (conRole === "lakin" ? "lakin" : null) ||
                                (conRole === "lianna" ? "lianna" : null) ||
                                heuristic
                              );

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (activeType === "wa") {
                                return (
                                  <span key={`con-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-gray-200 text-gray-900 hover:bg-gray-300 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-gray-200 text-gray-700 border border-gray-300 rounded px-1 font-arabic leading-none mt-0.5">وَ</span>
                                  </span>
                                );
                              }
                              if (activeType === "wa_prefix") {
                                // وَ is a clitic prefix on a longer token (e.g. وَأَقْرَأُ).
                                // Split at normalized position 1 (the single waw letter), show
                                // the وَ prefix in gray and the stem in plain styling.
                                const [prefixPart, stemPart] = splitTokenAtNormPos(tok, 1);
                                return (
                                  <span key={`con-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                      <span className="px-1 bg-gray-200 text-gray-900 rounded-r">{prefixPart}</span><span className="px-1 bg-white text-gray-900 border border-gray-200 rounded-l">{stemPart}</span>
                                    </button>
                                    <span className="text-[9px] bg-gray-200 text-gray-700 border border-gray-300 rounded px-1 font-arabic leading-none mt-0.5">وَ</span>
                                  </span>
                                );
                              }
                              if (activeType === "thumma") {
                                return (
                                  <span key={`con-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">ثُمَّ</span>
                                  </span>
                                );
                              }
                              if (activeType === "lakin") {
                                return (
                                  <span key={`con-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-red-100 text-red-900 hover:bg-red-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-red-100 text-red-600 border border-red-300 rounded px-1 font-arabic leading-none mt-0.5">لَكِنْ</span>
                                  </span>
                                );
                              }
                              if (activeType === "lianna") {
                                return (
                                  <span key={`con-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-blue-100 text-blue-900 hover:bg-blue-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-blue-100 text-blue-600 border border-blue-300 rounded px-1 font-arabic leading-none mt-0.5">لِأَنَّ</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Verb + Object (الفعل والمفعول به) rendering ──────────────────────
                          // Verb tokens → green (bg-green-100), object tokens → sky (bg-sky-100).
                          // Object detection: content words following a verb, until a preposition,
                          // connector, particle, pronoun, or another verb is encountered.
                          if (verbObjectMode) {
                            const voAnnotations = turn.word_annotations || {};

                            // Pass 1: collect explicit annotations and mark verbs from heuristic.
                            const explicitRoles = tokens.map((tok, idx) => {
                              const ann = voAnnotations[String(idx)] || "";
                              if (ann.startsWith("vo:")) {
                                const role = ann.slice(3);
                                if (role === "none") return "none";
                                if (role === "verb") return "verb";
                                if (role === "object") return "object";
                                if (role === "mudaf") return "mudaf";
                                if (role === "mudaf_ilaih") return "mudaf_ilaih";
                                if (role === "object_mudaf") return "object_mudaf";
                              }
                              if (isVerbToken(tok)) return "verb";
                              return null;
                            });

                            // Pass 2: for tokens with null role, mark as object if after a verb
                            // and not a preposition, connector, particle, or pronoun.
                            const voRoles = [...explicitRoles];
                            let inObjectPhrase = false;
                            for (let i = 0; i < tokens.length; i++) {
                              const tok = tokens[i];
                              const norm = normalizeArabic(tok.trim());
                              const explicit = explicitRoles[i];

                              if (explicit === "verb") {
                                inObjectPhrase = true;
                                continue;
                              }
                              if (explicit === "none" || explicit === "object" || explicit === "mudaf" || explicit === "mudaf_ilaih" || explicit === "object_mudaf") {
                                continue;
                              }
                              // explicit is null — apply heuristic
                              const isPrep = isPrepositionToken(tok);
                              const isParticle = GENDER_SKIP_PARTICLES.has(norm);
                              const isPronoun = INDEPENDENT_PRONOUNS_SET.has(norm);
                              const isConnector = getConnectorType(tok) !== null;

                              if (isPrep || isConnector) {
                                inObjectPhrase = false;
                              } else if (inObjectPhrase && !isParticle && !isPronoun && norm.length >= 2) {
                                voRoles[i] = "object";
                              } else if (isParticle || isPronoun) {
                                inObjectPhrase = false;
                              }
                            }

                            return tokens.map((tok, idx) => {
                              const role = voRoles[idx];
                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (role === "verb") {
                                return (
                                  <span key={`vo-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }
                              if (role === "object") {
                                return (
                                  <span key={`vo-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-sky-100 text-sky-900 hover:bg-sky-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-sky-100 text-sky-700 border border-sky-300 rounded px-1 font-arabic leading-none mt-0.5">مفعول به</span>
                                  </span>
                                );
                              }
                              if (role === "mudaf") {
                                return (
                                  <span key={`vo-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-blue-100 text-blue-900 hover:bg-blue-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-blue-100 text-blue-700 border border-blue-300 rounded px-1 font-arabic leading-none mt-0.5">مضاف</span>
                                  </span>
                                );
                              }
                              if (role === "mudaf_ilaih") {
                                return (
                                  <span key={`vo-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-amber-100 text-amber-700 border border-amber-300 rounded px-1 font-arabic leading-none mt-0.5">مضاف إليه</span>
                                  </span>
                                );
                              }
                              if (role === "object_mudaf") {
                                return (
                                  <span key={`vo-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-sky-100 text-sky-900 hover:bg-sky-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-sky-100 text-sky-700 border border-sky-300 rounded px-1 font-arabic leading-none mt-0.5">مفعول به</span>
                                    <span className="text-[9px] bg-blue-100 text-blue-700 border border-blue-300 rounded px-1 font-arabic leading-none mt-0.5">مضاف</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Future Tense (الْمُسْتَقْبَل) rendering ──────────────────────────────
                          // Future markers:
                          //   • سَوْفَ (standalone) → purple (bg-purple-100) + "سوف" badge.
                          //   • سَـ prefix on a present verb (e.g. سَأَذْهَبُ) → split rendering:
                          //     سَ in purple (rounded-r) + verb stem in green (rounded-l).
                          // Verbs after سَوْفَ → green (bg-green-100) + "فعل" badge.
                          // Annotation prefix: "ft:" with values sawfa | sa_prefix | verb | none.
                          if (futureTenseMode) {
                            const ftAnnotations = turn.word_annotations || {};

                            // Pass 1: resolve explicit annotations and auto-detect roles.
                            const ftRoles = tokens.map((tok, idx) => {
                              const ann = ftAnnotations[String(idx)] || "";
                              if (ann.startsWith("ft:")) {
                                const role = ann.slice(3);
                                if (role === "none") return "none";
                                if (role === "sawfa") return "sawfa";
                                if (role === "sa_prefix") return "sa_prefix";
                                if (role === "verb") return "verb";
                              }
                              return getFutureTenseRole(tok);
                            });

                            // Pass 2: mark present-tense verbs immediately after سَوْفَ as "sawfa_verb".
                            const finalFtRoles = [...ftRoles];
                            for (let i = 0; i < tokens.length - 1; i++) {
                              if (ftRoles[i] === "sawfa" && ftRoles[i + 1] === null) {
                                if (isVerbToken(tokens[i + 1])) {
                                  finalFtRoles[i + 1] = "sawfa_verb";
                                }
                              }
                            }

                            return tokens.map((tok, idx) => {
                              const role = finalFtRoles[idx];
                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (role === "sawfa") {
                                return (
                                  <span key={`ft-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-purple-100 text-purple-900 hover:bg-purple-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-purple-100 text-purple-700 border border-purple-300 rounded px-1 font-arabic leading-none mt-0.5">سوف</span>
                                  </span>
                                );
                              }
                              if (role === "sa_prefix") {
                                // Split rendering: سَ (purple, rounded-r) + verb stem (green, rounded-l).
                                // No px on inner spans — button wrapper provides horizontal padding.
                                // This matches the past/present-tense split pattern and avoids the
                                // iOS Safari gap that appears when inner spans carry their own px.
                                const [prefixPart, stemPart] = splitTokenAtNormPos(tok, 1);
                                return (
                                  <span key={`ft-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                      <span className="bg-purple-100 text-purple-900 rounded-r">{prefixPart}</span><span className="bg-green-100 text-green-900 rounded-l">{stemPart}</span>
                                    </button>
                                    <span className="text-[9px] bg-purple-100 text-purple-700 border border-purple-300 rounded px-1 font-arabic leading-none mt-0.5">سَـ + فعل</span>
                                  </span>
                                );
                              }
                              if (role === "sawfa_verb" || role === "verb") {
                                return (
                                  <span key={`ft-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Object Pronouns (Basic) rendering ────────────────────────────────────
                          // Present-tense verbs with attached object pronouns ـهُ / ـهَا / ـنِي:
                          //   • Verb with pronoun suffix → split rendering:
                          //     verb stem in green (rounded-r) + pronoun suffix in teal (rounded-l)
                          //     + teal label badge showing the suffix.
                          //   • Plain verb (no detected pronoun suffix) → green + "فعل" badge.
                          //   • Other tokens → standard renderToken.
                          // Annotation prefix: "op:" with values fiil | mafool | none (suppress) or auto (reset).
                          if (objectPronounsBasicMode) {
                            return tokens.map((tok, idx) => {
                              const ann = (turn.word_annotations || {})[String(idx)] || "";
                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              // Suppressed by admin annotation
                              if (ann === "op:none") return renderToken(tok, idx, idx);

                              // Admin-forced plain verb
                              if (ann === "op:fiil") {
                                return (
                                  <span key={`op-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              // Admin-forced mafool bihi (split green+teal)
                              if (ann === "op:mafool") {
                                const forced = getObjectPronounSuffixForced(tok);
                                if (forced) {
                                  const [stemPart, suffixPart] = splitTokenAtNormPos(tok, forced.normStemLength);
                                  return (
                                    <span key={`op-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                      <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                        <span className="bg-green-100 text-green-900 rounded-r">{stemPart}</span><span className="bg-teal-200 text-teal-900 rounded-l">{suffixPart}</span>
                                      </button>
                                      <span className="text-[9px] bg-teal-100 text-teal-700 border border-teal-300 rounded px-1 font-arabic leading-none mt-0.5">{forced.label}</span>
                                    </span>
                                  );
                                }
                                // Fallback if no recognisable suffix — render whole token as green verb
                                return (
                                  <span key={`op-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              // Heuristic: verb with attached object pronoun suffix → split
                              const opInfo = detectObjectPronounBasic(tok);

                              if (opInfo) {
                                // Split rendering: verb stem (green, rounded-r) + pronoun suffix (teal, rounded-l)
                                const [stemPart, suffixPart] = splitTokenAtNormPos(tok, opInfo.normStemLength);
                                return (
                                  <span key={`op-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                      <span className="bg-green-100 text-green-900 rounded-r">{stemPart}</span><span className="bg-teal-200 text-teal-900 rounded-l">{suffixPart}</span>
                                    </button>
                                    <span className="text-[9px] bg-teal-100 text-teal-700 border border-teal-300 rounded px-1 font-arabic leading-none mt-0.5">{opInfo.label}</span>
                                  </span>
                                );
                              }

                              // Plain verb (no pronoun suffix) → green highlight
                              if (isVerbToken(tok)) {
                                return (
                                  <span key={`op-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // Object Pronouns (Expanded) mode: same rendering as Basic but with 6 suffixes.
                          // Annotation prefix: "op:" with values fiil | mafool | none | auto (reset).
                          if (objectPronounsExpandedMode) {
                            return tokens.map((tok, idx) => {
                              const ann = (turn.word_annotations || {})[String(idx)] || "";
                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (ann === "op:none") return renderToken(tok, idx, idx);

                              if (ann === "op:fiil") {
                                return (
                                  <span key={`ope-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              if (ann === "op:mafool") {
                                const forced = getObjectPronounSuffixExpandedForced(tok);
                                if (forced) {
                                  const [stemPart, suffixPart] = splitTokenAtNormPos(tok, forced.normStemLength);
                                  return (
                                    <span key={`ope-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                      <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                        <span className="bg-green-100 text-green-900 rounded-r">{stemPart}</span><span className="bg-teal-200 text-teal-900 rounded-l">{suffixPart}</span>
                                      </button>
                                      <span className="text-[9px] bg-teal-100 text-teal-700 border border-teal-300 rounded px-1 font-arabic leading-none mt-0.5">{forced.label}</span>
                                    </span>
                                  );
                                }
                                return (
                                  <span key={`ope-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              const opeInfo = detectObjectPronounExpanded(tok);

                              if (opeInfo) {
                                const [stemPart, suffixPart] = splitTokenAtNormPos(tok, opeInfo.normStemLength);
                                return (
                                  <span key={`ope-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                      <span className="bg-green-100 text-green-900 rounded-r">{stemPart}</span><span className="bg-teal-200 text-teal-900 rounded-l">{suffixPart}</span>
                                    </button>
                                    <span className="text-[9px] bg-teal-100 text-teal-700 border border-teal-300 rounded px-1 font-arabic leading-none mt-0.5">{opeInfo.label}</span>
                                  </span>
                                );
                              }

                              if (isVerbToken(tok)) {
                                return (
                                  <span key={`ope-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-green-100 text-green-900 hover:bg-green-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-green-100 text-green-700 border border-green-300 rounded px-1 font-arabic leading-none mt-0.5">فعل</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Comparatives (اسم التفضيل) rendering ─────────────────────────────────
                          // Comparative words (أفعل pattern) → pink (bg-pink-100 text-pink-900) + اسم تفضيل badge.
                          // مِنْ/مِنَ following a comparative → indigo (bg-indigo-100 text-indigo-900) + مِنْ badge.
                          // Annotation prefix: "comp:" with values comparative | min | none (suppress) or auto (reset).
                          if (comparativesMode) {
                            const compAnnotations = turn.word_annotations || {};

                            // Pass 1: detect comparative tokens and مِنْ following them.
                            const compRoles = tokens.map((tok, idx) => {
                              const ann = compAnnotations[String(idx)] || "";
                              if (ann.startsWith("comp:")) {
                                const role = ann.slice(5);
                                if (role === "none") return "none";
                                if (role === "comparative") return "comparative";
                                if (role === "min") return "min";
                              }
                              return null; // auto-detect in Pass 2
                            });

                            // Pass 2: auto-detect roles for unannotated tokens.
                            const finalCompRoles = compRoles.map((role, idx) => {
                              if (role !== null) return role;
                              if (isComparativeToken(tokens[idx])) return "comparative";
                              const norm = normalizeArabic(tokens[idx]);
                              if (norm === "من") {
                                // Highlight مِنْ only when preceded by a comparative
                                if (idx > 0) {
                                  const prevRole = compRoles[idx - 1] !== null
                                    ? compRoles[idx - 1]
                                    : (isComparativeToken(tokens[idx - 1]) ? "comparative" : null);
                                  if (prevRole === "comparative") return "min";
                                }
                              }
                              return null;
                            });

                            return tokens.map((tok, idx) => {
                              const role = finalCompRoles[idx];
                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (role === "comparative") {
                                return (
                                  <span key={`comp-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-pink-100 text-pink-900 hover:bg-pink-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-pink-100 text-pink-700 border border-pink-300 rounded px-1 font-arabic leading-none mt-0.5">تفضيل</span>
                                  </span>
                                );
                              }
                              if (role === "min") {
                                return (
                                  <span key={`comp-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-indigo-100 text-indigo-900 hover:bg-indigo-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-indigo-100 text-indigo-700 border border-indigo-300 rounded px-1 font-arabic leading-none mt-0.5">مِنْ</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Dual Forms (المثنى) rendering ──────────────────────────────────────────
                          // Dual nouns ending in ـانِ or ـيْنِ → orange stem + orange suffix + مثنى badge.
                          // Split rendering: stem (bg-orange-100) + suffix (bg-orange-200, rounded-l).
                          // Annotation prefix: "df:" with values "dual" | "dual_adj" | "none" or "auto" (reset).
                          //
                          // Two-pass detection:
                          //   Pass 1 — resolve manual annotations and auto-detect all dual tokens.
                          //   Pass 2 — promote auto-detected dual tokens that immediately follow a dual noun
                          //            (with no verb / connector / preposition gap) to "dual_adj", so that
                          //            adjective agreement (e.g. طَبَقَانِ لَذِيذَانِ) can be visualised with
                          //            stacked مثنى + نعت badges.
                          if (dualFormsMode) {
                            const dfAnnotations = turn.word_annotations || {};

                            // Pass 1: build rawRoles array.
                            // Values: "dual" | "dual_adj" | "none" | "auto_dual" | null
                            const rawRoles = tokens.map((tok, idx) => {
                              const ann = dfAnnotations[String(idx)] || "";
                              if (ann.startsWith("df:")) {
                                const role = ann.slice(3);
                                if (role === "none") return "none";
                                if (role === "dual") return "dual";
                                if (role === "dual_adj") return "dual_adj";
                              }
                              return isDualFormToken(tok) ? "auto_dual" : null;
                            });

                            // Pass 2: promote auto_dual tokens that follow a dual noun with no
                            // verb / connector / preposition between them to "dual_adj".
                            const finalRoles = rawRoles.slice();
                            let lastDualNounIdx = -1;
                            for (let i = 0; i < tokens.length; i++) {
                              const role = rawRoles[i];
                              if (role === "none") { lastDualNounIdx = -1; continue; }
                              if (role === "dual") { lastDualNounIdx = i; continue; }
                              if (role === "dual_adj") { /* manual override — don't change head */ continue; }
                              if (role === "auto_dual") {
                                if (lastDualNounIdx >= 0) {
                                  // If this token starts with وَ clitic it is a new coordinated noun, not an adjective.
                                  let broken = normalizeArabic(tokens[i]).startsWith("\u0648");
                                  if (!broken) {
                                    for (let j = lastDualNounIdx + 1; j < i && !broken; j++) {
                                      if (rawRoles[j] !== null) { broken = true; break; }
                                      if (isVerbToken(tokens[j]) || isPrepositionToken(tokens[j]) || getConnectorType(tokens[j]) !== null) broken = true;
                                    }
                                  }
                                  finalRoles[i] = broken ? "dual" : "dual_adj";
                                  if (broken) lastDualNounIdx = i;
                                } else {
                                  finalRoles[i] = "dual";
                                  lastDualNounIdx = i;
                                }
                                continue;
                              }
                              // Non-dual token — reset chain if it is a hard boundary.
                              if (isVerbToken(tokens[i]) || getConnectorType(tokens[i]) !== null) lastDualNounIdx = -1;
                            }

                            return tokens.map((tok, idx) => {
                              const role = finalRoles[idx];
                              const isSuppressed = role === "none";
                              const isDual = role === "dual" || role === "auto_dual" || role === "dual_adj";

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (!isSuppressed && isDual) {
                                const isDualAdj = role === "dual_adj";
                                const suffixInfo = detectDualFormSuffix(tok);
                                if (suffixInfo) {
                                  const [stemPart, suffixPart] = splitTokenAtNormPos(tok, suffixInfo.normStemLength);
                                  return (
                                    <span key={`df-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                      <button type="button" className="cursor-pointer py-0.5 px-0.5 rounded hover:opacity-80 transition-opacity" onClick={handleClick} style={{ border: "none" }}>
                                        <span className="bg-orange-100 text-orange-900 rounded-r">{stemPart}</span><span className="bg-orange-200 text-orange-900 rounded-l">{suffixPart}</span>
                                      </button>
                                      <span className="text-[9px] bg-orange-100 text-orange-700 border border-orange-300 rounded px-1 font-arabic leading-none mt-0.5">مثنى</span>
                                      {isDualAdj && <span className="text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-300 rounded px-1 font-arabic leading-none mt-0.5">نعت</span>}
                                    </span>
                                  );
                                }
                                // Fallback: no detectable suffix boundary — highlight whole token.
                                return (
                                  <span key={`df-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-orange-100 text-orange-900 hover:bg-orange-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-orange-100 text-orange-700 border border-orange-300 rounded px-1 font-arabic leading-none mt-0.5">مثنى</span>
                                    {isDualAdj && <span className="text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-300 rounded px-1 font-arabic leading-none mt-0.5">نعت</span>}
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Broken Plurals (جمع التكسير) rendering ────────────────────────────────
                          // Data-driven: singular forms (sky blue) and their broken plurals (rose).
                          // Detection uses singularByNorm and pluralsByNorm lookup maps built from
                          // topic.words plural relationships — no heuristics or pattern matching.
                          // Annotation prefix: "bp:" with values "plural" | "singular" | "none" or "auto" (reset).
                          if (brokenPluralsMode) {
                            const bpAnnotations = turn.word_annotations || {};

                            return tokens.map((tok, idx) => {
                              const normTok = normalizeArabic(tok);
                              const ann = bpAnnotations[String(idx)] || "";

                              // Resolve annotation or fall back to data-driven detection.
                              let role = null;
                              if (ann.startsWith("bp:")) {
                                const annRole = ann.slice(3);
                                if (annRole === "none") role = "none";
                                else if (annRole === "plural") role = "plural";
                                else if (annRole === "singular") role = "singular";
                              } else {
                                const isFuncWord = !normTok
                                  || INDEPENDENT_PRONOUNS_SET.has(normTok)
                                  || GENDER_SKIP_PARTICLES.has(normTok)
                                  || isPrepositionToken(tok);
                                if (!isFuncWord) {
                                  if (singularByNorm[normTok]) role = "plural";
                                  else if (pluralsByNorm[normTok]) role = "singular";
                                }
                              }

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (role === "plural") {
                                const singularWord = normTok ? singularByNorm[normTok] : null;
                                return (
                                  <span key={`bp-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-rose-100 text-rose-900 hover:bg-rose-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-rose-100 text-rose-700 border border-rose-300 rounded px-1 font-arabic leading-none mt-0.5">جمع</span>
                                    {singularWord && (
                                      <span className="text-[9px] text-slate-400 leading-none mt-0.5 font-arabic" aria-label={`singular: ${singularWord.text}`}>← {singularWord.text}</span>
                                    )}
                                  </span>
                                );
                              }

                              if (role === "singular") {
                                const pluralForms = normTok ? (pluralsByNorm[normTok] || []) : [];
                                return (
                                  <span key={`bp-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-sky-100 text-sky-900 hover:bg-sky-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-sky-100 text-sky-700 border border-sky-300 rounded px-1 font-arabic leading-none mt-0.5">مفرد</span>
                                    {pluralForms.length > 0 && (
                                      <span className="text-[9px] text-slate-400 leading-none mt-0.5 font-arabic" aria-label={`plural: ${pluralForms[0]}`}>→ {pluralForms[0]}</span>
                                    )}
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // ── Imperatives (فعل الأمر) rendering ────────────────────────────────
                          // Detection: data-driven lookup from s.imperative on verb senses,
                          // with heuristic fallback (isImperativeToken: ends with sukun ْ).
                          // Annotation prefix: "imp:" with values "fiil" | "none" or "auto" (reset).
                          if (imperativesMode) {
                            const impAnnotations = turn.word_annotations || {};

                            return tokens.map((tok, idx) => {
                              const normTok = normalizeArabic(tok);
                              const ann = impAnnotations[String(idx)] || "";

                              let isImperative = false;
                              let isSuppressed = false;

                              if (ann.startsWith("imp:")) {
                                const annRole = ann.slice(4);
                                if (annRole === "fiil") isImperative = true;
                                else if (annRole === "none") isSuppressed = true;
                              } else {
                                // Data-driven lookup first (from s.imperative on verb senses),
                                // then heuristic fallback (tokens ending with sukun), then the
                                // ready-made chunks (تَعَالَ، خُذْ، أَعْطِنِي) the heuristic misses.
                                isImperative =
                                  !!(normTok && imperativeByNorm[normTok]) ||
                                  isImperativeToken(tok) ||
                                  KNOWN_IMPERATIVE_CHUNKS.has(normTok);
                              }

                              const match = findWordForToken(tok);
                              const handleClick = (e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const wordForBubble = match || { id: `tok-${idx}`, text: tok, senses: [] };
                                setBubble({ word: wordForBubble, rect, turnId: turn.id, tokenIdx: idx });
                                if (match && selectedWordId && selectedWordId !== match.id) onClearWord && onClearWord();
                              };

                              if (!isSuppressed && isImperative) {
                                return (
                                  <span key={`imp-${idx}`} className="inline-flex flex-col items-center mx-0.5 my-0.5">
                                    <button type="button" className="cursor-pointer px-1 py-0.5 rounded bg-red-100 text-red-900 hover:bg-red-200 transition-colors" onClick={handleClick} style={{ border: "none" }}>{tok}</button>
                                    <span className="text-[9px] bg-red-100 text-red-700 border border-red-300 rounded px-1 font-arabic leading-none mt-0.5">أمر</span>
                                  </span>
                                );
                              }

                              return renderToken(tok, idx, idx);
                            });
                          }

                          // render each group with its bracket+badge, wrapped in the amber
                          // nominal-sentence frame.  Tokens with null role render plain.
                          // This ensures that changing ONE token only re-groups that token
                          // while every other token keeps its heuristic grouping/colours.
                          const manualAnnotations = turn.word_annotations || {};

                          const effectiveRoles = tokens.map((_, i) => {
                            const m = manualAnnotations[String(i)];
                            if (m === "none") return null;
                            if (m) return m;
                            return getHeuristicRoleForToken(turn, i);
                          });

                          // Helper: render a bracket group (mubtada or khabar)
                          const renderBracketGroup = (groupToks, groupStart, role, keyPfx) => {
                            const isMubtada = role === "mubtada";
                            const lineColor = isMubtada ? "border-blue-400" : "border-emerald-400";
                            const dropColor = isMubtada ? "bg-blue-400" : "bg-emerald-400";
                            const badgeClass = isMubtada
                              ? "bg-blue-100 text-blue-700 border-blue-300"
                              : "bg-emerald-100 text-emerald-700 border-emerald-300";
                            const bgClass = isMubtada ? "bg-blue-50 border-blue-300" : "bg-emerald-50 border-emerald-300";
                            return (
                              <span
                                key={keyPfx}
                                className={`inline-flex flex-col items-center gap-0 rounded-md border px-1 mx-0.5 ${bgClass}`}
                                dir="rtl"
                              >
                                <span className="inline-flex flex-wrap justify-center gap-0 pb-0.5">
                                  {groupToks.map((tok, ti) =>
                                    renderToken(tok, `${keyPfx}-${ti}`, groupStart + ti)
                                  )}
                                </span>
                                <span className={`self-stretch border-t-2 ${lineColor}`} />
                                <span className={`w-0.5 h-2.5 ${dropColor}`} />
                                <span className={`px-1.5 py-px rounded text-[10px] font-semibold leading-none border font-arabic ${badgeClass}`} dir="rtl">
                                  {isMubtada ? "مبتدأ" : "خبر"}
                                </span>
                              </span>
                            );
                          };

                          // Walk through segments (split on Arabic punctuation) so we can
                          // wrap nominal sub-sentences in the amber background.
                          const rawArabic = turn.arabic || "";
                          const segmentPattern = /([،.؟!؛,;])/;
                          const parts = rawArabic.split(segmentPattern);

                          let globalTokenOffset = 0;
                          const rendered = [];

                          for (let pi = 0; pi < parts.length; pi++) {
                            const part = parts[pi];
                            if (!part) continue;
                            if (segmentPattern.test(part) && part.length === 1) {
                              rendered.push(<span key={`delim-${pi}`}>{part} </span>);
                              continue;
                            }

                            const segTokens = part.trim().split(/\s+/).filter(Boolean);
                            if (!segTokens.length) continue;

                            const segOffset = globalTokenOffset;
                            const segRoles = segTokens.map((_, i) => effectiveRoles[segOffset + i]);

                            // A segment is "nominal" if any of its tokens has a role assigned.
                            const isNominalSeg = segRoles.some((r) => r !== null);

                            if (isNominalSeg) {
                              // Wrap the whole sub-sentence in amber, then render groups inside.
                              const groupElements = [];
                              let gi = 0;
                              while (gi < segTokens.length) {
                                const role = segRoles[gi];
                                if (!role) {
                                  groupElements.push(renderToken(segTokens[gi], `seg${pi}-p${gi}`, segOffset + gi));
                                  groupElements.push(<span key={`seg${pi}-sp${gi}`}> </span>);
                                  gi++;
                                  continue;
                                }
                                // Collect consecutive tokens with the same role
                                const groupStart = gi;
                                const groupToks = [];
                                while (gi < segTokens.length && segRoles[gi] === role) {
                                  groupToks.push(segTokens[gi]);
                                  gi++;
                                }
                                groupElements.push(
                                  renderBracketGroup(groupToks, segOffset + groupStart, role, `seg${pi}-grp${groupStart}`)
                                );
                              }
                              rendered.push(
                                <span
                                  key={`seg-${pi}`}
                                  className="inline-flex items-end gap-1 rounded-md bg-amber-50 border border-amber-300 px-2 py-1 mx-0.5 align-bottom"
                                  title="جملة اسمية — nominal sentence"
                                  dir="rtl"
                                >
                                  {groupElements}
                                </span>
                              );
                            } else {
                              // Non-nominal: render tokens plain.
                              segTokens.forEach((tok, ti) => {
                                rendered.push(renderToken(tok, `seg${pi}-t${ti}`, segOffset + ti));
                              });
                              rendered.push(<span key={`sp-${pi}`}> </span>);
                            }

                            globalTokenOffset += segTokens.length;
                          }

                          return rendered;
                        })()}
                      </div>

                      {/* Transliteration */}
                      {turn.transliteration && (
                        <div
                          className="text-xs italic text-right"
                          style={{ color: "var(--parchment-dim)" }}
                          dir="ltr"
                        >
                          {turn.transliteration}
                        </div>
                      )}

                      {/* Translation */}
                      {turn.translation && (
                        <div
                          className="text-sm mt-0.5 text-right"
                          style={{ color: "var(--parchment)" }}
                          dir="ltr"
                        >
                          {turn.translation}
                        </div>
                      )}
                    </>
                  )}
                </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Reorder status */}
          {isAdmin && turnReorderSaving && (
            <p className="text-xs text-slate-400 italic text-center py-1">Saving order…</p>
          )}
          {isAdmin && turnReorderError && (
            <p className="text-xs text-red-500 text-center py-1">{turnReorderError}</p>
          )}

          {/* Empty state for admin when no turns exist */}
          {isAdmin && displayTurns.length === 0 && !showAddTurnForm && (
            <p className="text-sm text-slate-400 italic text-center py-4">
              No dialogue lines yet. Click "+ Add Dialogue" to add the first one.
            </p>
          )}

          {/* Add Dialogue inline form */}
          {isAdmin && showAddTurnForm && (() => {
            const existingSpeakers = [
              ...new Set(
                (localTurns || topic.scenario_turns || [])
                  .map((t) => t.speaker)
                  .filter(Boolean)
              ),
            ];
            return (
              <div className="mt-3 border border-emerald-200 rounded-lg p-3 bg-emerald-50 space-y-2">
                <p className="text-xs font-semibold text-emerald-800">New Dialogue Line</p>

                {/* Speaker combobox */}
                <div className="relative">
                  <label className="block text-xs text-slate-500 mb-0.5">Speaker <span className="text-slate-400">(optional)</span></label>
                  <input
                    type="text"
                    value={addTurnSpeaker}
                    onChange={(e) => {
                      setAddTurnSpeaker(e.target.value);
                      setSpeakerDropdownOpen(true);
                    }}
                    onFocus={() => setSpeakerDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setSpeakerDropdownOpen(false), 150)}
                    placeholder="Leave blank for an unattributed line"
                    className="w-full text-sm border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  />
                  {speakerDropdownOpen && existingSpeakers.length > 0 && (
                    <ul className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-md shadow-md max-h-40 overflow-y-auto">
                      {existingSpeakers
                        .filter((s) =>
                          s.toLowerCase().includes(addTurnSpeaker.toLowerCase())
                        )
                        .map((s) => (
                          <li key={s}>
                            <button
                              type="button"
                              onMouseDown={() => {
                                setAddTurnSpeaker(s);
                                setSpeakerDropdownOpen(false);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-emerald-50 transition-colors"
                            >
                              {s}
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>

                {/* Arabic text */}
                <div>
                  <label className="block text-xs text-slate-500 mb-0.5">Arabic</label>
                  <textarea
                    dir="rtl"
                    lang="ar"
                    value={addTurnArabic}
                    onChange={(e) => setAddTurnArabic(e.target.value)}
                    rows={2}
                    placeholder="أدخل النص العربي هنا"
                    className="w-full font-arabic text-sm border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-400 resize-none"
                  />
                </div>

                {/* Transliteration */}
                <div>
                  <label className="block text-xs text-slate-500 mb-0.5">Transliteration (optional)</label>
                  <input
                    type="text"
                    value={addTurnTranslit}
                    onChange={(e) => setAddTurnTranslit(e.target.value)}
                    placeholder="e.g. 'ahlan wa sahlan"
                    className="w-full text-sm border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  />
                </div>

                {/* Translation */}
                <div>
                  <label className="block text-xs text-slate-500 mb-0.5">Translation (optional)</label>
                  <input
                    type="text"
                    value={addTurnTranslation}
                    onChange={(e) => setAddTurnTranslation(e.target.value)}
                    placeholder="English translation"
                    className="w-full text-sm border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  />
                </div>

                {addTurnError && (
                  <p className="text-xs text-red-600">{addTurnError}</p>
                )}

                <div className="flex gap-2 justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddTurnForm(false);
                      setAddTurnSpeaker("");
                      setAddTurnArabic("");
                      setAddTurnTranslit("");
                      setAddTurnTranslation("");
                      setAddTurnError("");
                    }}
                    className="px-3 py-1 text-sm rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={addTurnSaving || !addTurnArabic.trim()}
                    onClick={async () => {
                      if (!addTurnArabic.trim()) return;
                      setAddTurnSaving(true);
                      setAddTurnError("");
                      try {
                        const newTurn = await createScenarioTurn(topic.id, {
                          speaker: addTurnSpeaker.trim(),
                          arabic: addTurnArabic.trim(),
                          transliteration: addTurnTranslit.trim(),
                          translation: addTurnTranslation.trim(),
                        });
                        handleTurnCreated(newTurn);
                      } catch (err) {
                        setAddTurnError(err.message || "Failed to save");
                      } finally {
                        setAddTurnSaving(false);
                      }
                    }}
                    className="px-3 py-1 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {addTurnSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            );
          })()}
        </section>
      )}
      </div>

      {/* Vocabulary — word list separated by type */}
      {showVocabContent && (
      <div ref={vocabSectionRef}>
      {vocabularyStepView}
      </div>
      )}
      {/* ── PDF Attachments (reading reference) ─────────────────────────────── */}
      {showScenarioContent && ((topic.pdfs && topic.pdfs.length > 0) || isAdmin) && (
        <section className="surface-glass rounded-lg p-3 relative overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700">📄 PDFs</h3>
            {isAdmin && (
              <label className="cursor-pointer">
                <span className="px-2.5 py-1.5 rounded border border-slate-300 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
                  {pdfUploading ? "Uploading…" : "+ Attach PDF"}
                </span>
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  disabled={pdfUploading}
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    setPdfError("");
                    setPdfUploading(true);
                    try {
                      await uploadTopicPDF(topic.id, file);
                      onTurnSaved && onTurnSaved(); // refresh topic from parent
                    } catch (err) {
                      setPdfError(err.message || "Upload failed");
                    } finally {
                      setPdfUploading(false);
                      if (pdfInputRef.current) pdfInputRef.current.value = "";
                    }
                  }}
                />
              </label>
            )}
          </div>
          {pdfError && <p className="text-xs text-red-600 mb-2">{pdfError}</p>}
          {(!topic.pdfs || topic.pdfs.length === 0) ? (
            <p className="text-xs text-slate-400 italic">No PDFs attached.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {topic.pdfs.map((pdf) => (
                <div
                  key={pdf.id}
                  className="flex items-center gap-2 p-2 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setViewingPdf(pdf)}
                    className="flex-1 flex items-center gap-2 text-left min-w-0"
                  >
                    <span className="text-lg flex-shrink-0">📄</span>
                    <span className="text-sm text-slate-700 truncate">
                      {pdf.title || pdf.filename}
                    </span>
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      title="Remove PDF"
                      onClick={async () => {
                        if (!window.confirm(`Remove "${pdf.title || pdf.filename}"?`)) return;
                        try {
                          await deleteTopicPDF(pdf.id);
                          onTurnSaved && onTurnSaved();
                        } catch (err) {
                          setPdfError(err.message || "Delete failed");
                        }
                      }}
                      className="flex-shrink-0 text-red-400 hover:text-red-600 text-lg leading-none px-1 transition-colors"
                    >
                      🗑
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Role Play — launch panel (the activity opens full-screen) */}
      {showRoleplayPanel && (
        <section className="surface-glass rounded-lg p-5 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.6 7.1L3 20l1-5.2A8 8 0 1121 12z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">Role Play</h3>
          <p className="text-xs text-slate-600 max-w-md mx-auto">
            Practise this scenario as a live conversation with the AI tutor. Complete the
            role play to unlock the Quiz.
            {rolePlayLocked && " Finish the Listening step first to unlock this."}
          </p>
          <button
            type="button"
            onClick={() => onStartChat && onStartChat()}
            disabled={rolePlayLocked}
            className="mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg bg-dt-gold text-dt-ink shadow-sm hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter]"
          >
            Start Role Play
          </button>
        </section>
      )}

      {/* Quiz — AI launch panel (docs/AI_TOPIC_QUIZ.md); the quiz opens full-screen */}
      {showQuizPanel && useAiQuiz && (
        <section className="surface-glass rounded-lg p-5 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-slate-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">Quiz</h3>
          <p className="text-xs text-slate-600 max-w-md mx-auto">
            {aiQuizConfig.target_count} words · {aiQuizConfig.line_count} lines · {aiQuizConfig.questions_per_attempt} questions in
            {aiQuizConfig.type_count ? ` ${aiQuizConfig.type_count}` : " several"} styles. Every attempt is different; pass mark {aiQuizConfig.pass_percent}%.
            {quizStepLocked && !isAdmin && " Complete the earlier steps first to unlock this."}
          </p>
          {(aiQuizConfig.best_score > 0 || aiQuizConfig.mastered) && (
            <p className="text-xs text-slate-500 mt-1">
              Best score {aiQuizConfig.best_score}%{aiQuizConfig.mastered ? " · ★ mastered" : ""}
            </p>
          )}
          <button
            type="button"
            onClick={() => onStartAiQuiz()}
            disabled={quizStepLocked && !isAdmin}
            className="mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg bg-dt-gold text-dt-ink shadow-sm hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter]"
          >
            {aiQuizConfig.in_progress_attempt_id ? "Resume Quiz" : aiQuizConfig.attempt_count > 0 ? "Take it again" : "Start Quiz"}
          </button>
          {!aiQuizConfig.bank_ready && (
            <p className="text-[11px] text-slate-400 mt-2">First start writes the questions for this topic (about half a minute).</p>
          )}
          {isAdmin && (
            <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-slate-400">
              <span>{aiQuizConfig.bank_ready ? `${aiQuizConfig.question_count} questions in the bank` : "No question bank yet"}</span>
              {aiQuizConfig.bank_ready && (
                <button type="button" onClick={() => setAiQuizBankOpen(true)} className="underline hover:text-slate-600">
                  Manage questions
                </button>
              )}
              <button
                type="button"
                disabled={aiQuizRegen === "busy"}
                onClick={async () => {
                  if (aiQuizConfig.edited_count > 0 && !window.confirm(
                    `${aiQuizConfig.edited_count} hand-edited question(s) will be lost. Regenerate anyway?`
                  )) return;
                  setAiQuizRegen("busy");
                  try {
                    await regenerateAiQuiz(topic.id);
                    const cfg = await fetchAiQuizConfig(topic.id);
                    setAiQuizConfig(cfg);
                    setAiQuizRegen("done");
                  } catch {
                    setAiQuizRegen("error");
                  }
                }}
                className="underline hover:text-slate-600 disabled:opacity-50"
              >
                {aiQuizRegen === "busy" ? "Regenerating…" : aiQuizRegen === "done" ? "Regenerated" : aiQuizRegen === "error" ? "Failed — retry" : "Regenerate"}
              </button>
            </div>
          )}
        </section>
      )}

      {/* Quiz — legacy launch panel (the quiz opens full-screen) */}
      {showQuizPanel && !useAiQuiz && (
        <section className="surface-glass rounded-lg p-5 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-slate-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">Quiz</h3>
          <p className="text-xs text-slate-600 max-w-md mx-auto">
            The final step — pass the quiz to complete this topic.
            {quizStepLocked && " Complete the earlier steps first to unlock this."}
          </p>
          <button
            type="button"
            onClick={() => onStartQuiz && onStartQuiz()}
            disabled={quizStepLocked}
            className="mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg bg-dt-gold text-dt-ink shadow-sm hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter]"
          >
            Start Quiz
          </button>
        </section>
      )}
      </GuidedTopicJourney>

      {aiQuizBankOpen && (
        <QuizBankManager
          topicId={topic.id}
          onClose={() => {
            setAiQuizBankOpen(false);
            fetchAiQuizConfig(topic.id).then(setAiQuizConfig).catch(() => {});
          }}
        />
      )}

      {/* Word-structure blow-up modal */}
      {structureWord && (
        <WordMorphology word={structureWord} onClose={() => setStructureWord(null)} />
      )}

      {/* Word definition bubble */}
      {bubble && (
        <WordBubble
          word={bubble.word}
          anchorRect={bubble.rect}
          // Stub words (unmatched tokens, id "tok-N") have no word-detail page;
          // real Word rows always have numeric ids.
          onInfoClick={typeof bubble.word.id !== "number" ? null : () => {
            if (!onWordSelect) return;
            // Occurrence context (#92): the surface token can differ from the
            // stored Word.text in its case ending, so the detail panel needs
            // the form + structure of this occurrence, not the global word.
            const t = bubble.turnId ? (localTurns || []).find((x) => x.id === bubble.turnId) : null;
            const raw = t && bubble.tokenIdx !== undefined
              ? (t.arabic || "").split(/\s+/)[bubble.tokenIdx]
              : null;
            const occurrence = raw
              ? {
                  surfaceText: stripPunctuationOnly(raw),
                  morphology: (t.word_morphology || {})[String(bubble.tokenIdx)] || null,
                  // The whole line, so an AI look-up can disambiguate a
                  // noun/verb homograph from how the word is actually used.
                  sentence: t.arabic || "",
                  turnId: bubble.turnId,
                  tokenIdx: bubble.tokenIdx,
                }
              : null;
            onWordSelect(bubble.word.id, bubble.word.text, bubble.word.word_type, occurrence);
          }}
          onAddToWordBank={typeof bubble.word.id === "number"
            ? () => addWordBankEntry({ word_id: bubble.word.id })
            : async () => {
                // Stub word: add via the text path, enriched from the free
                // word-gloss cache probe when a gloss is known (mirrors the
                // Reading popover's fall-back-to-bare-word behavior).
                const text = (bubble.word.text || "").trim();
                let gloss = null;
                try {
                  const r = await analyzeWordBankWord(text, { probe: true });
                  if (r.cached && r.definition) gloss = r;
                } catch { /* probe is best-effort */ }
                return addWordBankEntry({
                  text,
                  pos: gloss?.pos || "noun",
                  definition: (gloss?.definition || "").trim(),
                  transliteration: (gloss?.transliteration || "").trim(),
                });
              }}
          hasStructure={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            return !!(t && (t.word_morphology || {})[String(bubble.tokenIdx)]);
          })()}
          onStructureClick={() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            const entry = t && (t.word_morphology || {})[String(bubble.tokenIdx)];
            if (entry) setStructureWord(entry);
          }}
          canRegenStructure={!!(isAdmin && bubble.turnId && bubble.tokenIdx !== undefined)}
          onRegenStructure={() => handleRegenerateStructure(bubble.turnId, bubble.tokenIdx)}
          regenStructureBusy={regenStructureKey === `${bubble.turnId}:${bubble.tokenIdx}`}
          onClose={() => setBubble(null)}
          onPlay={playWord}
          isLoading={wordLoadingId === bubble.word.id}
          isPlaying={wordPlayingId === bubble.word.id}
          showLinkControls={!!isAdmin}
          linkCandidates={linkCandidates}
          onLinkLemma={handleLinkLemma}
          linkBusy={linkBusy}
          showGrammarControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "nominal_sentence" && bubble.turnId !== undefined)}
          grammarRole={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const manual = (t.word_annotations || {})[String(bubble.tokenIdx)];
            // "none" means admin explicitly cleared the heuristic — show as null
            if (manual && manual !== "none") return manual;
            if (manual === "none") return null;
            return getHeuristicRoleForToken(t, bubble.tokenIdx);
          })()}
          isManuallyOverridden={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            return t ? String(bubble.tokenIdx) in (t.word_annotations || {}) : false;
          })()}
          onGrammarRoleSet={handleGrammarRoleSet}
          showPronounControls={!!(isAdmin && ((showGrammar && topic.grammar_rule_code === "personal_pronouns") || colorToggles.pronoun) && bubble.turnId !== undefined)}
          pronounSuffixOptions={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return [];
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return [];
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            return tok ? getMatchingSuffixOptions(normalizeArabic(tok)) : [];
          })()}
          currentPronounAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (!val || !val.startsWith("pronoun:")) return null;
            return val.slice("pronoun:".length); // "none" | suffix string
          })()}
          isManualPronounOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("pronoun:"));
          })()}
          globalPronounAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return undefined;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return undefined;
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return undefined;
            const normTok = normalizeArabic(tok);
            return globalPronounAnnotations[normTok]; // undefined if no global override
          })()}
          onPronounAnnotationSet={handlePronounAnnotationSet}
          showPrepControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "prepositions" && bubble.turnId !== undefined)}
          currentPrepAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            // Manual annotation takes priority.
            if (val && val.startsWith("prep:")) return val.slice("prep:".length);
            // Fall back to auto-detected role so buttons reflect current display state.
            const toks = (t.arabic || "").split(/\s+/);
            const tok = toks[bubble.tokenIdx];
            if (!tok) return null;
            if (isPrepositionToken(tok)) return "harf_jar";
            const prevTok = bubble.tokenIdx > 0 ? toks[bubble.tokenIdx - 1] : null;
            if (prevTok) {
              const prevAnn = (t.word_annotations || {})[String(bubble.tokenIdx - 1)] || "";
              const prevRole = prevAnn.startsWith("prep:") ? prevAnn.slice(5) : null;
              const prevIsPrep = prevRole === "harf_jar" || (prevRole === null && isPrepositionToken(prevTok));
              if (prevIsPrep) return "baad_harf_jar";
            }
            return null;
          })()}
          isManualPrepOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("prep:"));
          })()}
          onPrepAnnotationSet={handlePrepAnnotationSet}
          showDefControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "definite_indefinite" && bubble.turnId !== undefined)}
          currentDefAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("def:")) return val.slice("def:".length);
            // Fall back to auto-detected role.
            const toks = (t.arabic || "").split(/\s+/);
            const tok = toks[bubble.tokenIdx];
            if (!tok) return null;
            if (isDefiniteToken(tok)) return "definite";
            return null;
          })()}
          isManualDefOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("def:"));
          })()}
          onDefAnnotationSet={handleDefAnnotationSet}
          showWoControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "word_order" && bubble.turnId !== undefined)}
          currentWoRole={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val === "mubtada" || val === "khabar") return val;
            if (val && val.startsWith("wo:") && !val.startsWith("wo:seg:")) return val.slice(3); // "fiil"|"faail"|"mafool"|"none"
            return null;
          })()}
          currentWoSegType={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const segFirstIdx = bubble.segFirstIdx ?? bubble.tokenIdx;
            const val = (t.word_annotations || {})[String(segFirstIdx)];
            if (val === "wo:seg:filia") return "filia";
            if (val === "wo:seg:ismia") return "ismia";
            return null;
          })()}
          isManualWoRoleOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && ((val.startsWith("wo:") && !val.startsWith("wo:seg:")) || val === "mubtada" || val === "khabar"));
          })()}
          isManualWoSegOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const segFirstIdx = bubble.segFirstIdx ?? bubble.tokenIdx;
            const val = (t.word_annotations || {})[String(segFirstIdx)];
            return !!(val && val.startsWith("wo:seg:"));
          })()}
          onWoAnnotationSet={handleWoAnnotationSet}
          showPtControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "past_tense" && bubble.turnId !== undefined)}
          currentPtAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("pt:")) return val.slice(3);
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            return isVerbToken(tok) ? "fiil" : null;
          })()}
          isManualPtOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("pt:"));
          })()}
          onPtAnnotationSet={handlePtAnnotationSet}
          showPtiControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "present_tense_intro" && bubble.turnId !== undefined)}
          currentPtiAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("pti:")) return val.slice(4);
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            return isPresentTenseIntroToken(tok) ? "fiil" : null;
          })()}
          isManualPtiOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("pti:"));
          })()}
          onPtiAnnotationSet={handlePtiAnnotationSet}
          showGenderControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "gender" && bubble.turnId !== undefined)}
          currentGenderAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("gend:")) return val.slice(5);
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            const norm = normalizeArabic(tok);
            if (norm.endsWith("ة") && norm.length >= 2) return "feminine";
            if (isVerbToken(tok)) return "fiil";
            if (norm.length >= 2 && !GENDER_SKIP_PARTICLES.has(norm) && !INDEPENDENT_PRONOUNS_SET.has(norm) && !isPrepositionToken(tok)) return "masculine";
            return null;
          })()}
          isManualGenderOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("gend:"));
          })()}
          onGenderAnnotationSet={handleGenderAnnotationSet}
          showAdjControls={!!(isAdmin && showGrammar && (topic.grammar_rule_code === "adjectives" || topic.grammar_rule_code === "adjective_agreement") && bubble.turnId !== undefined)}
          currentAdjAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("adj:")) return val.slice(4);
            return null;
          })()}
          isManualAdjOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("adj:"));
          })()}
          onAdjAnnotationSet={handleAdjAnnotationSet}
          showIdafaControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "idafa" && bubble.turnId !== undefined)}
          currentIdafaAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("idafa:")) return val.slice(6);
            return null;
          })()}
          isManualIdafaOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("idafa:"));
          })()}
          idafaHasPronounSuffix={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return false;
            return detectAttachedPronoun(normalizeArabic(tok)) !== null;
          })()}
          onIdafaAnnotationSet={handleIdafaAnnotationSet}
          showNegControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "negation" && bubble.turnId !== undefined)}
          currentNegAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("neg:")) return val.slice(4);
            // No manual annotation — compute heuristic state so buttons reflect current role
            const toks = (t.arabic || "").split(/\s+/);
            const tok = toks[bubble.tokenIdx];
            const nextTok = toks[bubble.tokenIdx + 1];
            if (!tok) return null;
            if (isNegationParticle(tok, nextTok)) return "particle";
            const prevTok = toks[bubble.tokenIdx - 1];
            if (prevTok) {
              const prevAnn = ((t.word_annotations || {})[String(bubble.tokenIdx - 1)] || "");
              const prevNegRole = prevAnn.startsWith("neg:") ? prevAnn.slice(4) : null;
              const prevIsParticle = prevNegRole === "particle" || (prevNegRole === null && isNegationParticle(prevTok, tok));
              if (prevIsParticle) {
                const prevNorm = normalizeArabic(prevTok.trim());
                if (prevNorm === NEGATION_LAM) return "majzoom";
                if (LAYSA_FORMS.has(prevNorm) && isVisiblyMansuub(tok)) return "mansuub";
              }
            }
            return null;
          })()}
          isManualNegOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("neg:"));
          })()}
          onNegAnnotationSet={handleNegationAnnotationSet}
          showQwControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "question_words" && bubble.turnId !== undefined)}
          currentQwAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("qw:")) return val.slice(3);
            // No manual annotation — reflect heuristic state
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            if (isQuestionWord(tok)) return "question_word";
            return null;
          })()}
          isManualQwOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("qw:"));
          })()}
          onQwAnnotationSet={handleQuestionWordsAnnotationSet}
          showConControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "connectors" && bubble.turnId !== undefined)}
          currentConAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("con:")) return val.slice(4);
            // No manual annotation — reflect heuristic state
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            return getConnectorType(tok);
          })()}
          isManualConOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("con:"));
          })()}
          onConAnnotationSet={handleConnectorAnnotationSet}
          showVoControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "verb_object" && bubble.turnId !== undefined)}
          currentVoAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("vo:")) return val.slice(3);
            // No manual annotation — reflect heuristic state
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            return isVerbToken(tok) ? "verb" : null;
          })()}
          isManualVoOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("vo:"));
          })()}
          onVoAnnotationSet={handleVerbObjectAnnotationSet}
          showFtControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "future_tense" && bubble.turnId !== undefined)}
          currentFtAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("ft:")) return val.slice(3);
            // No manual annotation — reflect heuristic state
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            return getFutureTenseRole(tok);
          })()}
          isManualFtOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("ft:"));
          })()}
          onFtAnnotationSet={handleFutureTenseAnnotationSet}
          showOpControls={!!(isAdmin && showGrammar && (topic.grammar_rule_code === "object_pronouns_basic" || topic.grammar_rule_code === "object_pronouns_expanded") && bubble.turnId !== undefined)}
          currentOpAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("op:")) return val.slice(3);
            // No manual annotation — reflect heuristic state.
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            if (topic.grammar_rule_code === "object_pronouns_expanded") {
              if (detectObjectPronounExpanded(tok)) return "mafool";
            } else {
              if (detectObjectPronounBasic(tok)) return "mafool";
            }
            if (isVerbToken(tok)) return "fiil";
            return null;
          })()}
          isManualOpOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("op:"));
          })()}
          onOpAnnotationSet={handleObjectPronounsBasicAnnotationSet}
          showCompControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "comparatives" && bubble.turnId !== undefined)}
          currentCompAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("comp:")) return val.slice(5);
            // No manual annotation — reflect heuristic state.
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            if (isComparativeToken(tok)) return "comparative";
            // Check if this "من" token follows a comparative — heuristic مِنْ detection.
            if (normalizeArabic(tok) === "من" && bubble.tokenIdx > 0) {
              const prevTok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx - 1];
              if (prevTok && isComparativeToken(prevTok)) return "min";
            }
            return null;
          })()}
          isManualCompOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("comp:"));
          })()}
          onCompAnnotationSet={handleComparativesAnnotationSet}
          showDfControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "dual_forms" && bubble.turnId !== undefined)}
          currentDfAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("df:")) return val.slice(3);
            // No manual annotation — reflect heuristic state (two-pass, same logic as rendering).
            const toks = (t.arabic || "").split(/\s+/);
            const tok = toks[bubble.tokenIdx];
            if (!tok) return null;
            if (!isDualFormToken(tok)) return null;
            // Check if a dual noun precedes this token (no chain-breaker between them).
            let lastNounIdx = -1;
            const dfAnns = t.word_annotations || {};
            for (let i = 0; i < bubble.tokenIdx; i++) {
              const a = dfAnns[String(i)] || "";
              const r = a.startsWith("df:") ? a.slice(3) : (isDualFormToken(toks[i]) ? "auto_dual" : null);
              if (r === "none") { lastNounIdx = -1; continue; }
              if (r === "dual") { lastNounIdx = i; continue; }
              if (r === "dual_adj") { /* adjective doesn't start a new chain */ continue; }
              if (r === "auto_dual") { lastNounIdx = i; continue; }
              if (isVerbToken(toks[i]) || getConnectorType(toks[i]) !== null) lastNounIdx = -1;
            }
            if (lastNounIdx >= 0) {
              let broken = normalizeArabic(tok).startsWith("\u0648");
              for (let j = lastNounIdx + 1; j < bubble.tokenIdx && !broken; j++) {
                const a = dfAnns[String(j)] || "";
                const r = a.startsWith("df:") ? a.slice(3) : (isDualFormToken(toks[j]) ? "auto_dual" : null);
                if (r !== null) { broken = true; break; }
                if (isVerbToken(toks[j]) || isPrepositionToken(toks[j]) || getConnectorType(toks[j]) !== null) broken = true;
              }
              if (!broken) return "dual_adj";
            }
            return "dual";
          })()}
          isManualDfOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("df:"));
          })()}
          onDfAnnotationSet={handleDualFormsAnnotationSet}
          showBpControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "broken_plurals_intro" && bubble.turnId !== undefined)}
          currentBpAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("bp:")) return val.slice(3);
            // No manual annotation — reflect data-driven detection state.
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            const normTok = normalizeArabic(tok);
            const isFuncWord = !normTok
              || INDEPENDENT_PRONOUNS_SET.has(normTok)
              || GENDER_SKIP_PARTICLES.has(normTok)
              || isPrepositionToken(tok);
            if (!isFuncWord) {
              if (normTok && singularByNorm[normTok]) return "plural";
              if (normTok && pluralsByNorm[normTok]) return "singular";
            }
            return null;
          })()}
          isManualBpOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("bp:"));
          })()}
          onBpAnnotationSet={handleBrokenPluralsAnnotationSet}
          showImpControls={!!(isAdmin && showGrammar && topic.grammar_rule_code === "imperatives" && bubble.turnId !== undefined)}
          currentImpAnnotation={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return null;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return null;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            if (val && val.startsWith("imp:")) return val.slice(4);
            // No manual annotation — reflect data-driven + heuristic detection state.
            const tok = (t.arabic || "").split(/\s+/)[bubble.tokenIdx];
            if (!tok) return null;
            const normTok = normalizeArabic(tok);
            return ((normTok && imperativeByNorm[normTok]) || isImperativeToken(tok)) ? "fiil" : null;
          })()}
          isManualImpOverride={(() => {
            if (!bubble.turnId || bubble.tokenIdx === undefined) return false;
            const t = (localTurns || []).find((t) => t.id === bubble.turnId);
            if (!t) return false;
            const val = (t.word_annotations || {})[String(bubble.tokenIdx)];
            return !!(val && val.startsWith("imp:"));
          })()}
          onImpAnnotationSet={handleImperativesAnnotationSet}
        />
      )}
      {/* PDF viewer modal — portal to body so it covers the whole screen */}
      {viewingPdf && ReactDOM.createPortal(
        <Suspense fallback={null}>
          <TopicPdfViewer pdf={viewingPdf} onClose={() => setViewingPdf(null)} />
        </Suspense>,
        document.body
      )}
    </div>
  );
}
